import * as L from 'leaflet';
import { OPERATORS } from 'src/app/services/constants';
import { CollectionPopupComponent } from '../../shared';
import { WarningPopup } from '../../shared/warning-popup/warning-popup';
import {
  PendingPlaybackRef,
  PendingTrackRef,
  SavedTrackRef,
} from '../playback/playback-controller';
import { VoyagePlaybackStep } from '../playback/voyageradar-playback.controller';
import {
  VoyageActiveVessel,
  VoyageTrackFrame,
  VoyageTrackPoint,
  VOYAGE_TRACK_SPANS,
  isTrackGap,
  splitTrackAtGaps,
  trackGapLabel,
  trackGapLinks,
} from '../playback/voyage-track.controller';
// Time formatting only — no component or DI reaches this file from there.
import { formatUtcShort, formatUtcStamp } from '../playback/controls/base-control';
import {
  buildVoyageClusterPopup,
  buildVoyagePoiNearbyPopup,
  buildVoyagePoiNearbyRows,
  VoyagePoiPopupContext,
  buildVoyagePopup,
  VoyagePopupActions,
} from './voyage-vessel-popup';
import {
  describeVoyageTrack,
  fallbackVesselColor,
  resolveVesselIdentity,
  VOYAGE_DEFAULT_TRACK_COLOR,
} from './track-history/voyage-track-history.model';
import { formatVoyageDate } from './refresh-history/voyage-refresh-history.model';
import {
  hasVoyageToiFinished,
  syncWalletFromError,
  voyageCreditsRemaining,
  voyageSensorSpendGate,
  voyageSpendErrorMessage,
  voyageSpendRefusal,
  VoyageSpendAction,
} from './voyage-credits.model';
import {
  actionCredits,
  VOYAGE_POI_RADIUS_KM,
} from 'src/app/services/operators/voyageradar-credit-actions';
import { VoyageLocalHit, VoyageLocatedRecord, VoyageRadarHost } from './voyage-map.types';

/**
 * Owns everything Voyage Radar draws on the map. Six areas, in the order the
 * `── ──` section markers below run — this list is the file's table of contents:
 *
 *   THE TASKED TARGET — the yellow AOI ring, or the POI reticle and its queried
 *   radius disc. Its lifecycle is the sensor CHECKBOX, not the data.
 *
 *   AOI SNAPSHOTS — the live vessel layer, frame playback over saved refreshes,
 *   and the toolbar's Refresh button.
 *
 *   THE DISPLAY PASS — ship-type filter, count-bubble clustering and name-label
 *   thinning. One pass; each stage decides what the next one sees.
 *
 *   TRACK MODE — the selected vessel, its halo, and the replay of a single
 *   vessel's path (a paid fetch from the operator, or a saved track from S3).
 *
 *   LOCATED VESSELS — hulls found by worldwide search or a by-MMSI lookup, on
 *   their own amber layer above the snapshot so no display rule can hide one the
 *   user explicitly asked for.
 *
 *   PAID BY-MMSI QUESTIONS — the vessel lookup, port-call history and nearby
 *   search, plus the wallet and the credit lines pushed to every priced control.
 *
 * They share one map, one viewport, one set of vessel colours and one wallet,
 * which is why they live together. AOI snapshots and track mode are also
 * mutually exclusive on screen.
 *
 * Extracted from LeafletNewcollectionsComponent to keep the parent lean, the
 * same way WeatherExMapController is. It has since grown well past that
 * controller's size: the last three areas above arrived as separate features
 * over ~2 weeks, each one reaching a `this.` away for the map and the wallet
 * that were already here. See the toolbar's own plugin registry
 * (`TOOLBAR_CONTROLS` / `SENSOR_TOOLBAR_CONFIGS`) for the pattern this half of
 * the feature has not yet been given.
 *
 * Lifecycle: instantiated once per component instance via
 *   `readonly voyage = new VoyageMapController(this as unknown as VoyageRadarHost);`
 * then `attach()` from the constructor to wire the toolbar callbacks.
 *
 * Division of labour with the two toolbar controllers: they own the clock and
 * the button state, this owns Leaflet. Frames arrive here ~10x/s, so the render
 * paths mutate existing layers rather than recreating them.
 */
export class VoyageMapController {
  // ── Layer state ─────────────────────────────────────────────────────────────
  /** Single Leaflet layer holding the currently rendered playback frame. */
  playbackLayer: any = null;
  /** True while Voyage Radar vessels are on the map — gates the live toolbar. */
  dataActive = false;
  /**
   * The layer currently holding the LIVE vessels. Written by both host entry
   * paths (sensor click → aisVesselLayer, refresh-history display →
   * markersLayer) so the toolbar, legend and filter work regardless of source.
   */
  liveLayer: any = null;
  /** The features currently visible (live snapshot or playback frame). */
  currentFeatures: any[] = [];
  /**
   * The tasked target on the map, and the ONLY thing that draws it.
   *
   * Its lifecycle is the sensor CHECKBOX, not the data. Ticked puts it up and
   * frames it; unticked takes it down; everything in between — a refresh, a
   * snapshot recall, a step through history, a shared map sweep — leaves it
   * exactly where it is. Before this it was a side-effect of whichever render
   * path happened to run, so the same task drew its target on a snapshot click
   * and nothing at all on a plain tick.
   *
   * One layer group, whatever the shape: a yellow ring for an AOI footprint,
   * the reticle + queried-radius disc for a POI (Point) footprint. Same yellow
   * and pane as every other AOI outline in the app.
   */
  private targetLayer: any = null;
  /**
   * The POI disc, held separately from the group so a refresh can re-size it in
   * place to the radius the server actually served. Null on an AOI task.
   */
  private targetCircle: any = null;
  /**
   * The POI reticle. The nearby-vessel list hangs off it as a popup, so the
   * in-place row re-renders need it. Null on an AOI task.
   */
  private targetMarker: any = null;
  /**
   * Rows shown in the POI popup, in the operator's nearest-first order.
   *
   * The list is the POI refresh's ENTIRE result: /v1/vessels/nearby returns no
   * positions, so nothing is drawn from it — a row click buys that vessel's
   * full details (fix included) via the by-MMSI lookup and places it exactly
   * like a search result.
   */
  private poiVessels: any[] = [];
  private poiRadiusKm: number | null = null;
  /**
   * What the filter box currently holds, and which row's lookup is in flight.
   *
   * Both live here rather than in the DOM because the rows container is
   * re-rendered from `poiVessels` by two separate triggers — a keystroke and a
   * row click — and each has to preserve what the other set.
   */
  private poiQuery = '';
  private poiPendingMmsi: string | null = null;
  /**
   * The live snapshot's features, kept separately from `currentFeatures` —
   * which playback overwrites frame by frame. Exiting playback needs the live
   * set back to restore the type categories, and the layer itself does not
   * carry them in a form worth re-deriving.
   */
  private _liveFeatures: any[] = [];
  /** Every snapshot loaded this playback session — also feeds free path tracing. */
  loadedSteps: VoyagePlaybackStep[] = [];
  /** Window + history fingerprint `loadedSteps` was built from; '' means unusable. */
  private _stepsCacheKey = '';

  /** True while playback has temporarily hidden the live vessel layer. */
  private _liveLayerHidden = false;
  /** Resolver for the in-flight toolbar refresh (null when none is pending). */
  private _refreshResolve: (() => void) | null = null;
  /** True once a toolbar refresh actually issued a request (vs hitting a guard). */
  private _refreshStarted = false;

  // ── Track mode ──────────────────────────────────────────────────────────────
  /** The vessel the user has selected on the map (null = none). */
  active: VoyageActiveVessel | null = null;
  /** Pulsing selection ring; rides the ghost during playback. */
  private _haloMarker: any = null;
  /**
   * Full track geometry, faded + dashed. Drawn once per load, never per frame.
   * A MULTI-polyline: one part per run of continuous coverage, so an unobserved
   * leg leaves a hole instead of a straight line across it.
   */
  private _trailBase: any = null;
  /**
   * The unobserved legs themselves, as a faint grey multi-polyline — "these two
   * sightings are the same vessel", not "it went this way". Null when the track
   * has no gaps, which is the common case and saves a layer.
   */
  private _trailLink: any = null;
  /** The travelled portion, solid. Only its last vertex moves on a normal frame. */
  private _trailDone: any = null;
  /**
   * Segments of the travelled trail. Nested for the same reason `_trailBase` is:
   * the solid line has to break where the dashed one does, or replaying a track
   * would paint back over a gap the static view correctly left open.
   */
  private _trailDoneSegments: any[][] = [];
  /** `_stepGaps[i]` — the leg from step i to i+1 was never observed. */
  private _stepGaps: boolean[] = [];
  /**
   * One dot per position report — the EVIDENCE the line is drawn from.
   *
   * A line alone says only what shape the voyage was; it says nothing about how
   * much was actually observed. Two stretches drawn identically can rest on a
   * report every thirty seconds and on one report a day. These dots are that
   * difference made visible, and they are the reason index-stride decimation is
   * the right kind: sampling every Nth entry keeps the RELATIVE density, so
   * where the dots bunch is where the reports really did.
   *
   * Added and removed by zoom rather than always drawn — see `_syncTrackDots`.
   */
  private _trackDots: any = null;
  /**
   * Start, end, and the two ends of every gap. Few enough to be interactive
   * (tooltips), unlike the dots, and drawn last so they sit above them.
   */
  private _trackMarks: any = null;
  /**
   * Bound while a track is drawn, so the beads can answer a change of VIEW —
   * zoom or pan. Both matter: zoom changes how far apart the reports land, pan
   * changes which of them the separability gate is looking at.
   */
  private _trackViewHandler: (() => void) | null = null;

  // ── Track hover readout ─────────────────────────────────────────────────────
  /**
   * The reports themselves, kept so the hover readout can say what one SAYS
   * rather than only where it is. `_reportLatLngs` has the positions; time,
   * speed and course live only here.
   *
   * THE FULL REPORT LIST, not the decimated transport one — and the difference
   * is a bug this fixed. Beads were built from `steps`, which is capped at 2000
   * for the replay clock, while the line is drawn from every report. On any
   * longer track decimation dropped vertices: the line still bent at them,
   * because the line knows about them, and no bead existed to explain the bend.
   * A mark that claims to BE the reports has to be drawn from the reports.
   */
  private _trackReports: VoyageTrackPoint[] = [];
  /** `_trackReports` as LatLngs — the bead positions. */
  private _reportLatLngs: any[] = [];
  /**
   * `_reportLatLngs` projected to layer points, rebuilt on zoom.
   *
   * Cached because the alternative is projecting two thousand coordinates on
   * every mousemove. Layer points are stable under PAN — the origin only moves
   * on zoom — so a zoom rebuild is the whole invalidation rule.
   */
  private _trackProjected: any[] = [];
  /**
   * Indices of the reports that currently HAVE a bead. Rebuilt per zoom by
   * `_syncTrackDots`, and the set the hover readout is allowed to name.
   */
  private _trackDotIndices: number[] = [];
  /** The floating readout. One tooltip, moved, never one per point. */
  private _trackHoverTip: any = null;
  private _trackHoverHandler: ((e: any) => void) | null = null;
  private _trackLeaveHandler: (() => void) | null = null;
  /** rAF coalescing: a mousemove can fire many times per frame. */
  private _trackHoverFrame: number | null = null;
  /** Which report the readout is currently showing, or -1. */
  private _trackHoverIndex = -1;
  private _trailIndex = -1;
  /**
   * Decimated transport geometry (one latlng per toolbar step).
   *
   * REPLAY ONLY — the travelled trail, the ghost and `_doneSegments`, all of
   * which index by `frame.index` and so must match the transport's own list.
   * The beads and the hover readout deliberately do NOT read this; they use
   * `_reportLatLngs`, which is every report rather than every 2000th of them.
   */
  private _trackLatLngs: any[] = [];
  /** The interpolated vessel drawn at the virtual clock's position. */
  private _ghostMarker: any = null;
  /** Cached icon element of the ghost — rotated in place instead of setIcon(). */
  private _ghostEl: HTMLElement | null = null;
  /**
   * Shared canvas renderer for the trail polylines. Leaflet never removes a
   * custom renderer when its last path goes, so creating one per track load
   * would leave a viewport-sized canvas (and its map handlers) behind each time.
   */
  private _trackRenderer: any = null;
  /**
   * MMSI the map is focused on while a track plays. Every OTHER live vessel is
   * dimmed to near-invisible: a Voyage Radar AOI can hold hundreds of markers,
   * and a single vessel's path drawn under them is unreadable.
   *
   * Null whenever nothing is dimmed — either no track is loaded, or the user
   * turned the dim off from the track bar. It is NOT "who is being traced": see
   * `_tracedMmsi`, which outlives the dim being switched off.
   */
  private _focusMmsi: string | null = null;
  /**
   * MMSI of the vessel the loaded track belongs to (null = no track drawn).
   *
   * Its live marker is held at a fixed fade for as long as the track is up, so
   * the moving ghost reads as "the" vessel — independent of the fleet dim, which
   * the user can switch off underneath it.
   */
  private _tracedMmsi: string | null = null;
  /**
   * Where the map was before a track took it over. `buildTrackLayers` fits the
   * track's bounds, which otherwise silently throws away the view the user had
   * arranged — closing the track puts it back.
   */
  private _preTrackView: { center: any; zoom: number } | null = null;
  /**
   * The view `buildTrackLayers`' own fitBounds produced.
   *
   * The pre-track view is only worth putting back while the TRACK is still what
   * framed the map. Plenty of things move it afterwards for reasons of their own
   * — picking a snapshot from Refresh History fits its markers, and the user can
   * pan — and replaying a stale view over those throws away a frame that was
   * asked for. See `_trackOwnsView`.
   */
  private _trackFitView: { center: any; zoom: number } | null = null;
  /**
   * Guards the deferred `_trackFitView` write against a second track loading
   * while the first one's flight is still in the air. See `fitTrackBounds`.
   */
  private _fitToken = 0;
  /** requestAnimationFrame handle for the arrival sweep, or null. */
  private _revealFrame: number | null = null;
  /** How long the path takes to draw itself on. */
  private static readonly REVEAL_MS = 420;
  /**
   * Above this many vertices the sweep is skipped and the path simply appears.
   *
   * Redrawing a canvas polyline once per frame is cheap at a few thousand
   * points and stops being cheap well before a 90-day track's tens of
   * thousands — and a reveal that stutters is worse than no reveal.
   */
  private static readonly REVEAL_MAX_POINTS = 4000;

  /**
   * MMSI → the vessel's live name/colour, for every vessel in the snapshot on
   * the map. The Vessel Tracks panel reads it to decide the presence dot and to
   * prefer a name being broadcast NOW over one stored with a track last week.
   *
   * Built once per pull rather than derived per change-detection pass — a busy
   * AOI is hundreds of features and the panel would otherwise walk them all
   * several times a second.
   */
  liveVessels = new Map<string, { name?: string | null; color?: string | null }>();

  /** Encoded vessel glyphs, keyed by view + colour. See `vesselIconUrl`. */
  private _iconCache = new Map<string, string>();

  // ── Located vessels (worldwide search) ──────────────────────────────────────
  /**
   * Layer holding located/kept vessel markers — its OWN layer above the
   * snapshot, and deliberately so: the type filter, the clustering pass and the
   * track dim all walk the snapshot layers only, so a vessel the user
   * explicitly searched for can never be filtered out, absorbed into a count
   * bubble, or faded by a rule they did not set. The exemption is structural,
   * not a flag someone can forget to check.
   */
  private _locatedLayer: any = null;
  /**
   * mmsi → { record, marker, ring } for every located vessel ON the map.
   *
   * `ring` records which of the two forms the marker is currently wearing — the
   * full amber diamond, or the ring-only form it takes when the snapshot is
   * already drawing this hull at the same fix. See `syncLocatedTwins`.
   */
  private _locatedVessels = new Map<
    string,
    { record: VoyageLocatedRecord; marker: any; ring: boolean }
  >();
  /** MMSI of the by-MMSI lookup in flight, or null — one at a time. */
  lookupLoadingMmsi: string | null = null;
  /**
   * MMSI of the port-call fetch in flight, or null. Separate from the lookup
   * guard above: the two are different purchases and one must not gate the
   * other, but a double-click on either must never buy the same thing twice.
   */
  portCallLoadingMmsi: string | null = null;
  /**
   * MMSI of the nearby-vessel search in flight, or null. Its own field for the
   * same reason the port-call guard above is: three separate purchases sit on
   * this card, and one being out must not stand the others down — but a
   * double-click on any of them must never buy the same answer twice.
   *
   * It matters more here than it does above, because a nearby search is the one
   * of the three whose result the user can change without changing the hull:
   * two clicks at two radii are two different questions and two legitimate
   * charges, so nothing further down can tell a duplicate from a re-ask. This
   * guard is what makes the FIRST of two identical clicks the only one that
   * spends.
   */
  nearbyLoadingMmsi: string | null = null;
  /**
   * The vessel popup currently open and the hull it describes. Maintained by
   * the `popupopen`/`popupclose` listeners in `ensurePopupActionsWired`, which
   * read the card's own `data-mmsi` — so it holds whichever of the four layers
   * opened the card, not just this controller's own.
   *
   * Two things need it. The take-off paths close the card of the vessel they
   * remove (a card opened at a point rather than bound to a marker does not
   * leave with that marker, so it was left floating over empty water), and the
   * lookup marks the card of the vessel it is fetching as updating. Both are
   * scoped by MMSI: an unrelated card must neither close nor spin.
   *
   * Only one is ever open — opening closes the previous — so a single reference
   * is the whole of this state.
   */
  private _vesselPopup: any = null;
  private _vesselPopupMmsi: string | null = null;
  /** The map container carrying the popup click-delegate (rewired if it changes). */
  private _popupClickTarget: any = null;
  /** A fix older than this is drawn hollow and captioned "last known". */
  private static readonly STALE_FIX_MS = 48 * 3600 * 1000;
  /**
   * How close a located fix has to be to the snapshot's own fix for the two to
   * be the SAME fix rather than two facts about one hull.
   *
   * Metres, not screen pixels, on purpose: a pixel threshold would flip the
   * marker's form on every zoom, so a vessel would grow and lose its diamond
   * under a gesture that changed nothing about the data. In metres the answer
   * is a property of the two fixes, and it only changes when one of them does.
   * Generous enough to absorb float rounding and a moored hull's drift between
   * an AOI pull and a by-MMSI lookup.
   */
  private static readonly TWIN_FIX_METERS = 120;

  // ── Clustering ──────────────────────────────────────────────────────────────
  /**
   * Grid cell for clustering, in px. Deliberately smaller than a label's cell:
   * a bubble only has to stop ARROWS overlapping, and arrows are 24–50px wide,
   * where a name is ~110px.
   */
  private static readonly CLUSTER_CELL = 60;
  /**
   * Below this, the cell draws its arrows instead of a bubble.
   *
   * Three, not two: a "2" bubble replaces two hulls you could already tell apart
   * with a count you then have to click to resolve — strictly less information in
   * the same space. It also keeps a pair of vessels passing each other visible as
   * two vessels passing each other, which is the reading the chart is for.
   */
  private static readonly CLUSTER_MIN = 3;
  /** Layer holding the count bubbles (null until the first one is needed). */
  private _clusterLayer: any = null;
  /**
   * Bubble markers, reused between passes rather than rebuilt.
   *
   * Playback runs this pass per frame, and a marker whose position and count are
   * unchanged should cost nothing — recreating a few hundred divIcons ten times a
   * second is exactly the stutter the label thinning was added to avoid.
   */
  private _clusterPool: any[] = [];
  /** How many pooled bubbles the last pass actually used. */
  private _clusterActive = 0;

  // ── Name labels ─────────────────────────────────────────────────────────────
  /**
   * Screen footprint one label reserves, in px. Labels are thinned to one per
   * cell — see `applyVesselLabels`. Wider than a short name on purpose: the cell
   * has to cover the label AND the gap to the next one, or the survivors still
   * collide.
   */
  private static readonly LABEL_CELL_X = 112;
  private static readonly LABEL_CELL_Y = 26;
  /** Viewport listener that re-runs the display pass (null while detached). */
  private _viewportRepaint: (() => void) | null = null;
  /** True while any label is bound — lets an off-state pass skip the walk. */
  private _labelsPainted = false;

  constructor(private host: VoyageRadarHost) {}

  /**
   * Wires both toolbar controllers' callbacks. Called once from the host
   * constructor.
   */
  attach(): void {
    const playback = this.host.voyageRadarPlayback;
    const track = this.host.voyageTrack;

    playback.onLoadPlaybackSteps = (from, to) => this.loadPlaybackSteps(from, to);
    // Answers on the click, off the same window the load will walk, so the
    // pending row can say how many snapshots are coming before any of them are.
    playback.onDescribePlayback = (from, to) => this.describePlayback(from, to);
    playback.onRenderStep = (step) => this.renderStep(step);
    playback.onTypeFilterChange = () => this.applyTypeFilterToLive();
    // The T control on the live bar. Goes through the same pass as the filter
    // because a label may only appear on a hull that is actually drawn — and
    // what is drawn is settled by that pass and the clustering below it. In
    // playback the frame re-render carries the labels instead.
    playback.onLabelsToggle = () => {
      this.applyTypeFilterToLive();
      // Located markers obey the SAME toggle as the fleet — one labels
      // control, not a second per-vessel one (which shipped and was
      // immediately read as a duplicate).
      this.syncLocatedLabels();
    };
    // Grouping decides only which of the markers already on the map are drawn,
    // so it repaints them in place — no filter re-run, no frame rebuild, and it
    // works the same in playback as it does live.
    playback.onClustersToggle = () => this.applyVesselDisplay(this.displayLayer);
    // Toolbar Refresh → pull a fresh AOI snapshot (spends one refresh entitlement).
    playback.onRefresh = () => this.refresh();
    // "Playback history" with no saved snapshots in range → tell the user.
    playback.onHistoryEmpty = () =>
      this.host.dialog.open(WarningPopup, {
        width: '40%',
        data: {
          title: 'No Playback Available',
          message:
            'There are no saved refreshes to play back. Refresh the area at least twice to build a timeline.',
        },
      });
    // Snapshots existed and none of them arrived. A different accident from the
    // one above and a different thing to do about it — advising a user with 200
    // saved refreshes to go and make some more is why these were split.
    playback.onHistoryFailed = () =>
      this.host.dialog.open(WarningPopup, {
        width: '40%',
        data: {
          title: 'Playback Unavailable',
          message:
            'Those saved refreshes could not be loaded. Please check your connection and try again.',
        },
      });
    // Closing playback from the toolbar X resets the Timeseries toggle so its
    // visual state stays in sync with the (now-hidden) toolbar.
    playback.onExit = () => {
      this.host.timeseries = false;
      this.host.sliderView = false;
      this.restoreLiveLayer();
    };

    track.onRenderTrack = (frame) => this.renderTrackFrame(frame);
    track.onTrackLoaded = (full, steps) => this.buildTrackLayers(full, steps);
    track.onActiveChange = (vessel) => {
      this.applyActiveVisual(vessel);
      // The dim button counts the hulls it would fade, and the traced one is not
      // among them — so the count moves when the selection does, not only when
      // the layer does.
      this.host.voyageTrack.setOthersOnMap(this.othersOnMap(vessel?.mmsi ?? null));
    };
    track.onClearActive = () => this.deactivateVessel();
    track.onRequestFull = (minutes) => this.confirmFullTrack(minutes);
    track.onRequestSaved = (trackId) => this.replaySavedTrack(trackId);
    track.onExit = () => this.clearTrackLayers();
    track.onLabelsToggle = (show) => this.toggleGhostLabel(show);
    track.onDimOthersToggle = (dim) => this.setTrackFocus(dim ? this._tracedMmsi : null);

    // The search-history drawer's per-row actions — same callback pattern.
    const vessel = this.host.voyageVessel;
    vessel.onCenterVessel = (mmsi) => this.centerLocatedVessel(mmsi);
    vessel.onTakeOffMap = (mmsi) => this.takeLocatedOffMap(mmsi);
    // Inherited from the panel's ON MAP summary cards when those were removed;
    // this was the only act on them without a home elsewhere.
    vessel.onClearAll = () => this.takeAllLocatedOffMap();

    // Port calls. `onLookupStored` is the FREE half: the record already rode to
    // the client on the sensor document, so re-opening a bought history is a
    // lookup in memory rather than a request. The controller calls it itself on
    // every selection change, so there is no "open for this mmsi" method here.
    const ports = this.host.voyagePortCalls;
    ports.onLookupStored = (mmsi) =>
      (this.sensor?.portCallHistory || []).find((r: any) => String(r?.mmsi) === mmsi) ?? null;
    ports.onFetch = (mmsi, vesselName) => this.fetchPortCalls(mmsi, vesselName);

    // Nearby vessels — the same three callbacks, wired the same way and for the
    // same reasons, because the drawer is a copy of the one above. The free
    // half is `onLookupStored` again: `nearbyHistory` rides to the client on the
    // sensor document exactly as `portCallHistory` does, so re-opening a bought
    // list is a lookup in memory rather than a second 2-credit question.
    //
    // `onFetch` takes a THIRD argument the port-call one has no use for. The
    // radius is the user's own, it is part of what was bought, and it lives in
    // the controller's state rather than in a DOM input — so the drawer's
    // header action and the popup's radar icon reach this call the same way,
    // carrying the same number.
    const nearby = this.host.voyageNearby;
    nearby.onLookupStored = (mmsi) =>
      (this.sensor?.nearbyHistory || []).find((r: any) => String(r?.mmsi) === mmsi) ?? null;
    nearby.onFetch = (mmsi, vesselName, radiusKm) =>
      this.fetchNearbyVessels(mmsi, vesselName, radiusKm);
    // The fourth wire, and the one port calls has no equivalent of: a row click
    // PLACES A HULL. `/v1/vessels/nearby` returns no position for anything in
    // its list, so a row is a name and a distance until someone buys its fix —
    // and the drawer has neither a map nor a wallet, where this controller has
    // both. The remembered radius is pushed by `syncCreditLines` for a related
    // reason: it is scoped by task AND sensor, and a toolbar control is handed
    // `{ controller, slot, options }` and so cannot know either.
    nearby.onPickVessel = (mmsi, onSettled) => this.pickNearbyRowVessel(mmsi, onSettled);

    // Vessel search. `onSearchLoaded` is the FREE half and the exact analogue of
    // the two `onLookupStored` wires above: `searchLoaded` is an in-memory index
    // over the drawn snapshot, every loaded frame and the task's kept vessels,
    // so filtering as you type is a lookup rather than a request. The paid half
    // is `onSearchWorldwide`, which is the only one of the five that spends.
    const search = this.host.voyageSearch;
    search.onSearchLoaded = (q) => this.searchLoaded(q);
    search.onPickLocal = (hit) => this.locateLoadedVessel(hit);
    search.onPickWorldwide = (row, q) => this.locateSearchResult(row, q);
    search.onSearchWorldwide = (q) => this.searchWorldwideVessels(q);

    this.syncCreditLines();
    this.pushSearchContext();
  }

  /** The open collection's task id — the search component's request context. */
  get taskId(): string | null {
    return this.host.collection?.taskId ?? null;
  }

  /** The first Voyage Radar sensor on the open collection, or null. */
  get sensor(): any | null {
    return (
      this.host.collection?.sensors?.find(
        (s: any) => s?.operatorKey === OPERATORS.VOYAGERADAR
      ) ?? null
    );
  }

  /**
   * True only when EVERY sensor in the collection is Voyage Radar.
   *
   * Deliberately not "contains a VR sensor": Voyage Radar replays through its
   * own track/playback toolbar rather than the shared Timeseries control, but in
   * a mixed task that control still belongs to the Spire/ClydeSpace/optical
   * sensor sitting next to it.
   */
  get isOnlyCollection(): boolean {
    const sensors = this.host.collection?.sensors ?? [];
    return (
      sensors.length > 0 &&
      sensors.every((s: any) => s?.operatorKey === OPERATORS.VOYAGERADAR)
    );
  }

  // ── The tasked AOI ──────────────────────────────────────────────────────────
  /**
   * The tasked area's bounds, read from a Voyage Radar sensor's scene footprint,
   * or null when the task carries no usable one.
   *
   * The single source for "where is this task": the zoom-to-task button, the
   * load-time framing below, and the nominatim guard in `loadUserDetails` — which
   * reads it to decide whether the country-level pan would be overwriting a view
   * that already means something — all go through here.
   *
   * Takes the collection as an argument because the load path calls it before
   * `host.collection` has been assigned; falls back to the open one otherwise.
   */
  /**
   * The scene carrying the task's target geometry.
   *
   * Shared by `aoiBounds` and `showTarget` so the outline and the framing can
   * never disagree about which scene the target is: one task, one target.
   *
   * Takes the collection as an argument because the load path calls it before
   * `host.collection` has been assigned; falls back to the open one otherwise.
   */
  private targetScene(collection?: any): any | null {
    const sensors = (collection ?? this.host.collection)?.sensors ?? [];
    const sensor = sensors.find((s: any) => s?.operatorKey === OPERATORS.VOYAGERADAR);
    return (
      sensor?.scenes?.find(
        (sc: any) =>
          sc?.footprint?.coordinates?.[0]?.length || sc?.footprint?.type === 'Point',
      ) ?? sensor?.scenes?.[0] ?? null
    );
  }

  aoiBounds(collection?: any): any | null {
    const scene = this.targetScene(collection);

    // A POI task's target is a point plus the radius the refresh queries — the
    // point alone would frame a zero-area view, so the disc is what "where is
    // this task" means here. The constant is the pre-refresh answer; once a
    // snapshot draws, `aoiFootprintLayer` carries the radius the server served.
    if (scene?.footprint?.type === 'Point') {
      const [lng, lat] = scene.footprint.coordinates ?? [];
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
      // `latLng.toBounds(diameter)`, NOT `L.circle(...).getBounds()`: Circle
      // derives its bounds by projecting through `this._map`, so an unattached
      // one throws. This runs before anything is on the map — that is the whole
      // point of it — so the bounds have to be computed, not projected.
      const bounds = L.latLng(lat, lng).toBounds(VOYAGE_POI_RADIUS_KM * 2 * 1000);
      return bounds?.isValid?.() ? bounds : null;
    }

    const ring = scene?.footprint?.coordinates?.[0];
    if (!ring?.length) return null;
    // Footprints are [lng, lat] and Leaflet wants [lat, lng]; the ring has to
    // close for the polygon to bound correctly.
    const coords = ring.map((p: number[]) => [p[1], p[0]]);
    coords.push(coords[0]);
    const bounds = L.polygon(coords as any).getBounds();
    return bounds?.isValid?.() ? bounds : null;
  }

  /**
   * Frame the map on the AOI when the task loads, before anything is drawn.
   *
   * Mirrors `WeatherExMapController.showTarget`, which does this for a WeatherEx
   * target polygon. Without it a Voyage Radar task opens on the country-level
   * view `loadUserDetails` sets from the user's profile — nowhere near the tasked
   * area, and not a view any of this task's data will ever appear in.
   *
   * It also stops that view being carried around by anything that captures the
   * viewport before the first snapshot lands. `buildTrackLayers` stores the
   * pre-track view to return to, and a track replayed from the Vessel Tracks
   * panel can be the very first thing on the map.
   *
   * Framing only — drawing the outline is `showTarget`'s job, and on load that
   * is gated on the sensor being checked. A task that opens with its box clear
   * is still framed on its AOI, because "where is this task" is worth answering
   * whether or not anything of the task is on the map yet.
   *
   * Returns whether it framed anything, so a task with no usable footprint falls
   * back to the country view rather than being left wherever the map happens to be.
   */
  showAoi(collection?: any): boolean {
    const bounds = this.aoiBounds(collection);
    if (!bounds || !this.host.map) return false;
    // On initial load the flex layout may not have settled, so Leaflet's cached
    // container size can be stale and the fit would frame a partial viewport.
    // WeatherEx calls `fixLeafletSizeCache` at the same point for the same reason.
    this.host.map.invalidateSize();
    this.host.map.fitBounds(bounds, { padding: [50, 50] });
    return true;
  }

  /**
   * Puts the tasked target on the map — the ring for an AOI task, the reticle
   * and queried-radius disc for a POI one — and optionally frames it.
   *
   * Idempotent, so every door into "Voyage Radar is on" can call it without
   * checking first: the checkbox, and every panel action that ticks the
   * checkbox on the user's behalf (see the host's `_showVoyageChrome`). A
   * second call re-attaches an existing layer rather than building a rival one.
   *
   * `fit` belongs to the CHECKBOX alone. Ticking the box is the user saying
   * "show me this task", so it frames the target; the panel actions that tick
   * it as a side-effect each frame their own result — a snapshot fits its
   * vessels, a track fits its path — and re-framing the AOI over those would
   * yank the view away from the thing that was actually asked for.
   *
   * Returns whether a target is now drawn.
   *
   * `scene` is for the one caller that already holds the geometry it means
   * (`showPoiNearby`, off the refresh it is rendering) — everyone else resolves
   * it from the task. `collection` is for the load path, which runs before
   * `host.collection` has been assigned.
   */
  showTarget(opts: { fit?: boolean; collection?: any; scene?: any } = {}): boolean {
    if (!this.host.map) return false;
    if (!this.targetLayer) {
      const scene = opts.scene ?? this.targetScene(opts.collection);
      const layer = this.buildTarget(scene);
      if (!layer) return false;
      this.targetLayer = layer;
    }
    // Both branches go through here, so a freshly built target is attached and
    // lifted by exactly the same code that re-attaches an existing one.
    this.ensureTargetOnMap();
    if (opts.fit) this.fitTarget();
    return true;
  }

  /**
   * Re-attaches the target after something swept the map out from under it.
   *
   * `resetMapNew` removes every non-tile layer, and it runs for reasons that
   * have nothing to do with this sensor. The WeatherEx twin
   * (`ensureBoundsLayerOnMap`) is called from the same place for the same
   * reason. Does not re-frame: the sweep's caller owns the viewport.
   */
  ensureTargetOnMap(): void {
    if (!this.targetLayer || !this.host.map) return;
    if (!this.host.map.hasLayer(this.targetLayer)) {
      this.targetLayer.addTo(this.host.map);
    }
    // The target outlives every vessel layer drawn over it, and a snapshot that
    // lands afterwards would otherwise bury the ring under a few hundred hulls.
    // featureGroup, not layerGroup, is what makes this available.
    try {
      this.targetLayer.bringToFront?.();
    } catch (_) {}
  }

  /** Frames the drawn target. No-op when nothing is drawn. */
  fitTarget(): void {
    const bounds = this.targetBounds();
    if (bounds) this.host.map.fitBounds(bounds, { padding: [50, 50] });
  }

  /**
   * The drawn target's bounds — what the map is actually showing, as opposed to
   * `aoiBounds`, which answers off the task data whether or not anything is up.
   * They agree on an AOI task; on a POI one this reflects the radius the server
   * served, which the constant in `aoiBounds` can only guess at before a pull.
   */
  targetBounds(): any | null {
    try {
      const bounds = this.targetLayer?.getBounds?.();
      return bounds?.isValid?.() ? bounds : null;
    } catch (_) {
      return null;
    }
  }

  /**
   * Takes the target off the map. The other end of the checkbox's lifecycle —
   * and the only thing that ends it, so no data path can take the target down
   * while the box still says the sensor is on.
   */
  clearTarget(): void {
    if (this.targetLayer) this.host.map?.removeLayer?.(this.targetLayer);
    this.targetLayer = null;
    this.targetCircle = null;
    this.targetMarker = null;
  }

  /**
   * Builds the target group for a scene, or null when the scene carries no
   * usable geometry. Point footprints get the POI reticle and disc; everything
   * else gets the ring.
   */
  private buildTarget(scene: any): any | null {
    if (scene?.footprint?.type === 'Point') {
      const [lng, lat] = scene.footprint.coordinates ?? [];
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
      // The tasked radius is the pre-refresh answer; a POI pull re-sizes the
      // circle in place to whatever it was actually served.
      this.poiRadiusKm = this.poiRadiusKm ?? VOYAGE_POI_RADIUS_KM;
      this.targetCircle = L.circle([lat, lng], {
        radius: this.poiRadiusKm * 1000,
        color: 'yellow',
        fillColor: 'none',
        pane: 'left',
      });
      this.targetMarker = this.buildPoiReticle(lat, lng);
      // featureGroup, NOT layerGroup: only the former has `getBounds`, which is
      // what `fitTarget` frames the task with. (The disc contributes real bounds
      // only once the group is on the map — L.Circle projects through it — which
      // is why `showTarget` attaches before it fits.)
      return L.featureGroup([this.targetCircle, this.targetMarker]);
    }

    const ring = scene?.footprint?.coordinates?.[0];
    if (!ring?.length) return null;
    // Footprints are [lng, lat] and Leaflet wants [lat, lng].
    const coords = ring.map((p: number[]) => [p[1], p[0]]);
    const polygon = L.polygon(coords as any, {
      color: 'yellow',
      fillColor: 'none',
      pane: 'left',
      // Nothing hangs off the ring, and an interactive one would swallow clicks
      // meant for the vessels drawn inside it.
      interactive: false,
    });
    return L.featureGroup([polygon]);
  }

  // ── Snapshot loading ────────────────────────────────────────────────────────
  /**
   * The refresh-history records a replay of [from, to] would fetch.
   *
   * One place rather than two: `describePlayback` counts these on the click and
   * `loadPlaybackSteps` walks them a moment later, and a pending row promising
   * twelve frames over a load that only ever tries eight would be worse than no
   * pending row at all.
   */
  private playbackRecordsIn(fromIso: string, toIso: string): any[] {
    const history: any[] = Array.isArray(this.sensor?.refreshHistory)
      ? this.sensor.refreshHistory
      : [];
    const fromMs = new Date(fromIso).getTime();
    const toMs = new Date(toIso).getTime();
    return history.filter((h) => {
      // A POI refresh stores the positionless nearby LIST, not a frame — it
      // cannot be played back, only re-opened from Refresh History.
      if (h?.area?.type === 'Point') return false;
      const t = new Date(h?.name).getTime();
      return !isNaN(t) && t >= fromMs && t <= toMs;
    });
  }

  /**
   * What the pending ON MAP row says while a replay's frames are in the air.
   *
   * The snapshot twin of `pendingRefFor`. Both answer synchronously off data the
   * panel already holds, so the wait describes the thing that was clicked from
   * the first frame — "18 snapshots · 12 Jul 2026 – 28 Jul 2026" rather than an
   * anonymous spinner, and rather than the REFRESHING line this load used to
   * borrow, which claimed a paid pull was in flight when nothing was being
   * bought at all.
   *
   * Null when the window holds nothing: there is no honest wait to describe, and
   * the empty-archive dialog is a better answer than a row that counts to zero.
   */
  describePlayback(fromIso: string, toIso: string): PendingPlaybackRef | null {
    const records = this.playbackRecordsIn(fromIso, toIso);
    if (!records.length) return null;

    const times = records
      .map((h) => new Date(h?.name).getTime())
      .filter((t) => !isNaN(t));
    const window = times.length
      ? `${formatVoyageDate(Math.min(...times))} – ${formatVoyageDate(Math.max(...times))}`
      : null;
    return { frames: records.length, window };
  }

  /**
   * Loads the refresh-history snapshots that fall inside the given TOI window
   * and turns each into a playback frame. Each snapshot is an already paid-for
   * full-AOI capture stored on S3, so replaying them costs 0 credits.
   */
  async loadPlaybackSteps(fromIso: string, toIso: string): Promise<VoyagePlaybackStep[]> {
    const sensor = this.sensor;
    const history: any[] = Array.isArray(sensor?.refreshHistory)
      ? sensor.refreshHistory
      : [];
    if (!sensor || !history.length) return [];

    const inWindow = this.playbackRecordsIn(fromIso, toIso);
    const taskId = this.host.collection?.taskId;

    // Re-entering playback over the same window used to re-fetch every frame —
    // a signed-URL round trip plus an S3 GET per snapshot, for data already
    // parsed and still held in `loadedSteps`. The fingerprint carries the
    // history length and newest refreshId as well as the window, so a refresh
    // taken in between still invalidates it.
    const cacheKey = [
      fromIso,
      toIso,
      sensor.id,
      history.length,
      history[history.length - 1]?.refreshId,
    ].join('|');
    if (cacheKey === this._stepsCacheKey && this.loadedSteps.length) {
      // Types are recomputed rather than assumed: exiting playback resets the
      // filter, so the categories have to be seeded again on every entry.
      const cachedUnion: any[] = [];
      for (const step of this.loadedSteps) cachedUnion.push(...(step.features ?? []));
      this.computeAvailableTypes(cachedUnion);
      return this.loadedSteps;
    }

    const loaded = await Promise.all(
      inWindow.map(async (h) => {
        try {
          const res: any = await this.host.apiService
            .getVoyageRadarSnapshot({
              sensorId: sensor.id,
              refreshId: h?.refreshId,
              taskid: taskId,
              action: 'display',
            })
            .toPromise();
          const url = res?.result?.imageUrl;
          if (!url) return null;
          const json: any = await this.host.httpClient.get(url).toPromise();
          const features = json?.features ?? json ?? [];
          return {
            time: h?.name,
            // Carried so the frame can name its own row back to the panel.
            refreshId: h?.refreshId ?? null,
            features,
          } as VoyagePlaybackStep;
        } catch {
          return null;
        }
      })
    );
    const steps = loaded.filter(
      (s): s is VoyagePlaybackStep => !!s && Array.isArray(s.features)
    );
    // Cache for free path-tracing and compute a stable (union) type list so the
    // filter categories don't flicker frame-to-frame.
    this.loadedSteps = steps;
    // Only a non-empty load is worth reusing; '' keeps a failed or empty window
    // from being served back as a cache hit.
    this._stepsCacheKey = steps.length ? cacheKey : '';
    const union: any[] = [];
    for (const step of steps) union.push(...(step.features ?? []));
    this.computeAvailableTypes(union);
    return steps;
  }

  // ── Frame rendering ─────────────────────────────────────────────────────────
  /**
   * Renders a single playback frame. Keeps exactly one layer alive (clearing the
   * previous frame's markers) so scrubbing and playing stay cheap. A null step
   * clears the current frame (used on close / resume-live).
   */
  renderStep(step: VoyagePlaybackStep | null): void {
    if (!step) {
      if (this.playbackLayer) {
        this.host.map.removeLayer(this.playbackLayer);
        this.playbackLayer = null;
      }
      // The bubbles belonged to the frame that just came off. Leaving them would
      // park the last frame's counts over the live layer that replaces it —
      // `restoreLiveLayer` runs the display pass again and rebuilds its own.
      this.clearVesselClusters();
      // Same for the selection ring, which was riding a marker in that frame.
      // `restoreLiveLayer`'s pass puts it back on the live hull; when there is no
      // live layer to go back to, it correctly stays off.
      this.syncActiveHalo(null);
      // And any saved-vessel marker that had collapsed to a ring around a hull in
      // that frame: the hull left with the frame, so the diamond comes back
      // rather than leaving an empty ring on the water.
      this.syncLocatedTwins();
      return;
    }
    const sensor = this.sensor;
    // One animation at a time: entering snapshot playback drops any vessel track,
    // the mirror of `buildTrackLayers` leaving playback when a path is loaded.
    //
    // Gated on the first frame of the replay rather than asked on every one. It
    // has to stay ABOVE the live-layer hide below: closing a track runs a display
    // pass over the live layer to undim the fleet, and that pass must not be
    // handed a layer that has already been detached from the map.
    if (!this.playbackLayer && this.host.voyageTrack.hasTrack) {
      this.host.voyageTrack.close();
    }
    // Hide the live vessel layer while animating so the map never shows the
    // static live snapshot and the animated frame at the same time.
    if (this.liveLayer && this.host.map.hasLayer(this.liveLayer)) {
      this.host.map.removeLayer(this.liveLayer);
      this._liveLayerHidden = true;
    }
    // Reuse a single GeoJSON layer across frames (clear + addData) rather than
    // tearing down and recreating every marker each tick.
    if (!this.playbackLayer) {
      this.playbackLayer = (L as any)
        .geoJSON(null, {
          pointToLayer: (feature: any, latlng: any) =>
            this.host.displayDirectionInMap(feature, latlng, sensor),
          onEachFeature: (feature: any, layer: any) => {
            // Vessels stay selectable mid-playback, so track mode can be entered
            // from any frame. The popup is built on click rather than bound per
            // feature: onEachFeature runs for every vessel of every frame, and
            // bindPopup would allocate a Popup per vessel ~10x a second.
            layer.on('click', () => {
              this.activateVessel(feature.properties, layer);
              L.popup({
                className: 'ais-vessel-popup',
                closeButton: false,
                offset: L.point(0, -6),
                autoPanPadding: L.point(40, 90),
              } as any)
                .setLatLng(layer.getLatLng())
                .setContent(buildVoyagePopup(
                  feature.properties,
                  this.popupOptsFor(String(feature.properties?.['MMSI'] ?? feature.properties?.mmsi ?? '')),
                ))
                .openOn(this.host.map);
            });
          },
        })
        .addTo(this.host.map);
    }
    this.playbackLayer.clearLayers();
    this.currentFeatures = step.features ?? [];
    const filtered = this.applyTypeFilter(this.currentFeatures);
    this.playbackLayer.addData(this.host.convertToGeoJSONNew(filtered, 'VOYAGERADAR'));
    // After the data, not inside `onEachFeature`: clusters and labels are both
    // decided against the OTHER vessels in the frame, so the pass has to see the
    // whole frame first. A replay stays clustered for the same reason the live
    // view is — a flip-book of a thousand overlapping arrows is no more readable
    // for moving.
    this.applyVesselDisplay(this.playbackLayer);
  }

  /** Puts back the live layer that entering playback hid. No-op otherwise. */
  restoreLiveLayer(): void {
    if (!this._liveLayerHidden || !this.liveLayer) {
      // Playback can be started from a cold map, in which case there was no
      // live layer to hide and none to put back. Its frames seeded the
      // categories from the union of every loaded snapshot, and those would
      // otherwise outlive the last frame — leaving the Vessel Types section on
      // screen, filtering nothing, over an empty map.
      if (!this.dataActive) this.host.voyageRadarPlayback.resetTypeFilter();
      return;
    }
    this.host.map.addLayer(this.liveLayer);
    this._liveLayerHidden = false;
    // Entering playback replaced the categories with the union across every
    // loaded snapshot, which is a superset of what the live layer holds. Put
    // the live set back so the Vessel Types section does not keep offering
    // chips for types that left the map with the last frame.
    this.currentFeatures = this._liveFeatures;
    this.computeAvailableTypes(this._liveFeatures);
    this.applyTypeFilterToLive();
  }

  // ── Ship-type filter ────────────────────────────────────────────────────────
  /**
   * Groups features by ship type (colour + count taken from the data) and pushes
   * the result to the playback controller, so the filter chips reflect what is
   * actually loaded.
   */
  computeAvailableTypes(features: any[]): void {
    const groups = new Map<string, { color: string; count: number }>();
    for (const f of features || []) {
      const p = f?.properties || f || {};
      const label = p.shipType || p['Ship Type'] || 'Unknown';
      const color = p.color || p['Color'] || 'ORANGE';
      const g = groups.get(label);
      if (g) g.count += 1;
      else groups.set(label, { color, count: 1 });
    }
    const types = Array.from(groups.entries())
      .map(([label, g]) => ({ code: label, label, color: g.color, count: g.count }))
      .sort((a, b) => b.count - a.count);
    this.host.voyageRadarPlayback.setAvailableTypes(types);
  }

  /** Filters a raw feature list by the active ship types (empty set = show all). */
  applyTypeFilter(features: any[]): any[] {
    const filter = this.host.voyageRadarPlayback.snapshot().typeFilter;
    if (!filter || filter.size === 0) return features || [];
    return (features || []).filter((f) => {
      const p = f?.properties || f || {};
      const label = p.shipType || p['Ship Type'] || 'Unknown';
      return filter.has(label);
    });
  }

  /**
   * Re-applies the ship-type filter to the LIVE (non-playback) view by toggling
   * marker visibility on the existing layer — no second layer, so Spire and
   * ClydeSpace rendering is never touched.
   *
   * The ONE place a live marker's opacity is decided. Three rules stack here
   * rather than in three passes, because passes fight: the last one to run wins,
   * and which runs last depends on the order a refresh, a filter change and a
   * track load happen to arrive in.
   *
   * What is DRAWN is then decided downstream, in `applyVesselDisplay` — a strict
   * pipeline (visible → clustered → labelled), never a second independent pass:
   * each stage only refines what this one settled, and all three re-run together.
   */
  applyTypeFilterToLive(): void {
    if (this.host.voyageRadarPlayback.mode === 'playback') return;
    if (!this.dataActive || !this.liveLayer) return;
    const filter = this.host.voyageRadarPlayback.snapshot().typeFilter;
    const showAll = !filter || filter.size === 0;
    const focus = this._focusMmsi;
    const traced = this._tracedMmsi;
    this.liveLayer.eachLayer((layer: any) => {
      const props = layer?.feature?.properties ?? {};
      const label = props['Ship Type'] || 'Unknown';
      const passesFilter = showAll || filter.has(label);
      const mmsi = String(props['MMSI'] ?? '');
      // Filter first: a vessel the user has filtered out stays hidden even when
      // it is the one being traced.
      // Then the traced hull, held down so the moving ghost — not this static
      // marker — reads as the vessel.
      // Then the fleet dim, which only applies while `_focusMmsi` is set:
      // dimmed rather than hidden, because the surrounding traffic is still
      // context for the track, it just must not compete with it.
      const opacity = !passesFilter
        ? 0
        : traced && mmsi === traced
        ? 0.35
        : focus && mmsi !== focus
        ? 0.12
        : 1;
      if (typeof layer.setOpacity === 'function') {
        layer.setOpacity(opacity);
      }
      const el = typeof layer.getElement === 'function' ? layer.getElement() : null;
      if (el) {
        (el as HTMLElement).style.display = passesFilter ? '' : 'none';
      }
      // Clustering and labels both ride this decision: only a hull drawn at full
      // strength is a candidate for either. A cluster must not count a vessel
      // the user filtered out, and a tooltip is its own element in Leaflet's
      // tooltip pane — it inherits neither the marker's opacity nor its
      // `display:none`, so a filtered-out vessel would otherwise keep a name
      // floating over empty water.
      //
      // It also means clustering and the fleet dim never stack: while a track
      // holds the fleet at 0.12 there are no candidates, so no bubbles form. The
      // two are alternative answers to the same clutter, and running both would
      // hide the traffic around the track that the dim exists to keep visible.
      //
      // Recorded on the marker rather than acted on here, because what a
      // candidate actually gets also depends on the OTHER candidates — that is
      // `applyVesselDisplay`, below, over the whole layer at once.
      layer._vrVisible = opacity === 1;
    });
    this.applyVesselDisplay(this.liveLayer);
  }

  /**
   * Turns the visibility decision above into what is actually on screen, in
   * order: cluster the crowded cells, then label whatever survived.
   *
   * Order matters and is the whole reason these are not two independent passes —
   * a hull absorbed into a bubble must not also be carrying a name, and the label
   * thinning must not spend one of its cells on a marker that is now hidden.
   */
  private applyVesselDisplay(layer: any): void {
    if (layer && typeof layer.eachLayer === 'function') this.syncViewportRepaint();
    this.applyVesselClusters(layer);
    this.applyVesselLabels(layer);
    // Last, because it reads what the two passes above settled: the ring belongs
    // on a hull that is on screen, and on nothing otherwise.
    this.syncActiveHalo(layer);
    // Same reason, for the saved-vessel markers: whether one collapses to a ring
    // around a snapshot hull depends on whether that hull ended up painted.
    this.syncLocatedTwins();
  }

  /**
   * Replaces crowded groups of vessels with one count bubble each.
   *
   * A thousand hulls in one AOI is not a chart at any zoom that shows the whole
   * area — the arrows overlap into texture, and the only way to read it was to
   * zoom until they separated. A bubble says how many are down there and splits
   * into its members as the zoom makes room for them.
   *
   * The grid is anchored to WORLD pixels (`map.project` at the current zoom), not
   * to the viewport. That distinction is the difference between clustering that
   * feels solid and clustering that shimmers: bucket by screen position and the
   * grid slides across the vessels as you pan, so counts flicker and bubbles jump
   * between cells under a drag that changed nothing. In world pixels the cells
   * are pinned to the sea floor — a pan cannot re-bucket anything, and only a
   * zoom (which genuinely changes what fits) redraws them.
   *
   * Never absorbs the selected vessel: it is the one hull the operator is
   * tracking, and a selection that vanishes into a count is a selection lost.
   */
  private applyVesselClusters(layer: any): void {
    const map = this.host.map;
    if (!map || !layer || typeof layer.eachLayer !== 'function') {
      this.clearVesselClusters();
      return;
    }
    // Switched off from the toolbar: every hull the visibility pass allowed goes
    // back on the map. Written explicitly rather than just dropping the bubbles —
    // the vessels a bubble stood for are hidden, and nothing else would restore
    // them.
    if (!this.clustersOn) {
      // Only when bubbles were actually up: with grouping off this runs on every
      // pan and on every playback frame, and there is nothing to put back once
      // the first pass has put it back.
      if (this._clusterActive) {
        layer.eachLayer((marker: any) => {
          marker._vrClustered = false;
          if (marker._vrVisible === false) return;
          this.setMarkerHidden(marker, false);
        });
      }
      this.clearVesselClusters();
      return;
    }
    const zoom = map.getZoom();
    // Padded, like the labels: a bubble whose members sit just off screen still
    // belongs on the edge of the view, and the slack keeps a small pan from
    // rebuilding everything.
    const bounds = map.getBounds().pad(0.2);
    const activeMmsi = this.active?.mmsi ?? null;
    const cells = new Map<string, { members: any[]; x: number; y: number }>();

    layer.eachLayer((marker: any) => {
      marker._vrClustered = false;
      if (marker._vrVisible === false) return;
      const props = marker?.feature?.properties ?? {};
      if (activeMmsi && String(props['MMSI'] ?? '') === activeMmsi) return;
      const latlng = typeof marker.getLatLng === 'function' ? marker.getLatLng() : null;
      if (!latlng || !bounds.contains(latlng)) return;
      const pt = map.project(latlng, zoom);
      const cx = Math.floor(pt.x / VoyageMapController.CLUSTER_CELL);
      const cy = Math.floor(pt.y / VoyageMapController.CLUSTER_CELL);
      const key = `${cx}:${cy}`;
      const cell = cells.get(key);
      if (cell) {
        cell.members.push(marker);
        cell.x += pt.x;
        cell.y += pt.y;
      } else {
        cells.set(key, { members: [marker], x: pt.x, y: pt.y });
      }
    });

    let used = 0;
    cells.forEach((cell) => {
      if (cell.members.length < VoyageMapController.CLUSTER_MIN) return;
      for (const marker of cell.members) marker._vrClustered = true;
      // The centroid of the members, not the centre of the cell: a bubble parked
      // on a grid intersection sits in open water beside the traffic it counts.
      const centre = map.unproject(
        L.point(cell.x / cell.members.length, cell.y / cell.members.length),
        zoom
      );
      this.placeClusterBubble(used++, centre, cell.members);
    });

    // Hulls the bubbles did not take are drawn; the ones they took are not. Both
    // written here, so a vessel released by a zoom comes straight back.
    layer.eachLayer((marker: any) => {
      if (marker._vrVisible === false) return;
      this.setMarkerHidden(marker, marker._vrClustered === true);
    });
    this.trimClusterPool(used);
  }

  /** Positions (and if needed creates) pooled bubble `index`. */
  private placeClusterBubble(index: number, at: any, members: any[]): void {
    const map = this.host.map;
    // Recreated only if a map reset swept it off — same guard as `_trackRenderer`,
    // and for the same reason: the reference outlives the sweep.
    if (!this._clusterLayer || !map?.hasLayer(this._clusterLayer)) {
      this._clusterLayer = L.layerGroup().addTo(map);
      this._clusterPool = [];
      this._clusterActive = 0;
    }
    let bubble = this._clusterPool[index];
    if (!bubble) {
      bubble = L.marker(at, {
        icon: this.clusterIcon(members.length),
        // Above the vessels it stands for, below the traced ghost (200000).
        zIndexOffset: 150000,
        keyboard: false,
      } as any);
      bubble.on('click', () => this.onClusterClick(bubble));
      this._clusterPool[index] = bubble;
    } else {
      bubble.setLatLng(at);
      // `setIcon` rebuilds the element, so only when the number on it changed —
      // during playback most bubbles keep their count from frame to frame.
      if (bubble._vrCount !== members.length) {
        bubble.setIcon(this.clusterIcon(members.length));
      }
    }
    bubble._vrCount = members.length;
    bubble._vrMembers = members;
    if (!this._clusterLayer.hasLayer(bubble)) this._clusterLayer.addLayer(bubble);
  }

  /** Drops pooled bubbles beyond the `used` this pass needed. */
  private trimClusterPool(used: number): void {
    for (let i = used; i < this._clusterActive; i++) {
      const bubble = this._clusterPool[i];
      if (bubble && this._clusterLayer?.hasLayer(bubble)) {
        this._clusterLayer.removeLayer(bubble);
      }
    }
    this._clusterActive = used;
  }

  /**
   * The bubble glyph. Four size tiers rather than a continuous scale: the count
   * is written on it, so the size only has to say "more than that one", and a
   * smooth area ramp makes every bubble a slightly different size for no reading.
   */
  private clusterIcon(count: number): any {
    const tier = count < 10 ? 'sm' : count < 50 ? 'md' : count < 200 ? 'lg' : 'xl';
    const size = tier === 'sm' ? 30 : tier === 'md' ? 36 : tier === 'lg' ? 42 : 48;
    // Four digits do not fit a 48px circle, and past a thousand the exact number
    // is not the point — the bubble is telling you to zoom in either way.
    const text = count >= 1000 ? `${(count / 1000).toFixed(1)}k` : String(count);
    return L.divIcon({
      className: `vr-cluster vr-cluster--${tier}`,
      html: `<span>${text}</span>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  }

  /**
   * Clicking a bubble frames what is inside it — which is the whole interaction,
   * so it has to work even when zooming cannot separate the members.
   *
   * Vessels moored side by side at a berth, or a map already at its deepest zoom,
   * would otherwise be a bubble that answers a click by doing nothing. Those name
   * their hulls instead.
   */
  private onClusterClick(bubble: any): void {
    const map = this.host.map;
    const members: any[] = bubble?._vrMembers ?? [];
    if (!map || !members.length) return;
    const latlngs = members
      .map((m) => (typeof m.getLatLng === 'function' ? m.getLatLng() : null))
      .filter(Boolean);
    if (!latlngs.length) return;
    const bounds = L.latLngBounds(latlngs as any);
    const zoom = map.getZoom();
    const maxZoom = map.getMaxZoom?.() ?? 18;
    // 30 m across is one ship's length: at that spread there is no zoom at which
    // these become separate arrows.
    const spread = bounds.isValid()
      ? map.distance(bounds.getNorthWest(), bounds.getSouthEast())
      : 0;
    if (zoom >= maxZoom || spread < 30) {
      L.popup({
        className: 'ais-vessel-popup',
        closeButton: false,
        offset: L.point(0, -6),
        autoPanPadding: L.point(40, 90),
      } as any)
        .setLatLng(bubble.getLatLng())
        .setContent(
          buildVoyageClusterPopup(
            members.map((m) => {
              const p = m?.feature?.properties ?? {};
              return { name: p['Name'], mmsi: p['MMSI'] };
            })
          )
        )
        .openOn(map);
      return;
    }
    // Capped rather than a straight fit: a cell holding two tight knots of
    // traffic would otherwise jump straight to street zoom and lose the context
    // around it.
    map.fitBounds(bounds, {
      padding: [48, 48],
      maxZoom: Math.min(zoom + 3, maxZoom),
    } as any);
  }

  /** Show or hide one vessel marker, element and opacity together. */
  private setMarkerHidden(marker: any, hidden: boolean): void {
    if (typeof marker.setOpacity === 'function') marker.setOpacity(hidden ? 0 : 1);
    const el = typeof marker.getElement === 'function' ? marker.getElement() : null;
    if (el) (el as HTMLElement).style.display = hidden ? 'none' : '';
  }

  /** Takes every bubble off the map — the vessels they stood for have gone. */
  clearVesselClusters(): void {
    if (this._clusterLayer) this._clusterLayer.clearLayers();
    this._clusterPool = [];
    this._clusterActive = 0;
  }

  /**
   * Decides which of a layer's markers actually carry a name label, and binds or
   * removes to match. Two rules, both of them there because "a label on every
   * vessel" does not survive a real AOI:
   *
   *   in view — a label on a vessel that is off screen shows nothing and costs
   *     the same. Padded by a fifth, so a hull just past the edge can still push
   *     its name in, and small pans do not thrash the bindings.
   *
   *   decluttered — one label per `LABEL_CELL` of screen, first marker in layer
   *     order wins. A thousand hulls in one AOI is a thousand tooltip elements
   *     that Leaflet re-positions on every zoom (which is the delay), and a
   *     thousand names over each other is unreadable anyway. Thinning fixes both
   *     at once: what is left is the labels you could actually have read.
   *
   * Zoom in and the cells cover less water, so more names appear — the density
   * of labels stays roughly constant while the density of vessels does not.
   */
  private applyVesselLabels(layer: any): void {
    const map = this.host.map;
    if (!map || !layer || typeof layer.eachLayer !== 'function') return;
    const show = this.labelsOn;
    // Nothing bound and nothing wanted: no reason to walk the layer at all. This
    // is the common case — it keeps the per-frame call in `renderStep` free.
    if (!show && !this._labelsPainted) return;
    const zoom = map.getZoom();
    const bounds = map.getBounds().pad(0.2);
    const taken = new Set<string>();
    layer.eachLayer((marker: any) => {
      const props = marker?.feature?.properties ?? {};
      // A hull inside a bubble is not on screen to be named, and spending one of
      // the thinning cells on it would cost a name that IS visible.
      let wanted = show && marker._vrVisible !== false && marker._vrClustered !== true;
      if (wanted) {
        const latlng = typeof marker.getLatLng === 'function' ? marker.getLatLng() : null;
        wanted = !!latlng && bounds.contains(latlng);
        if (wanted) {
          // World pixels, not container pixels — same reasoning as the cluster
          // grid: anchored to the viewport, a pan slides the grid across the
          // fleet and names hop from hull to hull under a drag.
          const pt = map.project(latlng, zoom);
          const cell = `${Math.floor(pt.x / VoyageMapController.LABEL_CELL_X)}:${Math.floor(
            pt.y / VoyageMapController.LABEL_CELL_Y
          )}`;
          if (taken.has(cell)) wanted = false;
          else taken.add(cell);
        }
      }
      this.setVesselLabel(marker, props, wanted);
    });
    this._labelsPainted = taken.size > 0;
  }

  /** Whichever layer the clusters and labels belong to right now. */
  private get displayLayer(): any {
    return this.host.voyageRadarPlayback.mode === 'playback'
      ? this.playbackLayer
      : this.liveLayer;
  }

  /**
   * Clusters and labels are both viewport-scoped, so the view moving has to
   * recompute them.
   *
   * `moveend`/`zoomend` rather than `move`/`zoom`: mid-gesture rebuilding is
   * invisible under the animation and would put a walk of the whole layer inside
   * every animation frame. Attached from the first display pass and dropped when
   * the vessels leave the map, so an idle map with nothing on it pays nothing.
   */
  private syncViewportRepaint(): void {
    const map = this.host.map;
    if (!map || this._viewportRepaint) return;
    this._viewportRepaint = () => this.applyVesselDisplay(this.displayLayer);
    map.on('moveend zoomend', this._viewportRepaint);
  }

  /** Stops recomputing on view changes (nothing left to recompute). */
  private stopViewportRepaint(): void {
    if (!this._viewportRepaint) return;
    this.host.map?.off('moveend zoomend', this._viewportRepaint);
    this._viewportRepaint = null;
  }

  /** True while the T control is on. The flag lives on the playback controller. */
  private get labelsOn(): boolean {
    return this.host.voyageRadarPlayback.snapshot().showLabels === true;
  }

  /**
   * True while vessels are being grouped into bubbles.
   *
   * `!== false`, not `=== true`: the field is optional on `PlaybackState` (see
   * there), and grouping is the default — an absent flag must not read as an
   * operator who turned it off.
   */
  private get clustersOn(): boolean {
    return this.host.voyageRadarPlayback.snapshot().clusterVessels !== false;
  }

  /**
   * Binds (or removes) one vessel marker's permanent name label — the T control,
   * for both the live snapshot and the playback frames.
   *
   * The offset is measured off the marker's OWN icon rather than fixed: the two
   * paths that draw live vessels use different glyph sizes (50px from the sensor
   * pull, 24px from a recalled refresh), and one constant would either sit on top
   * of the bigger one or float away from the smaller one.
   */
  private setVesselLabel(marker: any, props: any, show: boolean): void {
    if (!marker || typeof marker.bindTooltip !== 'function') return;
    const hasTooltip = typeof marker.getTooltip === 'function' && !!marker.getTooltip();
    // `||`, not `??`: a vessel that broadcasts no name arrives as '', which is
    // not nullish — and a PERMANENT tooltip bound to '' is an empty box parked
    // over the water for as long as labels are on. Same reasoning as
    // `toggleGhostLabel`.
    const text = show
      ? String(props?.['Name'] ?? '') || String(props?.['MMSI'] ?? '')
      : '';
    if (!text) {
      if (hasTooltip) marker.unbindTooltip();
      return;
    }
    if (hasTooltip) {
      marker.setTooltipContent(text);
      return;
    }
    const iconWidth = marker.options?.icon?.options?.iconSize?.[0] ?? 24;
    marker.bindTooltip(text, {
      permanent: true,
      direction: 'right',
      offset: [Math.round(iconWidth / 2 + 4), 0],
      className: 'ais-vessel-label',
    });
  }

  /**
   * Dim every live vessel except `mmsi` (null restores them all). Reuses the
   * type-filter pass so there is exactly one place that decides a marker's
   * opacity — two independent passes would fight each other.
   *
   * No early-out on an unchanged MMSI: `clearTrackLayers` calls this with null
   * to undo a track whose dim the user had already switched off, and that call
   * still has to repaint the traced hull back to full.
   */
  setTrackFocus(mmsi: string | null): void {
    this._focusMmsi = mmsi;
    this.applyTypeFilterToLive();
  }

  /**
   * Called by the host once live vessels are on the map: adopts the layer, seeds
   * the ship-type categories and re-applies the active filter. The layer is
   * rebuilt from scratch on every pull, so without the re-apply a refresh would
   * silently show every type again while the toolbar still reports a filter.
   */
  adoptLiveLayer(layer: any, features: any[]): void {
    this.dataActive = true;
    this.liveLayer = layer;
    this.currentFeatures = Array.isArray(features) ? features : [];
    this._liveFeatures = this.currentFeatures;
    this.indexLiveVessels(this.currentFeatures);
    // After the index, whose de-duplicated size is what the dim acts on — the
    // raw feature list can carry one MMSI more than once. A selection dropped
    // further down by `reanchorActiveVessel` re-pushes this through
    // `onActiveChange`, so the count never describes a vessel that has gone.
    this.host.voyageTrack.setOthersOnMap(this.othersOnMap(this.active?.mmsi ?? null));
    this.computeAvailableTypes(this.currentFeatures);
    this.applyTypeFilterToLive();
    // Last, and after the filter pass: the selection is reconciled against the
    // markers that are actually visible, and the dim it may re-apply must not be
    // undone by that pass.
    this.reanchorActiveVessel();
    // History vessels stay LIST-ONLY (drawn on demand from the panel), so a
    // snapshot arriving only recomputes the rows and arms the popup action.
    this.ensurePopupActionsWired();
    this.pushLocatedState();
    // A snapshot landing is the moment the free search goes from having nothing
    // to filter to having a fleet.
    this.pushSearchContext();
  }

  /**
   * Reconciles the selection with a live layer that has just REPLACED the one it
   * was made from — a refresh-history pick, a step through the archive, or a
   * fresh pull.
   *
   * Without this the selection survives the swap describing the layer that has
   * gone: the halo stays parked where the hull was in the previous snapshot, and
   * clicking that same hull again is a no-op, because `activateVessel` sees an
   * MMSI that is already selected. The vessel the user just clicked ends up the
   * one vessel on the map that is not highlighted.
   */
  reanchorActiveVessel(): void {
    if (!this.active) return;
    const marker = this.liveMarkerFor(this.active.mmsi);
    if (marker) {
      // No opacity to re-apply here: `_tracedMmsi` survives the layer swap, so
      // `adoptLiveLayer`'s filter pass has already faded this hull if a track is
      // up. See `applyTypeFilterToLive`.
      this.anchorActive(marker);
      return;
    }
    // A track keeps its vessel selected even when that hull is not in the
    // snapshot: the path is still drawn and the toolbar still belongs to it.
    // There is simply no live marker to fade — see `_adoptTrackVessel`.
    if (this.host.voyageTrack.hasTrack) return;
    // Nothing left to select: leaving it would park a halo over empty water and
    // offer a Trace CTA for a vessel that is not on the map.
    this.deactivateVessel();
  }

  /**
   * Puts the selection ring where the selected hull actually IS right now — or
   * takes it off the map when that hull is not drawn at all.
   *
   * The selection deliberately outlives the layer it was made from (a refresh, a
   * history pick and a playback frame all swap the markers underneath it), so
   * `this.active` alone never says whether there is a vessel on screen to ring.
   * Reading the drawn layer here is what stops the halo being left pulsing over
   * empty water — after the map is cleared, after the ship-type filter drops the
   * selected vessel's category, on any playback frame the vessel is absent from,
   * and for anything else the display pass decides not to paint.
   *
   * Runs at the END of the display pipeline (see `applyVesselDisplay`), because
   * it reads what that pipeline drew: a ring decided before the clustering pass
   * would be answering a question that pass had not settled yet.
   *
   * Hides rather than deselects: everything here is reversible from the toolbar
   * — unfilter the type, switch grouping off, step back to a snapshot the vessel
   * is in — and the ring comes straight back with the hull. Dropping the
   * selection is for when the vessel is gone for good (`reanchorActiveVessel`,
   * `dropSelectionWithMap`).
   */
  private syncActiveHalo(layer: any): void {
    const active = this.active;
    if (!active) return;
    // A loaded track owns the halo: `renderTrackFrame` rides it along the ghost,
    // and the traced hull is held faded by the pass above — so reading the layer
    // here would take the ring off a track that is still playing.
    if (this.host.voyageTrack.hasTrack) return;
    const marker = this.drawnMarkerFor(layer, active.mmsi);
    // Same reasoning as `anchorActive`: a position belongs to the SNAPSHOT, not
    // to the vessel, so the selection follows the marker it is drawn on rather
    // than keeping the coordinates it was first clicked at.
    const latlng =
      marker && typeof marker.getLatLng === 'function' ? marker.getLatLng() : null;
    if (latlng) {
      active.lat = latlng.lat;
      active.lng = latlng.lng;
    }
    this.placeHaloOn(marker, active);
  }

  /**
   * Rings `marker` — or takes the ring off the map when that marker is not one
   * the display pipeline actually painted.
   *
   * The ONE place the ring is put on a live/playback marker, so a filtered-out
   * or clustered hull cannot end up ringed by whichever call site ran last:
   * `syncActiveHalo` reaches here from the display pass, and `anchorActive` from
   * the selection being re-pointed at a layer that has just replaced its own.
   */
  private placeHaloOn(marker: any, active: VoyageActiveVessel): void {
    if (!this.isMarkerPainted(marker)) {
      this.removeHalo();
      return;
    }
    this.applyActiveVisual(active);
  }

  /** A layer's marker for an MMSI, but only while it is actually painted. */
  private drawnMarkerFor(layer: any, mmsi: string): any | null {
    if (!layer || typeof layer.eachLayer !== 'function') return null;
    let found: any = null;
    layer.eachLayer((l: any) => {
      if (found) return;
      const p = l?.feature?.properties ?? {};
      if (String(p['MMSI'] ?? p.mmsi ?? '') !== mmsi) return;
      if (!this.isMarkerPainted(l)) return;
      found = l;
    });
    return found;
  }

  /**
   * Whether a vessel marker is on screen right now.
   *
   * The icon element's own `display` is the ground truth, not `_vrVisible` /
   * `_vrClustered`: those record what the pipeline last INTENDED for a marker,
   * and the two drift whenever a layer is out of the pipeline's reach — the live
   * layer keeps the flags it was wearing when playback took it off the map, and
   * grouping switched off in the meantime rewrites neither.
   *
   * The flags are the fallback for a marker Leaflet has given no element: that
   * is a layer which is not on the map at all, where they are the only record of
   * what it looked like when it was.
   */
  private isMarkerPainted(marker: any): boolean {
    if (!marker) return false;
    const el = typeof marker.getElement === 'function' ? marker.getElement() : null;
    if (el) return (el as HTMLElement).style.display !== 'none';
    return marker._vrVisible !== false && marker._vrClustered !== true;
  }

  /** The live layer's marker for an MMSI, or null when that hull is not drawn. */
  private liveMarkerFor(mmsi: string): any | null {
    const layer = this.liveLayer;
    if (!layer || typeof layer.eachLayer !== 'function') return null;
    let found: any = null;
    layer.eachLayer((l: any) => {
      if (found) return;
      const p = l?.feature?.properties ?? {};
      if (String(p['MMSI'] ?? p.mmsi ?? '') === mmsi) found = l;
    });
    return found;
  }

  /**
   * Points the selection at `marker` — the same hull, in whatever layer is drawn
   * now.
   *
   * A vessel's position belongs to the SNAPSHOT, not to the vessel, so it is
   * re-read here rather than at each call site: `active.lat/lng` is what the halo
   * is drawn at, and what `clearTrackLayers` puts it back on when a track closes.
   */
  private anchorActive(marker: any, properties?: any): void {
    const active = this.active;
    if (!active) return;
    const latlng = typeof marker?.getLatLng === 'function' ? marker.getLatLng() : null;
    if (latlng) {
      active.lat = latlng.lat;
      active.lng = latlng.lng;
    }
    const p = properties ?? marker?.feature?.properties ?? {};
    active.timestampIso = p['Timestamp UTC'] ?? p['time_utc'] ?? active.timestampIso;
    // A loaded track owns the halo — `renderTrackFrame` rides it along the ghost,
    // so moving it onto the live marker here would fight the next frame.
    //
    // Through `placeHaloOn`, not straight to `applyActiveVisual`: the marker this
    // is re-pointed at is looked up by MMSI alone (`liveMarkerFor`), so it may be
    // one the display pass has filtered out or absorbed into a bubble — and
    // ringing it here would put back the ring that pass had just taken off.
    if (!this.host.voyageTrack.hasTrack) this.placeHaloOn(marker, active);
  }

  /**
   * Rebuilds the MMSI → name/colour index the Vessel Tracks panel reads.
   *
   * Both the mapped Feature keys and the raw feed keys are accepted, because
   * `adoptLiveLayer` is called from two host paths that differ in which shape
   * they hand over.
   */
  indexLiveVessels(features: any[]): void {
    this.liveVessels.clear();
    for (const f of features || []) {
      const p = f?.properties || f || {};
      const mmsi = String(p['MMSI'] ?? p.mmsi ?? '');
      if (!mmsi) continue;
      this.liveVessels.set(mmsi, {
        name: p['Name'] ?? p.name ?? null,
        color: p['Color'] ?? p.color ?? null,
      });
    }
  }

  /**
   * Vessels on the map that a track's dim would fade — the snapshot minus the
   * traced hull, which is held at its own fade for as long as the track is up
   * (see `_tracedMmsi`).
   *
   * The traced vessel is not always IN the snapshot: a track replayed from the
   * Vessel Tracks panel can belong to a hull that has left the AOI, or be drawn
   * over an empty map entirely, which is the case this count exists for.
   */
  private othersOnMap(tracedMmsi: string | null): number {
    const total = this.liveVessels.size;
    return tracedMmsi && this.liveVessels.has(tracedMmsi) ? total - 1 : total;
  }

  /**
   * Drops the live-layer bookkeeping without touching the map itself.
   *
   * `keepLayerRef` exists because the two callers have always differed:
   * `removeDataSourceById` clears the reference (that layer is gone for good),
   * while `removeVesselLayer` clears only the flags. The asymmetry looks
   * incidental, but resolving it is not this extraction's job — preserved as-is.
   */
  releaseLiveLayer(keepLayerRef = false): void {
    this.dataActive = false;
    this.currentFeatures = [];
    this._liveFeatures = [];
    // The bubbles counted the vessels that just left, so they go with them —
    // otherwise a cleared map keeps a scatter of counts over empty water. Also
    // stops the viewport listener: there is nothing left for a pan to recompute,
    // and the next `adoptLiveLayer` re-attaches it.
    this.clearVesselClusters();
    this.stopViewportRepaint();
    // The selection ring goes with the hull it was drawn on. Nothing else takes
    // it off here: the display pipeline (which normally decides this) only runs
    // over a layer, and there is no layer left to run over — so a halo would sit
    // pulsing over empty water until something happened to draw vessels again.
    //
    // The SELECTION survives, like the type filter above: this also runs mid-swap
    // between snapshots, where `adoptLiveLayer` re-anchors the ring onto the same
    // vessel in the layer that lands next. A map emptied for real goes through
    // `dropSelectionWithMap`.
    this.syncActiveHalo(null);
    // The presence dots in the Vessel Tracks panel describe the layer that just
    // went, so they go with it — every vessel reads "not in view" until the next
    // pull, which is the truth.
    this.liveVessels.clear();
    // Same reason: with the fleet gone there is nothing left for the track bar's
    // dim button to fade, so it takes itself off the bar until the next pull.
    this.host.voyageTrack.setOthersOnMap(0);
    if (!keepLayerRef) this.liveLayer = null;
    // The ship-type categories describe the layer that just went, so they go
    // with it — that is what hides the Vessel Types section instead of leaving
    // it listing types for a map with nothing on it. The user's SELECTION is
    // left alone: this also runs mid-swap between snapshots, where dropping it
    // would clear the filter on every step through history. Clearing the map
    // for real goes through `resetTypeFilter()`.
    if (this.host.voyageRadarPlayback.snapshot().availableTypes.length) {
      this.host.voyageRadarPlayback.setAvailableTypes([]);
    }
    // The located overlay stays — a searched vessel does not belong to the
    // snapshot that left.
    this.pushLocatedState();
    // The map is empty again, so the drawer goes back to naming the act that
    // would fill it rather than offering a filter over nothing.
    this.pushSearchContext();
  }

  // ── Toolbar refresh ─────────────────────────────────────────────────────────
  /**
   * Toolbar Refresh → pull a fresh AOI snapshot. Delegates to the host's
   * `fetchAISDataFromServer` so the entitlement guards, popups, spinner,
   * refresh-history push and rendering stay in one place (identical to
   * re-checking the sensor). Resolves once the pull settles, so the toolbar's
   * spinner tracks the real request.
   */
  refresh(): Promise<void> {
    // No sensor context from the map toolbar, so it acts on the first Voyage
    // Radar sensor — unchanged behaviour. The panel's button knows which row it
    // belongs to and passes it, which is the correct one on a two-VR-sensor task.
    const sensors = this.host.collection?.sensors ?? [];
    return this.refreshSensor(
      sensors.find((s: any) => s?.operatorKey === OPERATORS.VOYAGERADAR) ?? null
    );
  }

  /**
   * Pull a fresh AOI snapshot for a specific Voyage Radar sensor.
   *
   * Deliberately calls `fetchAISDataFromServer` directly rather than going
   * through `toggleSensorNew` the way the old sensor checkbox did: that path is
   * several hundred lines of shared, operator-agnostic teardown (map reset,
   * prediction clearing, scene re-selection) whose only purpose on this branch
   * was to arrive here. Entitlement guards, popups, spinner and the
   * refresh-history push all live in `fetchAISDataFromServer` either way.
   */
  refreshSensor(sensor: any): Promise<void> {
    // Playback owns the map while animating — refreshing under it would fight
    // the frame renderer, so ignore the click until the user exits playback.
    if (this.host.voyageRadarPlayback.mode === 'playback') return Promise.resolve();
    const sensors = this.host.collection?.sensors ?? [];
    const sensorIndex = sensors.indexOf(sensor);
    if (!sensor || sensorIndex === -1) return Promise.resolve();
    const scene = sensor?.scenes?.find((s: any) => s?.checked) ?? sensor?.scenes?.[0];
    if (!scene) return Promise.resolve();

    // `checked` is no longer a control, but it is still STATE: it is what marks
    // this sensor as having data on the map, and several readers downstream
    // (the timeseries overlay, `clearVoyageSnapshot`, the scene lookup above)
    // depend on it. The old checkbox set it as a side effect of being ticked;
    // now the refresh sets it deliberately. Failure paths inside
    // `fetchAISDataFromServer` still reset it, and the ON MAP bar's clear still
    // unsets it, so the flag's lifecycle is unchanged.
    sensor.checked = true;

    return new Promise<void>((resolve) => {
      this._refreshResolve = resolve;
      this._refreshStarted = false;
      try {
        this.host.fetchAISDataFromServer(this.host.collection, sensorIndex, scene);
      } catch {
        this.settleRefresh();
        return;
      }
      // Every guard path (TOI not started, expired, refreshes exhausted) returns
      // synchronously without firing a request — settle now so the button resets.
      if (!this._refreshStarted) this.settleRefresh();
    });
  }

  /** The host calls this once a refresh request is genuinely in flight. */
  markRefreshStarted(): void {
    this._refreshStarted = true;
  }

  /** Completes the promise handed to the toolbar's refresh control (once). */
  settleRefresh(): void {
    const done = this._refreshResolve;
    this._refreshResolve = null;
    this._refreshStarted = false;
    done?.();
  }

  // ── Vessel icons ────────────────────────────────────────────────────────────
  /**
   * The vessel glyph as a data URL, cached.
   *
   * Encoding a 24px PNG through `canvas.toDataURL()` is expensive, and playback
   * asks for one per vessel per frame — roughly 10x/second across the whole AOI.
   * Without this, a busy snapshot re-encodes thousands of identical images a
   * second, which is what used to hang the flip-book.
   *
   * The key is (view, colour) only. Ship type is NOT part of it: unlike Spire —
   * which maps type to colour via `getHistoricalShipColor` — Voyage Radar takes
   * the colour straight from the feed and falls back to one constant per view,
   * so two ship types with the same colour are the same image.
   */
  vesselIconUrl(isHistory: boolean, color?: string): string {
    const key = `${isHistory ? 'history' : 'live'}|${color ?? ''}`;
    const cached = this._iconCache.get(key);
    if (cached) return cached;

    const iconColor = color || (isHistory ? 'YELLOW' : 'ORANGE');
    const canvas = isHistory
      ? this.host.createHistoricalNavigationIcon(iconColor)
      : this.host.createNavigationIcon(iconColor);
    const url = canvas.toDataURL('image/png');
    this._iconCache.set(key, url);
    return url;
  }

  // ── Track mode: entitlements ────────────────────────────────────────────────
  /**
   * Remaining API credits in the sensor's wallet, or null when the wallet is
   * unknown. Null means UNKNOWN, never zero — see `voyageCreditsRemaining`.
   */
  creditsRemaining(): number | null {
    return voyageCreditsRemaining(this.sensor);
  }

  /**
   * Pre-flight for every paid action this controller owns: the message to
   * refuse with, or null to proceed.
   *
   * Five call sites shared four hand-written variants of the same two refusals
   * before this, and the track one had drifted furthest — its own sentence, its
   * own word order, no mention of the balance being unknown. Routing all five
   * through `voyageSpendRefusal` is what makes the wallet speak with one voice,
   * and it is the only place the TOI needs adding for all five to honour it.
   *
   * This is the FULL ladder, unlike the drawers' `voyageSpendBlocker`: a control
   * stays pressable through an ended TOI precisely so the press lands here and
   * gets told why. Refusing again in the drawer would be the message nobody
   * reads; refusing here is the message the click asked for.
   *
   * An UNKNOWN balance still proceeds (see `canAfford`) — the server re-checks
   * and answers 402, which each caller's failure path reports. An ended TOI does
   * NOT: that fact is complete on the client, so there is nothing to defer.
   */
  private spendRefusal(action: VoyageSpendAction, cost: number): string | null {
    return voyageSpendRefusal(voyageSensorSpendGate(this.sensor, action, cost));
  }

  /** True once the TOI has closed — nothing on this task can be bought again. */
  private get toiEnded(): boolean {
    return hasVoyageToiFinished(this.sensor);
  }

  /** Already-purchased tracks for an MMSI — replayable for free. */
  savedTracksFor(mmsi: string): SavedTrackRef[] {
    const history: any[] = Array.isArray(this.sensor?.trackHistory)
      ? this.sensor.trackHistory
      : [];
    return history
      .filter((t) => t?.trackId && String(t.mmsi) === String(mmsi))
      .sort((a, b) => new Date(b.name).getTime() - new Date(a.name).getTime());
  }

  // ── Track mode: selection ───────────────────────────────────────────────────
  /**
   * Selects the clicked vessel. Selection is purely local — no request, no
   * credit — and is what gives the trace, port-call and nearby drawers a
   * subject. All three buttons stand on the bar either way; what changes here is
   * what they are ABOUT.
   */
  activateVessel(properties: any, marker: any): void {
    const p = properties || {};
    const mmsi = String(p['MMSI'] ?? p.mmsi ?? '');
    if (!mmsi) return;
    // Every hull click passes through here, and every hull popup now carries
    // the "Get latest" button — make sure its click-delegate is armed.
    this.ensurePopupActionsWired();
    // A loaded track is not given up by a click on another hull. Reading the
    // traffic around a path is most of what a path is for, and the vessels being
    // read are the dimmed ones — a stray click among them used to discard a PAID
    // track and put the map back where it was, with nothing to undo it.
    //
    // Leaflet still opens that vessel's popup on the same click (bound at the
    // layer, not here), so the information is on screen either way. Selecting a
    // different vessel now means closing the track first — the X on the track
    // bar, which is the deliberate act discarding it always should have been.
    if (this.host.voyageTrack.hasTrack && this.active && this.active.mmsi !== mmsi) {
      return;
    }
    // One animation at a time: selecting a vessel pauses snapshot playback.
    if (this.host.voyageRadarPlayback.mode === 'playback') {
      this.host.voyageRadarPlayback.pause();
    }
    if (this.active?.mmsi === mmsi) {
      // Same vessel, but NOT necessarily the same marker: a history pick, a
      // refresh or a playback frame swaps the layer under the selection, and the
      // hull has usually moved between snapshots. Re-anchor instead of returning
      // — a bare return leaves the halo on the position it was first clicked at,
      // which is the one place the vessel no longer is.
      this.anchorActive(marker, p);
      return;
    }
    this.deactivateVessel();
    const latlng = typeof marker?.getLatLng === 'function' ? marker.getLatLng() : null;
    const rawColor = String(p['Color'] ?? '');
    this.active = {
      mmsi,
      name: String(p['Name'] ?? mmsi),
      color: /^#[0-9a-fA-F]{3,8}$/.test(rawColor) ? rawColor : VOYAGE_DEFAULT_TRACK_COLOR,
      lat: latlng ? latlng.lat : Number(p['Lat']),
      lng: latlng ? latlng.lng : Number(p['Lng']),
      timestampIso: p['Timestamp UTC'] ?? p['time_utc'],
      shipType: p['Ship Type'] ?? 'OTHER',
    };
    this.syncCreditLines();
    this.host.voyageTrack.setSavedTracks(this.savedTracksFor(mmsi));
    this.host.voyageTrack.setActiveVessel(this.active);
  }

  /**
   * The map has been EMPTIED — the ON MAP bar's clear, a snapshot switched back
   * off — so the selection goes with it: the halo, the trace drawer and the
   * track bar's "Trace · <vessel>" pill all describe a hull that is no longer on
   * screen and cannot be brought back without drawing a snapshot again.
   *
   * Distinct from `releaseLiveLayer`, which also runs while one snapshot is
   * being swapped for the next and must NOT throw the selection away — that is
   * what `reanchorActiveVessel` re-establishes on the layer that lands.
   *
   * A loaded track keeps its vessel selected: the path is still drawn over the
   * (now empty) map and the track bar still belongs to it, exactly as in
   * `reanchorActiveVessel`. Closing the track is the X on that bar.
   */
  dropSelectionWithMap(): void {
    if (!this.active || this.host.voyageTrack.hasTrack) return;
    this.deactivateVessel();
  }

  /** Clears the selection, its halo and any track drawn for it. */
  deactivateVessel(): void {
    // The trace drawer is about the vessel being deselected — dismiss it too.
    this.host.toolbarDrawer.close();
    this.clearTrackLayers();
    this.removeHalo();
    this.active = null;
    if (this.host.voyageTrack.activeMmsi) this.host.voyageTrack.setActiveVessel(null);
  }

  /**
   * The halo's own map pane, created on first use.
   *
   * NOT `shadowPane`, which is where a ring drawn behind a marker would
   * otherwise belong: this component's stylesheet hides that pane outright
   * (`.leaflet-shadow-pane { display: none !important }`, to suppress Leaflet's
   * default marker shadows), so a halo rendered into it is never painted.
   *
   * z-index 500 keeps the stacking the shadow pane implied: above the track
   * trails (the canvas renderer draws into overlayPane, 400) and below the
   * vessel glyphs — the same stylesheet lifts `.leaflet-marker-pane` to 2700,
   * so the glyphs clear it comfortably.
   *
   * `pointer-events: none` because the ring is 72px across and would otherwise
   * swallow clicks aimed at the water — and at the vessel itself, once the halo
   * is wider than the glyph. `interactive: false` only stops Leaflet from
   * wiring handlers; it does not stop the div from being a hit target.
   */
  private haloPane(): string {
    const name = 'vrHaloPane';
    const map = this.host.map;
    // Created lazily rather than in the constructor: the controller is built
    // alongside the component, before its map exists. Panes outlive every layer
    // sweep (`resetMapNew` walks layers, not panes), so this runs once in
    // practice and the guard just keeps it idempotent.
    if (map && !map.getPane(name)) {
      const pane = map.createPane(name);
      pane.style.zIndex = '500';
      pane.style.pointerEvents = 'none';
    }
    return name;
  }

  /**
   * Takes the selection ring off the map WITHOUT touching the selection itself.
   *
   * The two are separate because the hull can leave the map while the vessel is
   * still the one the toolbar belongs to — see `syncActiveHalo`.
   */
  private removeHalo(): void {
    if (!this._haloMarker) return;
    this.host.map?.removeLayer(this._haloMarker);
    this._haloMarker = null;
  }

  /** Creates/moves the pulsing selection ring behind the vessel glyph. */
  applyActiveVisual(vessel: VoyageActiveVessel | null): void {
    if (!vessel) {
      this.removeHalo();
      return;
    }
    if (!this._haloMarker) {
      this._haloMarker = L.marker([vessel.lat, vessel.lng], {
        icon: L.divIcon({
          className: 'vr-halo',
          html: `<span class="vr-halo__ring"></span><span class="vr-halo__pulse"></span>`,
          iconSize: [72, 72],
          iconAnchor: [36, 36],
        }),
        interactive: false,
        keyboard: false,
        pane: this.haloPane(),
      } as any).addTo(this.host.map);
    } else {
      this._haloMarker.setLatLng([vessel.lat, vessel.lng]);
    }
    this._haloMarker.getElement()?.style?.setProperty('--vr-halo-color', vessel.color);
  }

  // ── Track mode: loading ─────────────────────────────────────────────────────
  /**
   * Pre-flight gate for the paid full-track fetch.
   *
   * There is no confirm-before-spend dialog: picking a period and then pressing
   * Trace is already two deliberate clicks, and the drawer states the price
   * ("Uses 5 credits · N remaining") directly above the button. A modal
   * repeating that was a third click saying nothing new.
   */
  confirmFullTrack(minutes: number): void {
    const vessel = this.active;
    if (!vessel) return;
    // One paid fetch at a time — two in flight together would both pass the
    // server's pre-flight credit check and spend two operator calls.
    if (this.host.voyageTrack.snapshot().trackLoading) return;
    const refusal = this.spendRefusal('track', this.trackCredits);
    if (refusal) {
      this.host.popupService.openPopup(CollectionPopupComponent, refusal, '', '40%');
      return;
    }
    this.traceVesselFull(minutes);
  }

  /**
   * PAID track: the operator's recorded path for the active vessel.
   *
   * Private, because it is the only spend in this controller with its gate in a
   * SEPARATE method — the other four check and fetch in one body, so there is
   * no ungated entrance to reach. `confirmFullTrack` is that entrance.
   */
  private traceVesselFull(minutes: number): void {
    const vessel = this.active;
    if (!vessel) return;
    const sensor = this.sensor;
    this.host.voyageTrack.setTrackLoading(true, {
      trackId: null, // a paid fetch has no stored id until it lands
      source: 'full',
      mmsi: vessel.mmsi,
      label: vessel.name,
      // The selection's name is already resolved (`_adoptTrackVessel` and the
      // popup both put the shared resolver's label there), so an unnamed hull
      // arrives as "MMSI …" — which is a stand-in, not a broadcast name.
      isNamed: !!vessel.name && vessel.name !== `MMSI ${vessel.mmsi}` && vessel.name !== vessel.mmsi,
      color: vessel.color,
      // Unknowable before the operator answers — the pending row says "buying"
      // rather than inventing a count. The span is what was asked for, so it
      // can be shown.
      points: null,
      spanLabel: VOYAGE_TRACK_SPANS.find((s) => s.minutes === minutes)?.label ?? null,
      window: null,
    });
    this.host.apiService
      .getVoyageRadarHistoricalData({
        taskid: this.host.collection?.taskId,
        sensorId: sensor?.id,
        mmsi: vessel.mmsi,
        vesselName: vessel.name,
        shipType: vessel.shipType,
        endTime: vessel.timestampIso,
        inputTime: minutes,
        startTimeType: 'minutes',
        // Human label for the saved-track chips ("24 h" reads better than
        // "1440 minutes" once the track is in trackHistory).
        spanLabel: VOYAGE_TRACK_SPANS.find((s) => s.minutes === minutes)?.label,
      })
      .subscribe(
        (res: any) => {
          this.host.voyageTrack.setTrackLoading(false);
          const data = res?.data ?? [];
          if (!data.length) {
            // The operator does not bill empty results, so nothing was spent.
            this.host.popupService.openPopup(
              CollectionPopupComponent,
              'There are no recorded positions for this vessel in the selected period. Try a longer span.',
              '',
              '40%'
            );
            return;
          }
          this.applyCreditSpend(res, sensor?.id, this.trackCredits);
          this.host.voyageTrack.setSpan(minutes);
          const points = this.toTrackPoints(data);
          if (points.length < 2) {
            // The credit is already spent (the operator returned data), so say
            // so plainly rather than leaving the map silently unchanged.
            this.host.popupService.openPopup(
              CollectionPopupComponent,
              'Only one position was recorded for this vessel in the selected period, ' +
                'so there is no path to draw. The fetch has been used — try a longer span.',
              '',
              '40%'
            );
            return;
          }
          // `applyCreditSpend` has already pushed `res.track` onto the sensor's
          // trackHistory, so handing its id over is what marks the row the panel
          // is about to render as the one on the map.
          this.host.voyageTrack.loadTrack(points, 'full', res?.track?.trackId ?? null);
        },
        (err: any) => {
          this.host.voyageTrack.setTrackLoading(false);
          // OPERATOR_CREDITS_EXHAUSTED (platform-side) is checked before the
          // wallet branch inside the mapper — it must never read as "your
          // credits are spent". A wallet 402 carries the server's balance, so
          // re-sync before the popup: the local number is wrong by definition.
          syncWalletFromError(err, this.sensor);
          this.syncCreditLines();
          const message = voyageSpendErrorMessage(err, 'track', this.trackCredits);
          this.host.popupService.openPopup(CollectionPopupComponent, message, '', '40%');
        }
      );
  }

  /**
   * FREE replay of a track this task already paid for: the points are recalled
   * from S3 by id, so no operator call and no credit is involved.
   *
   * Reachable from two places that differ in what the user has selected: the map
   * toolbar's trace drawer, where the vessel was clicked a moment ago, and the
   * Vessel Tracks panel, where it may not be on the map at all. The selection is
   * therefore SYNTHESISED from the record when it is missing — see
   * `_adoptTrackVessel`. Without it an off-map replay would draw a track with no
   * halo, no toolbar title and the default blue instead of the vessel's colour.
   */
  async replaySavedTrack(trackId: string): Promise<void> {
    const sensor = this.sensor;
    if (!sensor || !trackId) return;
    const record = this.findTrackRecord(trackId);
    this.host.voyageTrack.setTrackLoading(true, this.pendingRefFor(record, trackId));
    // The map answers on the click, not on the response: the hull gets its ring
    // and the view slides onto it while the two fetches run. Without this the
    // map is inert for the whole wait and the path then teleports into place.
    const anchored = this.anchorPendingTrack(record);
    try {
      const res: any = await this.host.apiService
        .getVoyageRadarTrack({
          taskid: this.host.collection?.taskId,
          sensorId: sensor.id,
          trackId,
        })
        .toPromise();
      const url = res?.result?.imageUrl;
      if (!url) throw new Error('missing track url');
      const json: any = await this.host.httpClient.get(url).toPromise();
      const records = Array.isArray(json) ? json : json?.records ?? json?.features ?? [];
      const points = this.toTrackPoints(records);
      this.host.voyageTrack.setTrackLoading(false);
      if (points.length < 2) throw new Error('empty track');
      // Before loadTrack, never after: `setActiveVessel` tears the current track
      // down, so adopting afterwards would clear the one just installed.
      this._adoptTrackVessel(res?.result?.track ?? record, points);
      this.host.voyageTrack.loadTrack(points, 'saved', trackId);
    } catch {
      this.host.voyageTrack.setTrackLoading(false);
      // Put the ring back where it was. The anchor moved it onto a hull on the
      // strength of a track that never arrived, and a selection ring pointing at
      // a vessel nobody selected outlives the dialog below by the rest of the
      // session.
      if (anchored) this.applyActiveVisual(this.active);
      this.host.popupService.openPopup(
        CollectionPopupComponent,
        'That saved track could not be loaded. Please try again.',
        '',
        '40%'
      );
    }
  }

  /**
   * What the pending ON MAP row says while a saved track is being recalled.
   *
   * Built from the record the user clicked, so the wait names the same hull,
   * span and point count as the row that started it. The colour is resolved the
   * way the panel resolves it — live feed first, deterministic fallback second —
   * so the pending rail is already the colour the path will be drawn in.
   */
  private pendingRefFor(record: any, trackId: string): PendingTrackRef {
    const mmsi = String(record?.mmsi ?? '');
    const live = mmsi ? this.liveVessels.get(mmsi) ?? null : null;
    const identity = resolveVesselIdentity(record, live?.name);
    const described = describeVoyageTrack(record, Date.now());
    const rawColor = String(live?.color ?? '');
    return {
      trackId,
      source: 'saved',
      mmsi: mmsi || null,
      label: identity.label,
      isNamed: identity.isNamed,
      color: /^#[0-9a-fA-F]{3,8}$/.test(rawColor)
        ? rawColor
        : mmsi
        ? fallbackVesselColor(mmsi)
        : null,
      points: described.points,
      spanLabel: described.spanLabel || null,
      window: described.window || null,
    };
  }

  /**
   * Gives the map something to say while the track is still in the air.
   *
   * Only for a hull that is actually on the map: it takes the selection ring
   * immediately and the view slides onto it, so the click has a visible
   * consequence within a frame rather than after two round trips. A hull that
   * has drifted out of the snapshot is left alone — there is no honest place to
   * point at, and the stored record carries no bounding box to guess from.
   *
   * A pan, never a zoom. `buildTrackLayers` will fit the real extent when the
   * points land, and pre-zooming to a guess would only make that a second jump.
   *
   * Returns whether the ring was moved, so a load that fails can put it back.
   */
  private anchorPendingTrack(record: any): boolean {
    const mmsi = String(record?.mmsi ?? '');
    if (!mmsi || !this.host.map) return false;
    const marker = this.liveMarkerFor(mmsi);
    const latlng = marker?.getLatLng?.();
    if (!latlng) return false;

    const live = this.liveVessels.get(mmsi) ?? null;
    const rawColor = String(live?.color ?? '');
    this.applyActiveVisual({
      mmsi,
      name: resolveVesselIdentity(record, live?.name).label,
      color: /^#[0-9a-fA-F]{3,8}$/.test(rawColor) ? rawColor : fallbackVesselColor(mmsi),
      lat: latlng.lat,
      lng: latlng.lng,
      timestampIso: '',
      shipType: 'OTHER',
    });
    // Left where it is when the hull is already comfortably in view: moving a
    // map the user has just arranged, to a point already on it, reads as the
    // map losing their place rather than as progress.
    if (!this.host.map.getBounds().pad(-0.15).contains(latlng)) {
      this.host.map.panTo(latlng, { animate: !this.prefersReducedMotion(), duration: 0.4 } as any);
    }
    return true;
  }

  /** The stored record for a trackId, or null. */
  findTrackRecord(trackId: string): any | null {
    const history: any[] = Array.isArray(this.sensor?.trackHistory)
      ? this.sensor.trackHistory
      : [];
    return history.find((t) => t?.trackId === trackId) ?? null;
  }

  /**
   * Makes the track's vessel the selected one, building a `VoyageActiveVessel`
   * out of the stored record when it is not on the map.
   *
   * A track can outlive the snapshot it was bought from — the vessel may have
   * left the AOI, or the map may be empty — and before the Vessel Tracks panel
   * existed that track was simply unreachable. Everything downstream (the halo,
   * the trail colour, the ghost's icon, the toolbar title) reads `this.active`,
   * so a synthetic selection is what makes an off-map replay look like any other.
   *
   * No-op when the right vessel is already selected: `setActiveVessel` clears
   * the loaded track, so calling it needlessly would undo a replay in progress.
   */
  private _adoptTrackVessel(record: any, points: VoyageTrackPoint[]): void {
    const mmsi = String(record?.mmsi ?? '');
    if (!mmsi || this.active?.mmsi === mmsi) return;

    const live = this.liveVessels.get(mmsi) ?? null;
    const identity = resolveVesselIdentity(record, live?.name);
    const rawColor = String(live?.color ?? '');
    const start = points[0];

    this.deactivateVessel();
    this.active = {
      mmsi,
      // The panel and the map must agree on what this vessel is called, so the
      // label comes from the shared resolver rather than a second `?? mmsi`.
      name: identity.label,
      color: /^#[0-9a-fA-F]{3,8}$/.test(rawColor) ? rawColor : fallbackVesselColor(mmsi),
      lat: start.lat,
      lng: start.lng,
      timestampIso: start.timeIso,
      shipType: identity.shipType ?? 'OTHER',
    };
    this.syncCreditLines();
    this.host.voyageTrack.setSavedTracks(this.savedTracksFor(mmsi));
    this.host.voyageTrack.setActiveVessel(this.active);
  }

  /**
   * Syncs the local credit wallet after a successful (non-empty) paid call.
   * Prefers the server's authoritative balance; falls back to `current - cost`
   * only when the response omits it.
   */
  applyCreditSpend(res: any, sensorId: string | undefined, cost: number): void {
    // `!= null` before the finite check: Number(null) is 0, so an omitted or
    // null balance would otherwise read as "no credits left" — the same defect
    // that once reported a full refresh balance as exhausted.
    const reported = res?.apiCreditsRemaining;
    const authoritative = Number(reported);
    // Only the sensor that was charged — the server decrements exactly one.
    const sensor = (this.host.collection?.sensors ?? []).find(
      (s: any) =>
        s?.operatorKey === OPERATORS.VOYAGERADAR && (!sensorId || s?.id === sensorId)
    );
    if (!sensor) return;
    if (reported != null && isFinite(authoritative)) {
      sensor.apiCreditsRemaining = Math.max(0, authoritative);
    } else {
      const current = voyageCreditsRemaining(sensor);
      // NaN-safe: with no usable number there is nothing honest to write.
      const base = current != null ? current : Number(sensor.apiCredits);
      if (isFinite(base)) sensor.apiCreditsRemaining = Math.max(0, base - cost);
    }
    if (res?.track) {
      if (!Array.isArray(sensor.trackHistory)) sensor.trackHistory = [];
      sensor.trackHistory.push(res.track);
    }
    this.syncCreditLines();
    if (this.active) {
      this.host.voyageTrack.setSavedTracks(this.savedTracksFor(this.active.mmsi));
    }
  }

  /**
   * Normalizes track records into transport points. Accepts a raw
   * `/v1/vessel/path` TrackPoint, a saved-track record, or a GeoJSON Feature —
   * the same shapes `toVoyageVesselFeature` handles. Read directly rather than
   * through that mapper so a 90-day track doesn't allocate a Feature per point.
   */
  toTrackPoints(records: any[]): VoyageTrackPoint[] {
    const points: VoyageTrackPoint[] = [];
    for (const r of records || []) {
      const p = r?.properties || r || {};
      const coords = r?.geometry?.coordinates;
      const lat = Number(coords ? coords[1] : p.latitude ?? p.Lat);
      const lng = Number(coords ? coords[0] : p.longitude ?? p.Lng);
      const timeIso = p.timestamp ?? p.time_utc ?? p['Timestamp UTC'];
      if (!isFinite(lat) || !isFinite(lng) || !timeIso) continue;
      points.push({
        lat,
        lng,
        course: Number(p.cog ?? p.Course ?? p.heading ?? 0) || 0,
        timeIso,
        sog: Number(p.sog ?? p.Speed),
      });
    }
    return points;
  }

  // ── Track mode: rendering ───────────────────────────────────────────────────
  /**
   * Builds the three track layers once per load. Nothing here runs per frame:
   * the "ahead" polyline carries the FULL geometry and is never touched again,
   * and fitBounds happens exactly once.
   */
  buildTrackLayers(full: VoyageTrackPoint[], steps: VoyageTrackPoint[]): void {
    // A track and the AOI flip-book are mutually exclusive: leaving snapshot
    // playback here also brings back the live layer and the live/track toolbars.
    if (this.host.voyageRadarPlayback.mode === 'playback') {
      this.host.voyageRadarPlayback.resumeLive();
    }
    // `false`: swapping one track for another must not bounce the map back to
    // the pre-track view and then fit the new track. The restore belongs to
    // closing, not to reloading.
    this.clearTrackLayers(false);
    const color = this.active?.color ?? VOYAGE_DEFAULT_TRACK_COLOR;
    // Reuse one renderer across loads; recreate only if resetMapNew swept it off.
    if (!this._trackRenderer || !this.host.map?.hasLayer(this._trackRenderer)) {
      this._trackRenderer = L.canvas({ padding: 0.5 });
    }
    const renderer = this._trackRenderer;
    this._trackLatLngs = steps.map((p) => L.latLng(p.lat, p.lng));
    // Where the ink lifts, for the travelled trail below. Computed on `steps`
    // because that is the list `frame.index` indexes into.
    this._stepGaps = steps.map((p, i) =>
      i + 1 < steps.length ? isTrackGap(p, steps[i + 1]) : false
    );

    // NESTED arrays, not one flat list: Leaflet reads an array of arrays as a
    // multi-polyline and draws no join between the parts. That single change is
    // what stops a vessel that went dark for four days off Kochi being drawn
    // sailing overland to Chennai. Split on `full` independently of `steps` —
    // decimation changes the intervals, so the two lists can honestly disagree
    // about where a gap falls.
    this._trailBase = L.polyline(
      splitTrackAtGaps(full).map((seg) => seg.map((p) => [p.lat, p.lng] as [number, number])),
      {
        color,
        // Thicker and slightly less faint than it was (3 / 0.3). At three
        // pixels of a 30%-opacity hairline the untravelled path was competing
        // with basemap furniture and losing — and a dashed line loses more than
        // a solid one, because each dash is small enough to read as noise. The
        // opacity goes up with the width on purpose: widening alone turns a
        // faint line muddy rather than making it legible.
        //
        // Still well under `_trailDone`'s 4.5 / 0.95. That gap is the whole
        // grammar of the trail — bright and solid behind the vessel, faint and
        // dashed ahead of it — and thickening this must not flatten it.
        weight: 4,
        opacity: 0.4,
        dashArray: '6 8',
        interactive: false,
        smoothFactor: 1.5,
        renderer,
      } as any
    ).addTo(this.host.map);

    // The unobserved legs, drawn as what they are: a LINK between two sightings,
    // not a route between them. Neutral grey rather than the vessel's colour so
    // it never reads as part of the path, thin, sparsely dotted and well under
    // the trail's weight — present enough that two clusters are visibly one
    // vessel, faint enough that nobody mistakes it for a course made good.
    // Built from the same predicate as the split, so the two cannot disagree.
    const links = trackGapLinks(full);
    this._trailLink = links.length
      ? L.polyline(
          links.map(([a, b]) => [
            [a.lat, a.lng] as [number, number],
            [b.lat, b.lng] as [number, number],
          ]),
          {
            color: '#9AA0A6',
            weight: 1.5,
            opacity: 0.55,
            dashArray: '2 7',
            interactive: false,
            renderer,
          } as any
        ).addTo(this.host.map)
      : null;

    this._trailIndex = -1;
    this._trailDoneSegments = [[this._trackLatLngs[0]]];
    this._trailDone = L.polyline(this._trailDoneSegments, {
      color,
      weight: 4.5,
      opacity: 0.95,
      lineCap: 'round',
      lineJoin: 'round',
      interactive: false,
      smoothFactor: 1.5,
      renderer,
    } as any).addTo(this.host.map);

    this.buildTrackPointLayers(steps, full, color, renderer);

    this._ghostMarker = L.marker(this._trackLatLngs[0], {
      icon: this.host.createArrowIcon(
        steps[0]?.course ?? 0,
        this.active?.shipType ?? 'OTHER',
        this.sensor,
        color
      ),
      interactive: false,
      zIndexOffset: 200000,
    } as any).addTo(this.host.map);
    this._ghostEl =
      (this._ghostMarker.getElement()?.querySelector('.vessel-icon-img') as HTMLElement) ??
      null;
    this.toggleGhostLabel(this.host.voyageTrack.snapshot().showLabels === true);

    // The static live marker is faded so the moving ghost reads as "the" vessel,
    // and — unless the user has turned the dim off — so is everything else, so
    // the path is not drawn under a few hundred other hulls. Both are decided in
    // one pass; see `applyTypeFilterToLive`.
    this._tracedMmsi = this.active?.mmsi ?? null;
    // The track owns the ring from here on — `renderTrackFrame` rides it along
    // the ghost — so it has to be on the map even when the display pass had
    // taken it off: the selected hull may have been filtered out, absorbed into
    // a bubble, or off the map entirely (a saved track replayed from the panel)
    // at the moment Trace was pressed. See `syncActiveHalo`.
    if (this.active) this.applyActiveVisual(this.active);
    this.setTrackFocus(this.host.voyageTrack.dimOthers ? this._tracedMmsi : null);

    // Captured before the fit, and only when nothing is stored yet: swapping
    // tracks keeps the ORIGINAL pre-track view as the thing to go back to.
    if (!this._preTrackView && this.host.map) {
      this._preTrackView = {
        center: this.host.map.getCenter(),
        zoom: this.host.map.getZoom(),
      };
    }

    const bounds = this._trailBase.getBounds();
    if (bounds?.isValid()) {
      this.fitTrackBounds(bounds);
      // AFTER the fit, not before. The dots are decided on how far apart the
      // reports land in PIXELS, and until the fit has run the map is still on
      // whatever view the track was requested from — so the first answer was
      // being computed against a viewport nobody was going to look at. A fit
      // that only pans fires no `zoomend` either, so the listener alone could
      // not be relied on to correct it.
      this._syncTrackDots();
      // Drawn on rather than dropped in — see `revealTrail`. After the fit, so
      // the sweep runs across the frame the track will actually be read in.
      this.revealTrail(full);
    }
  }

  /**
   * Frames a freshly loaded track.
   *
   * Two ways in, because "fit these bounds" is one instruction covering two very
   * different moves. When the path is roughly where the map already is — the
   * usual case, since the hull was just anchored under the pointer — the view
   * GLIDES, and the eye keeps hold of where it was. When the track is somewhere
   * else entirely (a hull that left the AOI days ago, replayed from the panel), a
   * six-hundred-millisecond flight across an ocean is not continuity, it is a
   * long wait for a cut — so that one is instant.
   *
   * Either way `_trackFitView` records the FINAL view, which is what `close`
   * compares against to decide whether the pre-track view is still worth
   * restoring. Reading a centre mid-flight would never match, and would silently
   * disable that restore for the whole life of the track — so the animated path
   * records on `moveend` instead, tokened against a second track loading over
   * the first while the flight is still running.
   */
  private fitTrackBounds(bounds: any): void {
    const map = this.host.map;
    if (!map) return;
    const padding = L.point(60, 60);
    const record = () => {
      this._trackFitView = { center: map.getCenter(), zoom: map.getZoom() };
    };

    let near = false;
    if (!this.prefersReducedMotion()) {
      try {
        const targetZoom = map.getBoundsZoom(bounds, false, padding);
        near =
          Math.abs(targetZoom - map.getZoom()) <= 3 &&
          map.getBounds().pad(0.75).contains(bounds.getCenter());
      } catch {
        near = false; // an unfittable bounds is not a near one
      }
    }

    if (!near) {
      map.fitBounds(bounds, { padding: [60, 60], animate: false });
      record();
      return;
    }

    const token = ++this._fitToken;
    map.once('moveend', () => {
      if (token !== this._fitToken) return; // a later track owns the view now
      record();
    });
    // Recorded up front as well: a fly that has nowhere to go — the map is
    // already framing this track — emits no `moveend` at all, and without this
    // the fit view would stay unset and quietly disable the close-time restore.
    // Where the flight does happen, `moveend` overwrites this with the truth.
    record();
    map.flyToBounds(bounds, { padding: [60, 60], duration: 0.6 } as any);
  }

  /**
   * Draws the path on from its origin instead of switching it on whole.
   *
   * The arrival is the moment the whole wait was for, and a polyline that simply
   * exists on the next frame throws it away — worse, it reads as a glitch, since
   * nothing on screen connects the click to the shape. Unspooled from the first
   * position along its own course, the same shape ALSO says which way the vessel
   * was going and how far it ran, for free.
   *
   * Geometry rather than CSS: these layers are drawn by a canvas renderer, where
   * there is no stroke-dashoffset to animate and no per-layer element to fade.
   * So the "ahead" trail is grown a slice at a time — cheap, because it is the
   * one layer nothing else touches after it is built (`renderTrackFrame` works
   * on the travelled trail), and because the whole thing lasts 420 ms.
   *
   * Skipped outright for a very dense path: a 90-day track is tens of thousands
   * of vertices, and re-drawing that 25 times would cost more than the reveal is
   * worth. Skipped too when the user has asked for less motion.
   */
  private revealTrail(full: VoyageTrackPoint[]): void {
    this.cancelTrailReveal();
    const layer = this._trailBase;
    if (!layer || full.length < 3) return;
    if (full.length > VoyageMapController.REVEAL_MAX_POINTS || this.prefersReducedMotion()) return;

    // SEGMENTS, not one flat list, and this is a correction rather than a
    // refinement: the sweep used to slice a flat array and hand it back, which
    // silently undid the gap split `buildTrackLayers` had just applied — and
    // because the last frame writes the WHOLE list, the trail was left joined
    // across every gap once the animation settled. The bug was invisible on
    // tracks over REVEAL_MAX_POINTS and with reduced motion on, which is to say
    // invisible exactly where nobody was looking.
    const segments = splitTrackAtGaps(full).map((seg) =>
      seg.map((p) => [p.lat, p.lng] as [number, number])
    );
    const total = full.length;

    /** The first `count` points of the track, still broken at its gaps. */
    const upTo = (count: number): [number, number][][] => {
      const out: [number, number][][] = [];
      let left = count;
      for (const seg of segments) {
        if (left <= 0) break;
        out.push(left >= seg.length ? seg : seg.slice(0, left));
        left -= seg.length;
      }
      return out;
    };

    const started = Date.now();
    const step = () => {
      this._revealFrame = null;
      // Torn down mid-sweep — a second track, or the first being closed.
      if (this._trailBase !== layer) return;
      const t = Math.min(1, (Date.now() - started) / VoyageMapController.REVEAL_MS);
      // Ease out: the head leaves fast and settles, which reads as travel
      // rather than as a progress bar filling.
      const eased = 1 - Math.pow(1 - t, 3);
      layer.setLatLngs(upTo(Math.max(2, Math.round(eased * total))));
      if (t >= 1) return;
      this._revealFrame = requestAnimationFrame(step);
    };
    layer.setLatLngs(upTo(2));
    this._revealFrame = requestAnimationFrame(step);
  }

  /**
   * Stops a sweep in progress.
   *
   * Called from the teardown path, where the layer being drawn on is about to
   * leave the map: a frame still queued would set geometry on a polyline that
   * no longer belongs to anything. The partial path it leaves behind is never
   * seen — `buildTrackLayers` builds a fresh polyline for the next track.
   */
  private cancelTrailReveal(): void {
    if (this._revealFrame == null) return;
    cancelAnimationFrame(this._revealFrame);
    this._revealFrame = null;
  }

  /** Honours the OS "reduce motion" setting; false where it cannot be read. */
  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;
  }

  /**
   * Whether the map is still sitting exactly where this track's fit put it.
   *
   * If it is not, something else has claimed the view since — a snapshot fitting
   * its own markers, or the user panning to look at the path — and the pre-track
   * view is no longer a view anybody asked to go back to. Restoring it anyway is
   * how closing a track could land the map on the country-level default with a
   * snapshot still drawn: the track had been built before anything framed the
   * AOI, so the view it saved was that default.
   *
   * An exact comparison on purpose. After a fit with no interaction Leaflet hands
   * back the same centre it stored, so the margin only has to absorb float noise
   * — and a nudge of a single pixel still means the user took the map over.
   */
  private _trackOwnsView(): boolean {
    const map = this.host.map;
    const fit = this._trackFitView;
    if (!map || !fit) return false;
    return map.getZoom() === fit.zoom && map.getCenter().equals(fit.center, 1e-6);
  }

  /**
   * Minimum screen distance between two beads. Reports closer than this at the
   * current zoom get no mark of their own.
   *
   * Nine, because a bead is 2.5 px of radius plus a 1.2 px ring — about 7 px
   * across — so anything under that has them overlapping rather than beading.
   *
   * This is also the knob that decides how much a zoom level REVEALS: halve it
   * and beads crowd, double it and each zoom buys fewer new ones.
   */
  private static readonly TRACK_DOT_MIN_PX = 9;

  /**
   * Are the reports far enough apart ON SCREEN to be worth drawing?
   *
   * Which reports get a bead AT THIS ZOOM — every one far enough from the last
   * bead drawn to stand as its own mark.
   *
   * THIS REPLACED A WHOLE-TRACK TEST, and the replacement is the point. The old
   * rule divided the track's bounds diagonal by its point count and drew all or
   * nothing on the answer. That is an AVERAGE, and no real track is uniform: a
   * hull sits in a roadstead emitting hundreds of reports inside a hundred
   * metres, then transits three hundred miles. The average clears the bar on the
   * strength of the transit, every bead draws, and the roadstead becomes the
   * solid caterpillar the beads existed to avoid being.
   *
   * Deciding per bead instead has a second effect worth having on its own:
   * zooming in genuinely REVEALS more of them. Where reports crowd, each zoom
   * level separates a few more and they appear; where they are sparse, nothing
   * new arrives because there is nothing more to show. The density signal is
   * still there — it is just carried by how many new beads a zoom buys you
   * rather than by how tightly they are packed.
   *
   * Greedy and single-pass: keep the first, then keep any report at least
   * `TRACK_DOT_MIN_PX` from the last one KEPT — not from the last one seen, or a
   * long crawl of sub-threshold steps would never accumulate into a gap.
   *
   * Returns EMPTY when the gate below says this view has no business showing
   * beads at all, which is the clean-line state.
   */
  private _visibleDotIndices(): number[] {
    const pts = this._trackProjected;
    if (pts.length < 2) return pts.length ? [0] : [];
    if (!this._reportsAreSeparable()) return [];
    const min = VoyageMapController.TRACK_DOT_MIN_PX ** 2;
    const keep = [0];
    let anchor = pts[0];
    for (let i = 1; i < pts.length; i++) {
      const dx = pts[i].x - anchor.x;
      const dy = pts[i].y - anchor.y;
      if (dx * dx + dy * dy >= min) {
        keep.push(i);
        anchor = pts[i];
      }
    }
    return keep;
  }

  /**
   * Are the reports IN VIEW typically far enough apart to be shown one by one?
   *
   * The gate that restores the clean line. Culling alone guarantees beads never
   * overlap, but it cannot say whether they should be there: cull a thousand
   * crowded reports down to eighty and you get eighty tidy beads that look like
   * the whole answer and are a 4% sample of it. At that point the honest picture
   * is the path, not a handful of marks standing in for it.
   *
   * MEDIAN SPACING, and both words are doing work.
   *
   *  - MEDIAN, not mean: one 300-mile transit leg among a thousand harbour
   *    reports drags a mean over any threshold, which is precisely how the first
   *    version of this gate failed. The median describes the typical pair and
   *    shrugs at outliers.
   *  - IN VIEW, not whole-track: a global measure cannot be right for a track
   *    that is dense in one place and sparse in another. Zoomed into open water
   *    the reports there ARE separable and should bead, whatever a crowded port
   *    two hundred miles away would have said about the average.
   *
   * Costs one pass plus a sort of at most two thousand numbers, on zoom and pan
   * only — never per frame.
   */
  private _reportsAreSeparable(): boolean {
    const map = this.host.map;
    const pts = this._trackProjected;
    if (!map) return false;
    // Padded, so a bead just outside the frame still counts toward how dense
    // this neighbourhood is — otherwise the answer flickers as you pan.
    const view = map.getBounds()?.pad?.(0.25);
    if (!view) return false;

    const gaps: number[] = [];
    let prev = -1;
    for (let i = 0; i < pts.length; i++) {
      if (!view.contains(this._reportLatLngs[i])) continue;
      if (prev >= 0) {
        gaps.push(Math.hypot(pts[i].x - pts[prev].x, pts[i].y - pts[prev].y));
      }
      prev = i;
    }
    // Fewer than two reports in view: nothing to be crowded by, so show them.
    if (gaps.length < 2) return true;
    gaps.sort((a, b) => a - b);
    const median = gaps[Math.floor(gaps.length / 2)];
    return median >= VoyageMapController.TRACK_DOT_MIN_PX;
  }

  /**
   * Rebuild the beads for the current zoom.
   *
   * Rebuilt rather than toggled, because which reports deserve a bead is a
   * function of zoom and changes at every level. The cost is bounded by the
   * VIEWPORT and not by the track: two beads cannot be closer than
   * `TRACK_DOT_MIN_PX`, so however many thousand reports a track holds, only a
   * few hundred marks can exist across a screen.
   */
  private _syncTrackDots(): void {
    const map = this.host.map;
    if (!map || !this._reportLatLngs.length) return;
    if (!this._trackProjected.length) this._rebuildTrackProjection();

    if (this._trackDots && map.hasLayer(this._trackDots)) map.removeLayer(this._trackDots);

    this._trackDotIndices = this._visibleDotIndices();
    const color = this.active?.color ?? VOYAGE_DEFAULT_TRACK_COLOR;
    this._trackDots = L.layerGroup(
      this._trackDotIndices.map((i) =>
        L.circleMarker(this._reportLatLngs[i], {
          radius: 2.5,
          color,
          weight: 1.2,
          opacity: 0.9,
          fillColor: '#FFFFFF',
          fillOpacity: 1,
          interactive: false,
          renderer: this._trackRenderer,
        } as any)
      )
    ).addTo(map);

    // The readout names a bead. Rebuilding the set can drop the one it was
    // pointing at, and a label left over a mark that no longer exists is worse
    // than no label.
    this._hideTrackHover();
  }

  /** How near the pointer must come to a report before it is being asked about. */
  private static readonly TRACK_HOVER_PX = 12;

  /** Re-project the reports for hit-testing. Cheap; runs once per zoom. */
  private _rebuildTrackProjection(): void {
    const map = this.host.map;
    this._trackProjected =
      map && this._reportLatLngs.length
        ? this._reportLatLngs.map((ll) => map.latLngToLayerPoint(ll))
        : [];
  }

  /**
   * The report nearest the pointer, or -1 when none is close enough.
   *
   * A plain linear scan over at most two thousand cached points — a few
   * thousand subtractions once per animation frame, which is less work than the
   * spatial index that would replace it and has no invalidation rules of its
   * own. Squared distances, so the loop never takes a square root.
   */
  private _trackHoverPick(layerPoint: any): number {
    const pts = this._trackProjected;
    const limit = VoyageMapController.TRACK_HOVER_PX ** 2;
    let best = -1;
    let bestDist = Infinity;
    // Over the DRAWN beads only, not every report. The readout is a label for
    // the mark under the pointer, so it has to name that mark — searching all
    // reports would let it quote one culled out of view, which reads as the
    // bead you are pointing at lying about itself.
    for (const i of this._trackDotIndices) {
      const p = pts[i];
      if (!p) continue;
      const dx = p.x - layerPoint.x;
      const dy = p.y - layerPoint.y;
      const d = dx * dx + dy * dy;
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return bestDist <= limit ? best : -1;
  }

  /**
   * What one report says. Two lines: when, then how it was moving.
   *
   * The AIS sentinels are dropped here for the same reason the nearby drawer
   * drops them — 102.3 kn and 360° are how AIS says "not telling you", and
   * printing them is worse than printing nothing. A vessel that reported
   * neither shows the timestamp alone, which is still the answer to "what is
   * this bead".
   */
  private _trackHoverHtml(p: VoyageTrackPoint): string {
    const facts: string[] = [];
    const sog = Number(p.sog);
    if (Number.isFinite(sog) && sog !== 102.3) facts.push(`${sog} kn`);
    if (Number.isFinite(p.course) && p.course !== 360) facts.push(`${Math.round(p.course)}°`);
    const when = formatUtcShort(p.timeIso) || formatUtcStamp(p.timeIso);
    return facts.length ? `${when}<br>${facts.join(' · ')}` : when;
  }

  /**
   * Take every hover/zoom hook off the map and drop what they held.
   *
   * ONE method called from both teardown paths, rather than the same four
   * `off()` calls written twice. The listener leak this replaced was exactly
   * that: `clearTrackLayers` detached and `reset` only nulled the field, which
   * is the failure mode two copies of a teardown always eventually reach.
   */
  private _detachTrackHover(): void {
    const map = this.host.map;
    if (this._trackViewHandler) {
      map?.off('moveend', this._trackViewHandler);
      this._trackViewHandler = null;
    }
    if (this._trackHoverHandler) {
      map?.off('mousemove', this._trackHoverHandler);
      this._trackHoverHandler = null;
    }
    if (this._trackLeaveHandler) {
      map?.off('mouseout', this._trackLeaveHandler);
      this._trackLeaveHandler = null;
    }
    // A frame already queued would run against the track being torn down.
    if (this._trackHoverFrame != null) {
      cancelAnimationFrame(this._trackHoverFrame);
      this._trackHoverFrame = null;
    }
    if (this._trackHoverTip && map?.hasLayer(this._trackHoverTip)) {
      map.removeLayer(this._trackHoverTip);
    }
    this._trackHoverTip = null;
    this._trackHoverIndex = -1;
    this._trackProjected = [];
    this._trackDotIndices = [];
    this._trackReports = [];
    this._reportLatLngs = [];
  }

  private _hideTrackHover(): void {
    if (this._trackHoverIndex === -1) return;
    this._trackHoverIndex = -1;
    if (this._trackHoverTip && this.host.map?.hasLayer(this._trackHoverTip)) {
      this.host.map.removeLayer(this._trackHoverTip);
    }
  }

  /**
   * Pointer moved over the map while a track is drawn.
   *
   * ONE listener on the map, not two thousand interactive layers. Every bead
   * could have carried its own tooltip, and that is the obvious build — but it
   * would put all of them into Leaflet's hit test, which walks layers on every
   * mouse move. This walks a flat array of cached points instead, once per
   * frame, and moves a single tooltip.
   *
   * Gated on the beads being visible. Zoomed out past them, fifty reports fall
   * inside the same twelve pixels, and snapping a readout to one of them would
   * be a claim of precision the view cannot support.
   */
  private _onTrackHover(e: any): void {
    if (this._trackHoverFrame != null) return;
    this._trackHoverFrame = requestAnimationFrame(() => {
      this._trackHoverFrame = null;
      const map = this.host.map;
      if (!map || !this._trackDotIndices.length) {
        this._hideTrackHover();
        return;
      }
      if (!this._trackProjected.length) this._rebuildTrackProjection();
      const i = this._trackHoverPick(e.layerPoint);
      if (i < 0) {
        this._hideTrackHover();
        return;
      }
      if (i === this._trackHoverIndex) return;
      const p = this._trackReports[i];
      if (!p) return;
      this._trackHoverIndex = i;
      if (!this._trackHoverTip) {
        this._trackHoverTip = L.tooltip({
          direction: 'top',
          offset: L.point(0, -8),
          opacity: 0.95,
          className: 'vr-track-tip',
        } as any);
      }
      this._trackHoverTip.setLatLng(this._reportLatLngs[i]).setContent(this._trackHoverHtml(p));
      if (!map.hasLayer(this._trackHoverTip)) this._trackHoverTip.addTo(map);
    });
  }

  /**
   * The reports, the two ends of the voyage, and the two ends of every gap.
   *
   * Everything here is a `circleMarker` on the shared canvas renderer rather
   * than an `L.marker`: two thousand DOM nodes would cost more than the whole
   * rest of the track put together, and a canvas circle is one `arc()` in a
   * layer that is already being repainted.
   *
   * THREE LAYERS, THREE LIFETIMES. The dots come and go with zoom, so they own
   * a group of their own. The marks are always drawn and are the only things
   * here that are interactive — four to ten layers with tooltips, against two
   * thousand without, which is the difference between a hover test that is free
   * and one that walks the whole track.
   */
  private buildTrackPointLayers(
    steps: VoyageTrackPoint[],
    full: VoyageTrackPoint[],
    color: string,
    renderer: any,
  ): void {
    if (!this.host.map || !steps.length) return;

    // The beads themselves are NOT built here — `_syncTrackDots` builds them per
    // zoom, because which reports deserve one depends on how far apart they land
    // on screen. See the note there.
    //
    // Their look is decided there too, and the colour choice is load-bearing: a
    // WHITE core with a thin coloured ring. They were first drawn as small dots
    // in the vessel's own colour, which made them invisible — they sit ON a
    // 4.5 px solid line of exactly that colour, so the pixels were painted and
    // were identical to what was already there. Reading a mark on top of a line
    // means differing from the line, and the only free axis is lightness: hue
    // belongs to the vessel, width belongs to the trail.

    const marks: any[] = [];

    // Gap ends first, so the voyage's own start and end draw over them if a
    // dropout happens to sit on top of one.
    for (const [a, b] of trackGapLinks(full)) {
      const label = trackGapLabel(a, b);
      const lost = L.circleMarker([a.lat, a.lng], {
        radius: 3.5,
        color: '#9AA0A6',
        weight: 2,
        fill: false,
        renderer,
      } as any);
      const back = L.circleMarker([b.lat, b.lng], {
        radius: 3.5,
        color: '#9AA0A6',
        weight: 2,
        fill: false,
        renderer,
      } as any);
      // Both ends carry the SAME sentence. Whichever one the pointer finds, the
      // question being asked is the same: what is this grey thread.
      //
      // `<br>`, not `\n`. A Leaflet tooltip sets innerHTML, where a newline is
      // just whitespace — every two-line tooltip here was rendering as one long
      // line until this was caught.
      const text = label ? `No contact for ${label}` : 'No contact — gap in coverage';
      lost.bindTooltip(`${text}<br>Last seen ${formatUtcStamp(a.timeIso)}`, { direction: 'top' });
      back.bindTooltip(`${text}<br>Back in range ${formatUtcStamp(b.timeIso)}`, { direction: 'top' });
      marks.push(lost, back);
    }

    // START — hollow. END — solid. Told apart by FILL rather than by colour or
    // size, because colour here is the vessel's own identity and cannot also
    // carry a second meaning, and two rings of different radius is a difference
    // nobody reads at a glance.
    //
    // BOTH ringed in white, for the reason the dots are: these sit on the trail,
    // and the trail is the vessel's colour. The start was originally a ring in
    // that same colour, which on the line it marks is a thicker piece of line.
    // From `full`, like every other mark here. Decimation always keeps the
    // first and last point, so these are the same two either way — but reading
    // the reports rather than the transport's sample of them means one list
    // answers "where were the reports" and there is no second one to drift.
    const first = full[0];
    const last = full[full.length - 1];
    marks.push(
      L.circleMarker([first.lat, first.lng], {
        radius: 6,
        color: '#FFFFFF',
        weight: 2.5,
        fill: false,
        renderer,
      } as any).bindTooltip(`Track start<br>${formatUtcStamp(first.timeIso)}`, { direction: 'top' })
    );
    marks.push(
      L.circleMarker([last.lat, last.lng], {
        radius: 6,
        color: '#FFFFFF',
        weight: 2,
        fillColor: color,
        fillOpacity: 1,
        renderer,
      } as any).bindTooltip(`Last report<br>${formatUtcStamp(last.timeIso)}`, { direction: 'top' })
    );

    this._trackMarks = L.layerGroup(marks).addTo(this.host.map);

    // The reports themselves, for the beads to mark and the hover readout to
    // quote. FULL, not `steps`: see the field note — beads built from the
    // decimated list left every dropped vertex as a bend in the line with
    // nothing to explain it.
    this._trackReports = full;
    this._reportLatLngs = full.map((p) => L.latLng(p.lat, p.lng));

    // Only the listener here — NOT a first build of the beads. Which reports
    // deserve one depends on how far apart they land ON SCREEN, and until the
    // caller has fitted the track the map is still on whatever view the trace
    // was requested from. The first real build runs in `buildTrackLayers` after
    // that fit.
    //
    // `moveend`, NOT `zoomend`, and it is the gate that decides this. Zoom
    // invalidates the projected positions; the separability gate reads what is
    // IN VIEW, so a pan changes its answer without the zoom moving at all.
    // Leaflet fires `moveend` after a pan and after a zoom, so one listener
    // covers both and the two can never disagree about which view they describe.
    // Re-projecting on a pan is redundant — layer points are pan-stable — but it
    // is two thousand multiplications against a whole map repaint, and paying it
    // is cheaper than a second listener that has to stay in step with this one.
    this._trackViewHandler = () => {
      this._rebuildTrackProjection();
      this._syncTrackDots();
    };
    this.host.map.on('moveend', this._trackViewHandler);

    this._trackHoverHandler = (e: any) => this._onTrackHover(e);
    this.host.map.on('mousemove', this._trackHoverHandler);
    // A SEPARATE handler, deliberately: leaving the map fires no further
    // mousemove, so the readout would stay pinned to the last bead the pointer
    // passed. Reusing the move handler here would be worse than doing nothing —
    // it carries a layerPoint, so it would happily re-show the readout on the
    // way out.
    this._trackLeaveHandler = () => this._hideTrackHover();
    this.host.map.on('mouseout', this._trackLeaveHandler);
  }

  /**
   * The travelled trail up to `index`, ending at the interpolated `cursor`,
   * broken into the same runs of continuous coverage `_trailBase` uses.
   *
   * Rebuilt from scratch rather than appended to, exactly as the flat version
   * was: it runs only when the step actually advances, which on a decimated
   * transport is a few hundred times over a whole replay.
   *
   * The cursor joins the LAST segment because the ghost is always somewhere in
   * the leg ahead of `index` — and when that leg is a gap the ghost is frozen
   * on the step itself (see `_renderAt`), so the cursor coincides with the
   * vertex already there and the segment simply does not grow. That is the
   * behaviour we want: nothing is painted across the gap while the clock
   * crosses it, and the next segment opens when the far side is reached.
   */
  private _doneSegments(index: number, cursor: any): any[][] {
    const segments: any[][] = [[]];
    for (let i = 0; i <= index && i < this._trackLatLngs.length; i++) {
      segments[segments.length - 1].push(this._trackLatLngs[i]);
      // Start a new part AFTER pushing i, so the point before a gap is the last
      // vertex of the run that ends at it rather than the first of the next.
      if (this._stepGaps[i] && i < index) segments.push([]);
    }
    segments[segments.length - 1].push(cursor);
    return segments;
  }

  /** Per-frame update: grow the travelled trail and move the ghost + halo. */
  renderTrackFrame(frame: VoyageTrackFrame | null): void {
    if (!frame) {
      this.clearTrackLayers();
      return;
    }
    if (!this._trailDone || !this._trackLatLngs.length) return;
    const cursor = L.latLng(frame.lat, frame.lng);
    if (frame.index !== this._trailIndex) {
      // The step advanced — rebuild the travelled portion (rare).
      this._trailDoneSegments = this._doneSegments(frame.index, cursor);
      this._trailIndex = frame.index;
    } else {
      // Only the interpolation moved — mutate the last vertex of the last
      // segment in place (common). Still the cursor, still one write; the only
      // change from the flat version is which array it lands in.
      const last = this._trailDoneSegments[this._trailDoneSegments.length - 1];
      if (last?.length) last[last.length - 1] = cursor;
    }
    this._trailDone.setLatLngs(this._trailDoneSegments);
    this._ghostMarker?.setLatLng(cursor);
    if (this._ghostEl) {
      this._ghostEl.style.transform = `rotate(${frame.course}deg)`;
    }
    // Same call site as the ghost, so the halo can never drift out of sync.
    this._haloMarker?.setLatLng(cursor);
  }

  /**
   * Removes the track layers, undims the fleet and restores the live marker.
   *
   * `restoreView` is false only when `buildTrackLayers` calls this to make way
   * for the next track — putting the map back and immediately re-fitting it
   * would read as a glitch rather than as a return.
   *
   * Even when it is true the restore is conditional: it happens only while the
   * track is still what framed the map. See `_trackOwnsView`.
   */
  clearTrackLayers(restoreView = true): void {
    // Before the layers go: a frame still queued would call setLatLngs on a
    // polyline that has just left the map.
    this.cancelTrailReveal();
    // Before the layers: these handlers close over `_trackDots`, and an event
    // landing after the group has gone would act on a track no longer drawn.
    this._detachTrackHover();
    for (const layer of [
      this._trailBase,
      this._trailLink,
      this._trailDone,
      this._trackDots,
      this._trackMarks,
      this._ghostMarker,
    ]) {
      if (layer && this.host.map?.hasLayer(layer)) this.host.map.removeLayer(layer);
    }
    this._trailBase = null;
    this._trailLink = null;
    this._trailDone = null;
    this._trackDots = null;
    this._trackMarks = null;
    this._ghostMarker = null;
    this._ghostEl = null;
    this._trailDoneSegments = [];
    this._stepGaps = [];
    this._trackLatLngs = [];
    this._trailIndex = -1;
    // Drop the traced hull's fade and the fleet dim together, in one pass: the
    // live marker goes back to full because it is no longer `_tracedMmsi`.
    this._tracedMmsi = null;
    this.setTrackFocus(null);
    // Put the halo back on the vessel's live position.
    if (this.active && this._haloMarker) {
      this._haloMarker.setLatLng([this.active.lat, this.active.lng]);
    }
    // Deliberately NOT cleared on the swap path: at that moment the map is
    // still sitting on the previous track's fitted bounds, so dropping it would
    // let the next build capture THAT as the view to return to and the original
    // one would be lost after two swaps.
    if (restoreView) {
      // Only when the track is still what framed the map — see `_trackOwnsView`.
      if (this._preTrackView && this.host.map && this._trackOwnsView()) {
        const { center, zoom } = this._preTrackView;
        this.host.map.setView(center, zoom);
      }
      this._preTrackView = null;
      this._trackFitView = null;
    }
  }

  /** The T control: a permanent timestamp label on the ghost vessel. */
  toggleGhostLabel(show: boolean): void {
    const marker = this._ghostMarker;
    if (!marker) return;
    if (!show) {
      marker.unbindTooltip();
      return;
    }
    // `||`, not `??`: a vessel that broadcasts no name reaches `active.name` as
    // '' — `activateVessel` builds it with `String(p['Name'] ?? mmsi)`, and an
    // empty string is not nullish, so it passes both that guard and this one. A
    // PERMANENT tooltip bound to '' is a blank box parked beside the ghost for
    // the whole replay. With nothing usable at all, bind no tooltip.
    const label = this.active?.name || this.active?.mmsi || '';
    if (!label) {
      marker.unbindTooltip();
      return;
    }
    marker.bindTooltip(label, {
      permanent: true,
      direction: 'right',
      offset: [12, 0],
      className: 'ais-vessel-label',
    });
  }

  // ── Located vessels (worldwide search) ──────────────────────────────────────
  /**
   * The free in-AOI search index (A1): everything loaded this session — the
   * live snapshot AND every loaded playback frame — matched on MMSI
   * (prefix/exact) and name (case-insensitive substring). Those are the two
   * fields the AOI feed actually populates (name only ~52%); IMO and call sign
   * are absent from bbox data entirely, so they are matched only against KEPT
   * vessels, whose identity a paid call filled in.
   *
   * Client-side and instant — no network, no credit, ever.
   */
  searchLoaded(query: string, limit = 8): VoyageLocalHit[] {
    const q = String(query ?? '').trim().toLowerCase();
    if (q.length < 2) return [];
    const digits = /^\d+$/.test(q);
    const hits = new Map<string, VoyageLocalHit>();

    const match = (mmsi: string, name: string | null, extra?: string | null): boolean => {
      if (digits) return mmsi.startsWith(q);
      const inName = !!name && name.toLowerCase().includes(q);
      const inExtra = !!extra && extra.toLowerCase().includes(q);
      return inName || inExtra;
    };

    const history: any[] = Array.isArray(this.sensor?.vesselHistory)
      ? this.sensor.vesselHistory
      : [];
    const historyMmsis = new Set(history.map((v: any) => String(v?.mmsi ?? '')));

    const consider = (
      p: any,
      source: 'live' | 'frame',
      frameTime?: string,
    ): void => {
      const mmsi = String(p?.['MMSI'] ?? p?.mmsi ?? '');
      if (!mmsi) return;
      const existing = hits.get(mmsi);
      // A live hit always outranks a frame hit for the same hull.
      if (existing && existing.source === 'live') return;
      const name = (p?.['Name'] ?? p?.name ?? p?.shipName ?? '') || null;
      if (!match(mmsi, name)) return;
      const lat = Number(p?.['Lat'] ?? p?.lat ?? p?.latitude);
      const lng = Number(p?.['Lng'] ?? p?.lng ?? p?.longitude);
      hits.set(mmsi, {
        mmsi,
        name,
        shipType: (p?.['Ship Type'] ?? p?.shipType ?? '') || null,
        lat: Number.isFinite(lat) ? lat : null,
        lng: Number.isFinite(lng) ? lng : null,
        source,
        frameTime: source === 'frame' ? frameTime ?? null : null,
        kept: historyMmsis.has(mmsi),
      });
    };

    for (const f of this._liveFeatures) consider(f?.properties ?? f, 'live');
    for (const step of this.loadedSteps) {
      for (const f of step.features ?? []) {
        consider(f?.properties ?? f, 'frame', String(step.time ?? ''));
      }
    }
    // History vessels, straight off the sensor doc — NOT off the drawn markers,
    // because history is list-only until located. Their identity came from a
    // paid call, so IMO/call sign match here — and they are findable with
    // NOTHING drawn at all (the empty-map case).
    for (const raw of history) {
      const r = this.toLocatedRecord(raw);
      if (!r) continue;
      if (hits.get(r.mmsi)?.source === 'live') continue;
      const extra = [r.imoNumber, r.callSign].filter(Boolean).join(' ');
      if (!match(r.mmsi, r.name, extra)) continue;
      hits.set(r.mmsi, {
        mmsi: r.mmsi,
        name: r.name,
        shipType: r.shipType,
        lat: r.lat,
        lng: r.lng,
        source: hits.get(r.mmsi)?.source ?? 'kept',
        frameTime: null,
        kept: true,
      });
    }
    return Array.from(hits.values()).slice(0, limit);
  }

  /** How many loaded hulls broadcast no name — the honest-empty-state number. */
  unnamedCount(): number {
    let n = 0;
    for (const f of this._liveFeatures) {
      const p = f?.properties ?? f ?? {};
      if (!(p['Name'] ?? p.name)) n += 1;
    }
    return n;
  }

  /** Saved snapshots exist for this sensor (the two empty maps differ on it). */
  get hasRefreshHistory(): boolean {
    return (this.sensor?.refreshHistory?.length ?? 0) > 0;
  }

  /**
   * Push the four map facts the search drawer renders but cannot reach.
   *
   * A toolbar control gets `{ controller, slot, options }` and no sensor; this
   * class is constructed by the host rather than injected, so DI is not a route
   * either. Same shape as `pushLocatedState` above it, and called from the same
   * moments — anything that changes what is drawn changes what is searchable.
   */
  pushSearchContext(): void {
    this.host.voyageSearch.setMapContext({
      mapEmpty: !this.dataActive,
      hasHistory: this.hasRefreshHistory,
      vesselCount: this.currentFeatures?.length ?? 0,
      unnamed: this.unnamedCount(),
    });
  }

  /**
   * The one paid act in the search drawer: widen the query to the worldwide
   * registry. Modelled on `fetchPortCalls`, with one deliberate difference.
   *
   * FAILURES REPORT INLINE, not through `popupService`. Every other paid path
   * here raises a modal because its drawer has no room for a sentence and the
   * user's next act is elsewhere; a search miss is part of searching, and a
   * modal over a box you are still typing in interrupts the very loop that
   * fixes it. The copy is still `voyageSpendErrorMessage`, so one wallet is
   * never described two ways — only the surface differs.
   *
   * THE PRE-FLIGHT REFUSAL IS THE EXCEPTION TO THAT, and takes the modal every
   * other paid path takes. The inline rule above is scoped to what it was
   * written for — outcomes OF a search, which the box in front of the user is
   * how you fix. A spend refusal is not one of those: it is the same refusal
   * the trace, port-call and nearby drawers raise a modal for, about the same
   * wallet, and the one refusal that reaches here (an ended TOI) cannot be
   * fixed by editing the query at all. Reporting it in the panel's error slot
   * would have been the fourth surface for one sentence, and the quietest.
   */
  private searchWorldwideVessels(q: string): void {
    const sensor = this.sensor;
    const taskid = this.taskId;
    const sensorId = sensor?.sensor_uuid ?? sensor?.id;
    const search = this.host.voyageSearch;
    if (!sensor || !taskid || !sensorId || !q) {
      search.setSearchFailed('This sensor is not ready to search yet.');
      return;
    }

    const refusal = this.spendRefusal('search', this.searchCredits);
    if (refusal) {
      // Reset without a message, exactly as the nearby and port-call drawers do
      // before raising their own modal — the panel must not hold a spinner for
      // a request that never left, and must not print the sentence twice.
      search.setSearchFailed();
      this.host.popupService.openPopup(CollectionPopupComponent, refusal, '', '40%');
      return;
    }

    this.host.apiService
      .searchVoyageRadarVessels({ taskid, sensorId, q, limit: 20 })
      .toPromise()
      .then((res: any) => {
        const results = Array.isArray(res?.data) ? res.data : [];
        const total =
          Number.isFinite(res?.count) && res.count > results.length ? res.count : null;
        // The stored counter IS the balance — mirror it onto the sensor so the
        // trace, port-call and nearby drawers agree without a refetch.
        if (res?.apiCreditsRemaining != null) {
          sensor.apiCreditsRemaining = res.apiCreditsRemaining;
        }
        search.setWorldwide(q, results, total);
        this.syncCreditLines();
      })
      .catch((err: any) => {
        if (err?.error?.apiCreditsRemaining != null) {
          sensor.apiCreditsRemaining = err.error.apiCreditsRemaining;
        }
        search.setSearchFailed(
          voyageSpendErrorMessage(err, 'search', this.searchCredits),
        );
        this.syncCreditLines();
      });
  }

  /** True when a history vessel's marker is currently drawn — the panel's "on map" tag. */
  isLocatedOnMap(mmsi: string): boolean {
    return this._locatedVessels.has(String(mmsi));
  }

  /** True when the vessel has a saved record (drawn or not). */
  isSaved(mmsi: string): boolean {
    const m = String(mmsi ?? '').trim();
    return (this.sensor?.vesselHistory ?? []).some((v: any) => String(v?.mmsi) === m);
  }

  /**
   * The popup context-menu for a hull, resolved from its state. Every popup
   * gets the paid lookup and the free save toggle; a located (amber)
   * marker additionally gets Remove-from-the-map.
   *
   * The two used to be one button: a located marker offered only "Remove from
   * the map", and that also deleted the saved record. So the menu could
   * delete a saved vessel while saying it was tidying your map, and
   * there was no way to do either one on its own. They are now what they say —
   * the toggle owns Saved Vessels, remove owns the map.
   *
   * PORT CALLS AND NEARBY ARE GATED ON THEIR DRAWER BEING MOUNTED. Both buttons
   * do nothing but open a drawer — `openAndFetch` returns immediately when
   * `onRequestOpen` is null — and that callback exists only while the control
   * is on the current bar. Since neither rides the replay bars, a card opened
   * during a track or snapshot replay would otherwise show two buttons that
   * swallow the click in silence. A null price hides the button outright (see
   * `buildVoyagePopup`), so the card offers what it can actually do and nothing
   * else. Closing the replay restores the live bar and both buttons with it.
   */
  popupOptsFor(mmsi: string): VoyagePopupActions {
    const m = String(mmsi ?? '').trim();
    if (!m) return { lookupCredits: this.lookupCredits, save: null };
    return {
      lookupCredits: this.lookupCredits,
      // On every hull, saved or not: port calls are a by-MMSI question and any
      // arrow on the chart is a legitimate subject.
      portCallCredits: this.host.voyagePortCalls.onRequestOpen
        ? this.portCallCredits
        : null,
      // The other half of that question, on the same terms. This line was
      // MISSING until now, which made the radar button on this card dead from
      // the day it was written: the price gates the markup, so the button never
      // rendered and the click branch waiting for it in
      // `ensurePopupActionsWired` could never fire.
      nearbyCredits: this.host.voyageNearby.onRequestOpen ? this.nearbyCredits : null,
      // Offered only while the hull is NOT saved. DELETING the record is the
      // Saved Vessels section's job — see `buildVoyagePopup`.
      save: this.isSaved(m) ? null : 'add',
      // Only a located marker can be taken off on its own; snapshot hulls leave
      // by the type filter or with their layer.
      removeFromMap: this.isLocatedOnMap(m),
    };
  }

  /**
   * The best name we hold for a hull, or null.
   *
   * Only ever a title and a fallback: the port-call route prefers the
   * operator's own `shipName` off the first row it gets back, so this is what
   * the drawer shows until that lands. Ordered by how close each source is to
   * the vessel — the live selection, then a located record, then whatever the
   * drawn marker was labelled with.
   */
  vesselNameFor(mmsi: string): string | null {
    const m = String(mmsi ?? '').trim();
    if (!m) return null;
    if (this.active?.mmsi === m && this.active.name) return this.active.name;
    const located = this._locatedVessels.get(m)?.record;
    if (located?.name) return String(located.name);
    const props = this.liveMarkerFor(m)?.feature?.properties;
    const name = props?.['Name'] ?? props?.name;
    return name ? String(name) : null;
  }

  /**
   * The popup's free "Save vessel" on a snapshot hull: persist the
   * identity + fix the AOI refresh ALREADY PAID FOR — no lookup, no charge.
   * Data comes from the drawn live marker, the live features, or (during
   * playback) the newest loaded frame that carries the hull.
   */
  saveSnapshotVessel(mmsi: string): void {
    const m = String(mmsi ?? '').trim();
    if (!m || this.isSaved(m)) return;
    // A located (amber) hull can also be unsaved — its keep may have failed,
    // or it was deleted from the panel while its marker stayed up. It carries
    // its own record, and it is usually NOT in the snapshot, so the lookup
    // below would find nothing and the button would silently do nothing.
    const located = this._locatedVessels.get(m);
    if (located) {
      this.keepVessel(located.record);
      return;
    }
    const props = this.snapshotPropsFor(m);
    if (!props) return;
    const num = (v: any): number | null => {
      // Not defensive noise: `toVoyageVesselFeature` writes `Lat: ''` for a hull
      // with no position, and `Number('')` is 0 — so without this guard a
      // fix-less vessel is saved at 0, 0 and drawn in the Gulf of Guinea.
      if (v == null || (typeof v === 'string' && !v.trim())) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const record: VoyageLocatedRecord = {
      mmsi: m,
      name: (props['Name'] ?? props.name ?? '') || null,
      shipType: (props['Ship Type'] ?? props.shipType ?? '') || null,
      imoNumber: props['IMO'] ?? props.imo ?? null,
      callSign: props['Call Sign'] ?? props.callSign ?? null,
      flag: props['Flag'] ?? props.flag ?? null,
      country: props['Country'] ?? props.country ?? null,
      lat: num(props['Lat'] ?? props.lat ?? props.latitude),
      lng: num(props['Lng'] ?? props.lng ?? props.longitude),
      timeUtc: props['Timestamp UTC'] ?? props.time_utc ?? null,
      navStatus: props['Nav Status'] ?? props.navStatus ?? null,
      waterBody: props['Water Body'] ?? props.waterBody ?? null,
      source: 'snapshot',
      query: null,
      kept: false,
      sog: num(props['Speed'] ?? props.sog),
      cog: num(props['Course'] ?? props.cog),
      heading: num(props['Heading'] ?? props.heading),
      destination: null,
      fromPort: null,
      length: num(props['Length'] ?? props.length),
      beam: num(props['Beam'] ?? props.beam),
      maxDraught: null,
    };
    this.keepVessel(record);
  }

  /** A hull's loaded properties: live marker → live features → newest frame. */
  private snapshotPropsFor(mmsi: string): any | null {
    const marker = this.liveMarkerFor(mmsi);
    if (marker?.feature?.properties) {
      const ll = marker.getLatLng?.();
      return {
        ...marker.feature.properties,
        latitude: ll?.lat ?? marker.feature.properties?.latitude,
        longitude: ll?.lng ?? marker.feature.properties?.longitude,
      };
    }
    const withCoords = (f: any): any => {
      const p = f?.properties ?? f;
      const coords = f?.geometry?.coordinates;
      return coords
        ? { ...p, longitude: p?.longitude ?? coords[0], latitude: p?.latitude ?? coords[1] }
        : p;
    };
    for (const f of this._liveFeatures) {
      const p = f?.properties ?? f;
      if (String(p?.['MMSI'] ?? p?.mmsi ?? '') === mmsi) return withCoords(f);
    }
    for (let i = this.loadedSteps.length - 1; i >= 0; i--) {
      for (const f of this.loadedSteps[i].features ?? []) {
        const p = f?.properties ?? f;
        if (String(p?.['MMSI'] ?? p?.mmsi ?? '') === mmsi) {
          const merged = withCoords(f);
          return {
            ...merged,
            time_utc:
              merged?.['Timestamp UTC'] ?? merged?.time_utc ?? this.loadedSteps[i].time ?? null,
          };
        }
      }
    }
    return null;
  }

  /**
   * What one action costs on THIS sensor, off its live catalogue (an ee-admin
   * re-price sticks) with the shipped price as the fallback.
   *
   * Never a literal anywhere in this file: an ee-admin re-price has to move
   * every number the user sees. The five getters below are named windows onto
   * this one call — none of them is shape dependent, because every endpoint
   * behind them answers by MMSI or is global, so an area task and a point task
   * reach them at the identical price. (The area/point REFRESH is the one that
   * does differ, and it is priced by `refreshCredits(sensor)`, which picks its
   * key from the tasked footprint.)
   *
   * `nearby` is the one that needs that stated rather than assumed, because it
   * is BOTH: it is the key `refreshActionKey(true)` returns, so on a point task
   * `nearbyCredits` and `refreshCredits(sensor)` resolve the same number by
   * construction — one catalogue entry per upstream endpoint, reached here by a
   * second gesture. Two prices could never drift apart because there is only
   * one, which is the property the shared key exists to buy.
   */
  private creditsFor(key: string): number {
    return actionCredits(this.sensor, key);
  }

  get lookupCredits(): number {
    return this.creditsFor('lookup');
  }

  get trackCredits(): number {
    return this.creditsFor('track');
  }

  get searchCredits(): number {
    return this.creditsFor('search');
  }

  get portCallCredits(): number {
    return this.creditsFor('portCalls');
  }

  get nearbyCredits(): number {
    return this.creditsFor('nearby');
  }

  /**
   * Push price + balance into every controller that prints a cost.
   *
   * A toolbar control has no sensor of its own — `ToolbarControlContext` is
   * `{ controller, slot, options }` — so each controller is TOLD its prices
   * rather than reading them. The balance moves on every spend, and a control
   * quoting a stale one is the bug this mirroring exists to prevent, so the two
   * pushes are deliberately one call: a future third priced control gets kept
   * in step by adding a line here, not by finding all eight call sites again.
   *
   * The nearby drawer's STORAGE SCOPE rides along for the same reason rather
   * than as an afterthought: it is another per-sensor fact a toolbar control
   * cannot reach, and it cannot be pushed once from `attach()` because `sensor`
   * is a getter over `host.collection`, which is usually still empty when the
   * constructor runs. Every call site that can move the balance can also have
   * changed the task, so the two facts travel together and neither goes stale
   * on its own.
   */
  private syncCreditLines(): void {
    const remaining = this.creditsRemaining();
    // The TOI travels with the balance for the reason the storage scope below
    // does: it is a per-sensor fact a toolbar control cannot reach, it decides
    // whether a priced button may be pressed at all, and pushing it on any
    // other schedule than the balance's would let one drawer offer a spend the
    // next one had already stopped offering.
    const toiEnded = this.toiEnded;
    this.host.voyageTrack.setCreditsRemaining(remaining, this.trackCredits, toiEnded);
    this.host.voyagePortCalls.setCredits(remaining, this.portCallCredits, toiEnded);
    // Two prices: the search on the drawer's own button, and the ordinary
    // vessel lookup a row spends when it is clicked.
    this.host.voyageNearby.setCredits(
      remaining,
      this.nearbyCredits,
      this.lookupCredits,
      toiEnded,
    );
    this.host.voyageSearch.setCredits(remaining, this.searchCredits, toiEnded);

    const sensor = this.sensor;
    this.host.voyageNearby.setStorageScope(
      this.host.collection?.taskId ?? null,
      sensor?.sensor_uuid ?? sensor?.id ?? null,
    );
  }

  /**
   * The by-MMSI port-call history (1 credit): every recorded arrival, departure
   * and stay for one hull. Modelled on `refreshVesselLookup` above, which is
   * the reference implementation for a 1-credit client spend — every one of its
   * behaviours is load-bearing and repeated here.
   *
   * Stores nothing itself beyond mirroring the server's record onto the local
   * sensor doc: that mirror is what makes re-opening the drawer free, because
   * the controller's own selection watcher reads `portCallHistory` directly.
   */
  fetchPortCalls(mmsi: string, vesselName: string | null): boolean {
    const sensor = this.sensor;
    const taskid = this.host.collection?.taskId;
    const sensorId = sensor?.sensor_uuid ?? sensor?.id;
    const ports = this.host.voyagePortCalls;
    if (!taskid || !sensorId || !mmsi) {
      ports.setFailed();
      return false;
    }
    // In-flight guard — a double click must not buy the same history twice.
    if (this.portCallLoadingMmsi) return false;

    // Refused at the door, before any request, and the drawer's spinner is
    // cleared so it does not hang on a fetch that never left.
    const refusal = this.spendRefusal('portCalls', this.portCallCredits);
    if (refusal) {
      ports.setFailed();
      this.host.popupService.openPopup(CollectionPopupComponent, refusal, '', '40%');
      return false;
    }

    this.portCallLoadingMmsi = mmsi;
    this.host.apiService
      .getVoyageRadarPortCalls({ taskid, sensorId, mmsi, vesselName })
      .toPromise()
      .then((res: any) => {
        this.portCallLoadingMmsi = null;
        // The stored counter IS the balance — mirror it so every price line agrees.
        if (res?.apiCreditsRemaining != null && sensor) {
          sensor.apiCreditsRemaining = res.apiCreditsRemaining;
          this.syncCreditLines();
        }
        if (!res?.record) {
          // An empty answer is still an ANSWER for this hull, and it was free.
          // Handing it to the controller is what stops the drawer re-offering a
          // spend that already happened for nothing.
          ports.setFetched(mmsi, { mmsi, calls: [], total: res?.total ?? 0, name: null });
          return;
        }
        // Mirror the record the server wrote into the local sensor doc, in the
        // same shape and under the same cap, so the panel and the drawer agree
        // without a refetch.
        const list: any[] = Array.isArray(sensor.portCallHistory) ? sensor.portCallHistory : [];
        const i = list.findIndex((r: any) => String(r?.mmsi) === mmsi);
        if (i >= 0) list[i] = res.record;
        else {
          list.push(res.record);
          if (list.length > 25) list.splice(0, list.length - 25);
        }
        sensor.portCallHistory = list;
        ports.setFetched(mmsi, res.record);
      })
      .catch((err: any) => {
        this.portCallLoadingMmsi = null;
        ports.setFailed();
        if (err?.error?.apiCreditsRemaining != null && sensor) {
          sensor.apiCreditsRemaining = err.error.apiCreditsRemaining;
          this.syncCreditLines();
        }
        // Failure copy always states the charge outcome — silence here reads
        // as "it took my credit". One shared ladder, so this path and the
        // refresh/track/search ones cannot describe the same wallet differently.
        this.host.popupService.openPopup(
          CollectionPopupComponent,
          voyageSpendErrorMessage(err, 'portCalls', this.portCallCredits),
          '',
          '40%'
        );
      });
    return true;
  }

  /**
   * The by-vessel nearby search (2 credits): who is around THIS hull, right now.
   *
   * Line for line the same shape as `fetchPortCalls` above — same guards, same
   * wallet mirror, same failure copy — because it is the same kind of act: a
   * paid per-MMSI lookup whose answer is stored inline on the sensor so
   * re-opening the drawer costs nothing.
   *
   * Three things differ, and all three come from the endpoint rather than taste:
   *
   *  - IT CARRIES A RADIUS, which the operator chose. It is sent, and the radius
   *    the server actually SERVED comes back on the record — those differ
   *    whenever the platform clamped, and the drawer labels itself with the
   *    second, never the first.
   *  - IT SENDS A CENTRE ONLY WHEN IT HAS A REAL ONE. The server prefers its own
   *    stored fix for the hull and treats anything non-finite or out of range as
   *    absent, so `lat`/`lon` here are strictly the live-AOI case: a hull drawn
   *    from a snapshot that was never written to Mongo. `api.service` drops them
   *    when they are not real numbers, so passing undefined is honest rather
   *    than lossy.
   *  - AN EMPTY ANSWER IS STILL AN ANSWER. Nothing within 5 km of a ship in open
   *    water is the correct reply to a question the user framed, not a failure,
   *    and it was free. Handing a count-0 record to the controller is what keeps
   *    the drawer from dropping back to "not searched" and re-offering a spend
   *    that already happened.
   */
  fetchNearbyVessels(mmsi: string, vesselName: string | null, radiusKm: number): boolean {
    const sensor = this.sensor;
    const taskid = this.host.collection?.taskId;
    const sensorId = sensor?.sensor_uuid ?? sensor?.id;
    const nearby = this.host.voyageNearby;
    if (!taskid || !sensorId || !mmsi) {
      nearby.setFailed();
      return false;
    }
    // In-flight guard — a double click must not buy the same search twice.
    if (this.nearbyLoadingMmsi) return false;

    const refusal = this.spendRefusal('nearby', this.nearbyCredits);
    if (refusal) {
      nearby.setFailed();
      this.host.popupService.openPopup(CollectionPopupComponent, refusal, '', '40%');
      return false;
    }

    // The live-AOI centre, when this hull is one the browser drew and Mongo has
    // never seen. Anything else and the server's own stored fix is both better
    // and already there.
    const centre = this.snapshotFixFor(mmsi);

    this.nearbyLoadingMmsi = mmsi;
    this.host.apiService
      .getVoyageRadarNearbyVessels({
        taskid,
        sensorId,
        mmsi,
        radiusKm,
        lat: centre?.lat,
        lon: centre?.lon,
        vesselName,
      })
      .toPromise()
      .then((res: any) => {
        this.nearbyLoadingMmsi = null;
        if (res?.apiCreditsRemaining != null && sensor) {
          sensor.apiCreditsRemaining = res.apiCreditsRemaining;
          this.syncCreditLines();
        }
        if (!res?.record) {
          // No record came back, which the route does on the free paths. The
          // question was still answered, so it is shown as one — carrying the
          // radius so the drawer can say WHAT was empty rather than just that
          // something was.
          nearby.setFetched(mmsi, {
            mmsi,
            vessels: [],
            count: 0,
            truncated: false,
            radiusKm: res?.radiusKm ?? radiusKm,
            name: null,
          });
          return;
        }
        // Mirror the server's record onto the local sensor doc, same shape and
        // same cap, so the drawer and the panel agree without a refetch.
        const list: any[] = Array.isArray(sensor.nearbyHistory) ? sensor.nearbyHistory : [];
        const i = list.findIndex((r: any) => String(r?.mmsi) === mmsi);
        if (i >= 0) list[i] = res.record;
        else {
          list.push(res.record);
          if (list.length > 25) list.splice(0, list.length - 25);
        }
        sensor.nearbyHistory = list;
        nearby.setFetched(mmsi, res.record);
      })
      .catch((err: any) => {
        this.nearbyLoadingMmsi = null;
        nearby.setFailed();
        if (err?.error?.apiCreditsRemaining != null && sensor) {
          sensor.apiCreditsRemaining = err.error.apiCreditsRemaining;
          this.syncCreditLines();
        }
        // A 409 is not a failure of the platform but a missing precondition, and
        // it has its own way out: buy the 1-credit lookup that gives this hull a
        // stored position permanently. Saying so is the difference between a
        // dead end and a next step. Everything else goes through the shared
        // ladder, which always states the charge outcome.
        const message =
          err?.status === 409 || err?.error?.code === 'NO_POSITION'
            ? 'This vessel has no stored position yet, so there is no centre to search around. ' +
              'Use Get latest on it first — that is 1 credit and it fixes this for good. ' +
              'You were not charged.'
            : voyageSpendErrorMessage(err, 'nearby', this.nearbyCredits);
        this.host.popupService.openPopup(CollectionPopupComponent, message, '', '40%');
      });
    return true;
  }

  /**
   * A nearby ROW was clicked — place that hull on the map.
   *
   * Deliberately the same three-way decision `pickPoiNearbyVessel` already makes
   * for the POI list, because it is the same question asked from a different
   * drawer: already drawn, or kept with a stored fix, and it is free; otherwise
   * the 1-credit lookup, which is the ONLY way to get a position for a row whose
   * list carries none.
   *
   * `onSettled` is threaded back so the row's own spinner clears on both the
   * success and the failure path — and is fired IMMEDIATELY on the free paths,
   * which never had a request to wait for.
   */
  private pickNearbyRowVessel(mmsi: string, onSettled?: () => void): void {
    const m = String(mmsi ?? '').trim();
    if (!m) {
      onSettled?.();
      return;
    }
    const settleAndSync = () => {
      onSettled?.();
      // The badge is what tells the user a row is now free to revisit, and it
      // is read off the map rather than assumed from the click succeeding.
      this.host.voyageNearby.refreshOnMap((k) => this._locatedVessels.has(k));
    };

    if (this._locatedVessels.has(m)) {
      this.selectLocated(m, { openPopup: true, fly: true });
      settleAndSync();
      return;
    }
    const rec = (this.sensor?.vesselHistory ?? []).find((v: any) => String(v?.mmsi) === m);
    const stored = rec ? this.toLocatedRecord(rec) : null;
    if (stored && stored.lat != null && stored.lng != null) {
      this.locateHistoryRecord(rec);
      settleAndSync();
      return;
    }
    // Paid path. The row is marked busy by the controller only if a request
    // actually STARTED — the lookup turns several clicks back at the door — so a
    // row is never left spinning for a request that was never made.
    const started = this.refreshVesselLookup(m, {
      draw: true,
      select: true,
      onSettled: settleAndSync,
    });
    if (!started) settleAndSync();
  }

  /**
   * The fix for a hull the BROWSER drew, or null.
   *
   * Only the live-AOI snapshot case has a position the server does not: those
   * features live in the page and were never written to Mongo. A located or
   * searched hull is already in `vesselHistory` with an `asOf` block, and the
   * server reaches that itself and prefers it — so returning null here is not a
   * gap, it is deferring to the better source.
   */
  private snapshotFixFor(mmsi: string): { lat: number; lon: number } | null {
    const m = String(mmsi ?? '').trim();
    if (!m) return null;
    // `searchLoaded` is the free in-AOI index over the drawn snapshot and every
    // loaded frame — client-side and instant, no network and no credit, which is
    // the whole reason it is the right lookup for a value that is only ever a
    // fallback. Matched EXACTLY rather than taking the first hit: a digit query
    // matches on prefix there, and a neighbouring hull's position would be a
    // silently wrong centre rather than a visible failure.
    const hit = (this.searchLoaded(m, 8) || []).find((h) => h.mmsi === m);
    const lat = Number(hit?.lat);
    const lon = Number(hit?.lng);
    return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null;
  }

  /**
   * A Vessel Search History row's Locate: draw its marker at the stored fix and
   * make it the selection. History is LIST-ONLY until this is clicked — the
   * deliberate opposite of the old hydrate-everything pass, so ticking a sensor
   * with fifty history rows does not carpet the chart in amber diamonds.
   */
  locateHistoryRecord(rec: any): void {
    const record = this.toLocatedRecord(rec);
    if (!record) return;
    record.kept = true;
    if (record.lat == null || record.lng == null) {
      this.host.popupService.openPopup(
        CollectionPopupComponent,
        'This record carries no stored position. Use Refresh to fetch the vessel\'s latest fix.',
        '',
        '40%'
      );
      return;
    }
    this.upsertLocatedMarker(record);
    this.host.map?.flyTo([record.lat, record.lng], Math.max(this.host.map?.getZoom?.() ?? 8, 8));
    this.selectLocated(record.mmsi, { openPopup: true });
  }

  /**
   * The by-MMSI lookup (1 credit): latest fix + the fields nothing else carries
   * (destination, from port, dimensions). Refreshes the vessel's history record
   * in place server-side, mirrors it locally, and — when the vessel is drawn,
   * or `opts.draw` says to — moves its marker to the fresh fix and reopens the
   * popup there. Reached from a history row's Refresh, from the popup's
   * "Get latest" on any hull, and from a POI nearby row.
   *
   * Returns whether a request was actually sent: several clicks are turned back
   * at the door, and a caller that shows an in-flight mark has to be able to
   * tell those apart from a real one. `onSettled` then fires exactly once when
   * the round trip ends, on both the success and the failure path.
   */
  refreshVesselLookup(
    mmsi: string,
    opts: { draw?: boolean; select?: boolean; onSettled?: () => void } = {},
  ): boolean {
    const sensor = this.sensor;
    const taskid = this.host.collection?.taskId;
    const sensorId = sensor?.sensor_uuid ?? sensor?.id;
    if (!taskid || !sensorId || this.lookupLoadingMmsi) return false;
    const refusal = this.spendRefusal('lookup', this.lookupCredits);
    if (refusal) {
      this.host.popupService.openPopup(CollectionPopupComponent, refusal, '', '40%');
      return false;
    }

    this.lookupLoadingMmsi = mmsi;
    this.setVesselPopupUpdating(mmsi, true);
    this.host.apiService
      .lookupVoyageRadarVessel({ taskid, sensorId, mmsi })
      .toPromise()
      .then((res: any) => {
        this.lookupLoadingMmsi = null;
        this.setVesselPopupUpdating(mmsi, false);
        // Before the map work below, not after: the mark's job is to say the
        // ROUND TRIP is over, and placing the vessel is what happens next.
        opts.onSettled?.();
        // The stored counter IS the balance — mirror it so every price line agrees.
        if (res?.apiCreditsRemaining != null && sensor) {
          sensor.apiCreditsRemaining = res.apiCreditsRemaining;
          this.syncCreditLines();
        }
        if (!res?.vessel) {
          this.host.popupService.openPopup(
            CollectionPopupComponent,
            'The operator has no data for this vessel right now. You were not charged.',
            '',
            '40%'
          );
          return;
        }
        // Mirror the refreshed record into the local sensor doc (the server
        // updated it in place), so the panel rows agree without a refetch.
        const list: any[] = Array.isArray(sensor.vesselHistory) ? sensor.vesselHistory : [];
        const i = list.findIndex((v: any) => String(v?.mmsi) === mmsi);
        if (i >= 0) {
          list[i] = {
            ...list[i],
            name: res.vessel.name,
            identity: res.vessel.identity ?? list[i].identity,
            asOf: res.vessel.asOf ?? list[i].asOf,
            details: res.vessel.details ?? list[i].details,
          };
        } else {
          list.push(res.vessel);
          // Same cap the server enforces with $slice — newest stay.
          if (list.length > 50) list.splice(0, list.length - 50);
        }
        sensor.vesselHistory = list;

        // Move the drawn marker to the fresh fix — or draw it, when the lookup
        // came from a hull's popup ("get latest" implies "show me").
        const row = i >= 0 ? list[i] : list[list.length - 1];
        const normalized = this.toLocatedRecord(row);
        if (normalized && (this._locatedVessels.has(mmsi) || opts.draw)) {
          normalized.kept = true;
          if (normalized.lat != null && normalized.lng != null) {
            this.upsertLocatedMarker(normalized);
            if (opts.draw) {
              this.host.map?.flyTo(
                [normalized.lat, normalized.lng],
                Math.max(this.host.map?.getZoom?.() ?? 8, 8),
              );
            }
            // A hull fetched from the nearby list ends exactly where a search
            // result does — selected, so the arrival is marked by the halo and
            // the Trace button comes up on it. Scoped to that caller: pressing
            // Update on a card already open is not a "show me this one".
            if (opts.select) this.selectLocated(mmsi, { openPopup: true });
            else this.openLocatedInfo(mmsi);
          }
        }
        this.pushLocatedState();
      })
      .catch((err: any) => {
        this.lookupLoadingMmsi = null;
        // The card is NOT replaced on this path, so it would spin forever.
        this.setVesselPopupUpdating(mmsi, false);
        opts.onSettled?.();
        if (err?.error?.apiCreditsRemaining != null && sensor) {
          sensor.apiCreditsRemaining = err.error.apiCreditsRemaining;
        }
        // Failure copy always states the charge outcome — silence here reads
        // as "it took my credit".
        this.host.popupService.openPopup(
          CollectionPopupComponent,
          voyageSpendErrorMessage(err, 'lookup', this.lookupCredits),
          '',
          '40%'
        );
      });
    return true;
  }

  /**
   * One click-delegate on the map container for the popup's header actions —
   * Leaflet popups render outside Angular, so a template binding can never
   * reach them. Idempotent per container; rewired if the map is rebuilt.
   *
   * The free add closes the card, because its own button is what disappears on
   * the next open and a button that has just become a lie is worse than none.
   * The paid update keeps it open until the fresh card replaces it.
   */
  private ensurePopupActionsWired(): void {
    const container = this.host.map?.getContainer?.();
    if (!container || this._popupClickTarget === container) return;
    this._popupClickTarget = container;

    // Tracking, not actions — but it belongs to the same wiring, and for the
    // same reason: FOUR layers open this card (the live and history layers bind
    // their own popups in the host, playback builds one per click, and only
    // `openVesselPopupAt` is this controller's), so tracking it at the point of
    // creation would mean four places to keep in step. One `popupopen` listener
    // sees them all. Tracking only the one path is what made the update spinner
    // appear on the SECOND press: the first card was one this controller had
    // never seen, and the second — reopened by the lookup itself — was ours.
    const map = this.host.map;
    map?.on?.('popupopen', (e: any) => {
      // `[data-mmsi]` is what makes this a vessel card: a cluster popup carries
      // the same class and no hull.
      const card = e?.popup?.getElement?.()?.querySelector?.('.ais-popup[data-mmsi]');
      this._vesselPopup = card ? e.popup : null;
      this._vesselPopupMmsi = card?.getAttribute?.('data-mmsi') || null;
    });
    map?.on?.('popupclose', (e: any) => {
      if (e?.popup !== this._vesselPopup) return;
      this._vesselPopup = null;
      this._vesselPopupMmsi = null;
    });
    container.addEventListener('click', (e: any) => {
      const target = e?.target;
      const poirow = target?.closest?.('.ais-popup__poirow');
      if (poirow) {
        const mmsi = poirow.getAttribute?.('data-mmsi');
        if (mmsi) this.pickPoiNearbyVessel(String(mmsi));
        return;
      }
      const latest = target?.closest?.('.ais-popup__latest');
      if (latest) {
        const mmsi = latest.getAttribute?.('data-mmsi');
        if (mmsi) this.refreshVesselLookup(String(mmsi), { draw: true });
        return;
      }
      const ports = target?.closest?.('.ais-popup__ports');
      if (ports) {
        // Opens the drawer AND fetches, every time. The MMSI comes off the
        // CARD, not from the map's selection: four layers open this popup and
        // only the playback one calls `activateVessel`, so on a live snapshot
        // hull there is no active vessel to read and this button used to open
        // an empty drawer and buy nothing.
        const mmsi = ports.getAttribute?.('data-mmsi');
        if (mmsi) {
          const m = String(mmsi);
          this.host.voyagePortCalls.openAndFetch(m, this.vesselNameFor(m));
        }
        return;
      }
      const near = target?.closest?.('.ais-popup__nearby');
      if (near) {
        // Same contract as the ports branch above, and the same reason for it:
        // the MMSI comes off the CARD. Of the four layers that open this popup
        // the located-vessel one selects nothing, so a branch that consulted the
        // map's selection would open the drawer and buy nothing there — and the
        // card is the one thing that always knows whose it is regardless.
        const mmsi = near.getAttribute?.('data-mmsi');
        if (mmsi) {
          const m = String(mmsi);
          this.host.voyageNearby.openAndFetch(m, this.vesselNameFor(m));
        }
        return;
      }
      const save = target?.closest?.('.ais-popup__save');
      if (save) {
        const mmsi = save.getAttribute?.('data-mmsi');
        if (!mmsi) return;
        this.host.map?.closePopup?.();
        this.saveSnapshotVessel(String(mmsi));
        return;
      }
      const offmap = target?.closest?.('.ais-popup__offmap');
      if (offmap) {
        const mmsi = offmap.getAttribute?.('data-mmsi');
        if (!mmsi) return;
        // Closes the card itself: the marker it is anchored to is going, and
        // `takeLocatedOffMap` already owns that (see `closeVesselPopupFor`).
        this.takeLocatedOffMap(String(mmsi));
        return;
      }
      if (target?.closest?.('.ais-popup__close')) this.host.map?.closePopup?.();
    });
  }

  // ── POI target (nearby list) ────────────────────────────────────────────────
  /**
  * A POI refresh landed (or a stored one was recalled): fill the tasked target
  * with the result, keep its disc at the fixed 1,000 km POI radius, and open
  * the nearby-vessel list as the reticle's popup.
   *
   * It no longer BUILDS the reticle and disc. Those are the target, the
   * checkbox owns them, and by the time a refresh lands they are already on the
   * map; building a second pair here is what used to leave a POI task's target
   * blinking out on every snapshot swap and gone for good on a clear.
   */
  showPoiNearby(scene: any, vessels: any[]): void {
    const [lng, lat] = scene?.footprint?.coordinates ?? [];
    if (!Number.isFinite(lng) || !Number.isFinite(lat) || !this.host.map) return;

    // The reticle and disc are the TARGET, which the checkbox owns and which is
    // very likely already up — this pull only fills them with a result. Building
    // them here (as this used to) put a second reticle over the first and tied
    // the target's life to the data's.
    this.poiVessels = Array.isArray(vessels) ? vessels : [];
    this.poiRadiusKm = VOYAGE_POI_RADIUS_KM;
    // Passing the scene this refresh belongs to, rather than letting the task
    // be re-resolved: this one is already known to be the right geometry.
    if (!this.showTarget({ scene })) return;
    // Keep the visible POI coverage disc at the fixed platform radius.
    this.targetCircle?.setRadius?.(this.poiRadiusKm * 1000);

    const marker = this.targetMarker;
    if (!marker) return;
    this.bindPoiPopup(marker);
    this.ensurePopupActionsWired();

    // Frame the disc, then present the list — the popup IS the result.
    this.fitTarget();
    marker.openPopup();
  }

  /**
   * The amber reticle at a POI task's tasked point: ring + centre dot, styled
   * inline because divIcon HTML renders outside every Angular component. A
   * custom className REPLACES Leaflet's 'leaflet-div-icon' default, so the
   * white box never applies and no stylesheet entry is needed — the class is
   * purely an identifier.
   *
   * Carries no popup: the reticle goes up with the checkbox, before any pull,
   * and a card offering an empty list would be worse than no card. The popup is
   * bound by `showPoiNearby` once there is a list to show.
   */
  private buildPoiReticle(lat: number, lng: number): any {
    const icon = L.divIcon({
      className: 'vr-poi-target',
      html:
        '<span style="display:block;width:22px;height:22px;border-radius:50%;' +
        'border:2px solid #fbbf24;box-shadow:0 0 0 2px rgba(15,23,42,.55);position:relative;">' +
        '<span style="position:absolute;left:50%;top:50%;width:6px;height:6px;' +
        'margin:-3px 0 0 -3px;border-radius:50%;background:#fbbf24;"></span></span>',
      iconSize: [22, 22],
      iconAnchor: [11, 11],
      popupAnchor: [0, -12],
    });
    const marker = L.marker([lat, lng], { icon, zIndexOffset: 400 });
    // Wired on the MARKER, not per bind: the filter box is plain DOM inside the
    // popup, so it has to be re-wired on every open (each open re-renders the
    // card from the content function), and `clearPoiNearby` unbinds the popup
    // without touching the marker — re-registering there would stack a second
    // listener on every pull.
    marker.on('popupopen', (e: any) => this.wirePoiFilter(e?.popup));
    return marker;
  }

  /**
   * Hangs the nearby-vessel list off the reticle, once.
   *
   * The content is a FUNCTION, so every open re-renders against current state —
   * a vessel placed on the map shows its "On map" badge the next time the list
   * opens, without any re-render plumbing. That is also why re-binding on a
   * second pull is unnecessary: the same closure reads whatever `poiVessels`
   * holds now.
   */
  private bindPoiPopup(marker: any): void {
    if (marker.getPopup?.()) return;
    marker.bindPopup(
      () => buildVoyagePoiNearbyPopup(this.poiVessels, this.poiPopupContext()),
      {
        className: 'ais-vessel-popup',
        closeButton: false,
        // Wider than a vessel card: this one carries two lines per row and a
        // right-aligned distance column, and the card's 300px was where long
        // names started eating the sub-line.
        maxWidth: 340,
        minWidth: 260,
        offset: L.point(0, -10),
        autoPanPadding: L.point(40, 90),
      },
    );
  }

  /** Everything the nearby list renders against, resolved fresh on every render. */
  private poiPopupContext(): VoyagePoiPopupContext {
    return {
      lookupCredits: this.lookupCredits,
      onMap: (m) => this.isLocatedOnMap(m),
      radiusKm: this.poiRadiusKm,
      pendingMmsi: this.poiPendingMmsi,
    };
  }

  /**
   * Re-renders the nearby rows in place — the ONLY thing that redraws them.
   *
   * The rows container alone, never the card: the filter input lives outside it
   * and keeps its focus and caret, which rebuilding the popup would drop after
   * the first letter. Rows come from `poiVessels`, the complete list, so a match
   * beyond the rendered cap is still reachable.
   *
   * Both triggers — a keystroke and a lookup starting or ending — go through
   * here, so neither can wipe what the other set. `keepScroll` is the
   * difference between them: a filter is a NEW list and belongs at the top,
   * while a row going busy must not yank the list out from under the operator.
   */
  private renderPoiRows(opts: { keepScroll?: boolean } = {}): void {
    const el = this.targetMarker?.getPopup?.()?.getElement?.();
    const rows: HTMLElement | null = el?.querySelector?.('.ais-popup__poirows');
    if (!rows) return;
    const scroll = rows.scrollTop;
    rows.innerHTML = buildVoyagePoiNearbyRows(
      this.poiVessels,
      this.poiPopupContext(),
      this.poiQuery,
    );
    rows.scrollTop = opts.keepScroll ? scroll : 0;
  }

  /**
   * Marks one row as having its lookup in flight, or clears the mark.
   *
   * The card root carries it too, which is what stands the other rows down:
   * only one lookup runs at a time, so while this one is out every other row is
   * an offer the guard in `refreshVesselLookup` would silently refuse.
   */
  private setPoiBusy(mmsi: string | null): void {
    this.poiPendingMmsi = mmsi;
    const card = this.targetMarker?.getPopup?.()?.getElement?.()
      ?.querySelector?.('.ais-popup--poi');
    card?.classList?.toggle('is-busy', !!mmsi);
    this.renderPoiRows({ keepScroll: true });
  }

  /**
   * Wires the filter box on every popup open (each open re-renders the card
   * from the content function, so the old node and its listeners are gone).
   *
   * Keystrokes are stopped from reaching the map: Leaflet's keyboard handler
   * pans on arrows and zooms on +/-, and it binds while the map has focus.
   */
  private wirePoiFilter(popup: any): void {
    // The card was just rebuilt unfiltered, so the remembered query is stale —
    // clearing it first keeps this state and the DOM in step even on the short
    // lists that never get a filter box at all.
    this.poiQuery = '';

    const el = popup?.getElement?.();
    const input: HTMLInputElement | null = el?.querySelector?.('.ais-popup__poifilter');
    if (!input) return;

    L.DomEvent.on(input, 'keydown keypress keyup', L.DomEvent.stopPropagation);
    L.DomEvent.on(input, 'input', () => {
      this.poiQuery = input.value;
      this.renderPoiRows();
    });
  }

  /**
   * Takes the nearby RESULT off the map — the popup and the list behind it.
   *
   * The reticle and disc stay: they are the tasked target, they belong to the
   * checkbox, and this runs on every snapshot swap. Removing them here is what
   * used to make a POI task's target blink out whenever the panel changed what
   * was shown, and vanish for good on a clear.
   *
   * The disc goes back to the TASKED radius, since the served radius it was
   * carrying described a result that is no longer on the map.
   */
  clearPoiNearby(): void {
    this.targetMarker?.closePopup?.();
    this.targetMarker?.unbindPopup?.();
    this.poiVessels = [];
    this.poiRadiusKm = null;
    this.poiQuery = '';
    this.poiPendingMmsi = null;
    this.targetCircle?.setRadius?.(VOYAGE_POI_RADIUS_KM * 1000);
  }

  /**
   * A row of the nearby list was clicked. Free when the hull is already
   * drawn or kept with a stored fix (same rule as the panel's Locate); the
   * paid by-MMSI lookup otherwise — which is the ONLY way to obtain a
   * position for a nearby row, since the list itself carries none.
   */
  private pickPoiNearbyVessel(mmsi: string): void {
    const m = String(mmsi ?? '').trim();
    if (!m) return;
    if (this._locatedVessels.has(m)) {
      this.selectLocated(m, { openPopup: true, fly: true });
      return;
    }
    const rec = (this.sensor?.vesselHistory ?? []).find(
      (v: any) => String(v?.mmsi) === m,
    );
    const stored = rec ? this.toLocatedRecord(rec) : null;
    if (stored && stored.lat != null && stored.lng != null) {
      this.locateHistoryRecord(rec);
      return;
    }
    // Paid path: latest fix + full details, placed like a search result. The
    // wallet gate, spend mirror and failure copy all live in the lookup.
    //
    // The row is marked busy only if a request actually STARTED — the lookup
    // turns several clicks back at the door (no wallet, one already in flight),
    // and a row left spinning for a request that was never made is a worse lie
    // than no feedback at all. `onSettled` fires on both the success and the
    // failure path, including the ones that leave this popup open.
    const started = this.refreshVesselLookup(m, {
      draw: true,
      select: true,
      onSettled: () => this.setPoiBusy(null),
    });
    if (started) this.setPoiBusy(m);
  }

  /**
   * A worldwide search result the user picked (A4 → A5): place it, keep it,
   * and — unless a trace is mid-flight — make it the selection.
   */
  locateSearchResult(result: any, query: string): boolean {
    const record = this.toLocatedRecord(result);
    if (!record) return false;
    if (record.lat == null || record.lng == null) {
      // The feed answered without a fix — nothing to place, and pretending
      // otherwise would drop a marker at [0,0].
      this.host.popupService.openPopup(
        CollectionPopupComponent,
        'The feed carries no position for this vessel right now, so it cannot be placed on the map.',
        '',
        '40%'
      );
      return false;
    }
    record.query = query;
    record.source = 'search';
    this.upsertLocatedMarker(record);
    this.keepVessel(record);
    this.host.map?.flyTo([record.lat, record.lng], Math.max(this.host.map?.getZoom?.() ?? 8, 8));
    this.selectLocated(record.mmsi, { openPopup: true });
    return true;
  }

  /** A row from the free in-AOI list: fly to it and select it where possible. */
  locateLoadedVessel(hit: VoyageLocalHit): void {
    if (hit.source === 'live') {
      const marker = this.liveMarkerFor(hit.mmsi);
      if (marker) {
        const latlng = marker.getLatLng();
        this.host.map?.flyTo(latlng, Math.max(this.host.map?.getZoom?.() ?? 8, 8));
        this.activateVessel(marker?.feature?.properties ?? { MMSI: hit.mmsi, Name: hit.name }, marker);
        this.openVesselPopupAt(latlng, marker?.feature?.properties ?? {}, this.popupOptsFor(hit.mmsi));
        return;
      }
    }
    if (this._locatedVessels.has(hit.mmsi)) {
      this.selectLocated(hit.mmsi, { openPopup: true, fly: true });
      return;
    }
    // A history hit that is not drawn yet — the list-only rule means this is
    // the normal path for a kept vessel: locate it from its stored record.
    if (hit.source === 'kept') {
      const rec = (this.sensor?.vesselHistory ?? []).find(
        (v: any) => String(v?.mmsi) === hit.mmsi,
      );
      if (rec) {
        this.locateHistoryRecord(rec);
        return;
      }
    }
    // A frame-only hit: the hull was in an earlier loaded snapshot. Fly to
    // where that frame saw it; stepping playback there is the user's call.
    if (hit.lat != null && hit.lng != null) {
      this.host.map?.flyTo([hit.lat, hit.lng], Math.max(this.host.map?.getZoom?.() ?? 8, 8));
    }
  }

  /**
   * Selection guard (A2): locating never steals the selection out from under a
   * trace. `deactivateVessel` closes the drawer and clears the active vessel,
   * so an unguarded swap would silently retarget a trace someone was mid-way
   * through configuring — or discard one that is loading.
   */
  private canTakeSelection(): boolean {
    if (this.host.voyageTrack.snapshot().trackLoading) return false;
    if (this.host.voyageTrack.hasTrack) return false;
    if (this.host.toolbarDrawer.active !== null) return false;
    return true;
  }

  /** Make a located vessel the map selection (guard permitting) — the normal
   *  grammar: selection is what the ordinary Trace button acts on. */
  selectLocated(mmsi: string, opts: { openPopup?: boolean; fly?: boolean } = {}): void {
    const entry = this._locatedVessels.get(mmsi);
    if (!entry) return;
    const r = entry.record;
    if (opts.fly && r.lat != null && r.lng != null) {
      this.host.map?.flyTo([r.lat, r.lng], Math.max(this.host.map?.getZoom?.() ?? 8, 8));
    }
    if (this.canTakeSelection()) {
      this.deactivateVessel();
      this.active = {
        mmsi,
        name: r.name ?? mmsi,
        color: VoyageMapController.LOCATED_COLOR,
        lat: r.lat ?? 0,
        lng: r.lng ?? 0,
        timestampIso: r.timeUtc ?? '',
        shipType: r.shipType ?? 'OTHER',
      };
      this.syncCreditLines();
      this.host.voyageTrack.setSavedTracks(this.savedTracksFor(mmsi));
      this.host.voyageTrack.setActiveVessel(this.active);
    }
    if (opts.openPopup) this.openLocatedInfo(mmsi);
    this.pushLocatedState();
  }

  /** Persist a located vessel into `vesselHistory` — free, per §A5. */
  private keepVessel(record: VoyageLocatedRecord): void {
    const sensor = this.sensor;
    const taskid = this.host.collection?.taskId;
    const sensorId = sensor?.sensor_uuid ?? sensor?.id;
    if (!taskid || !sensorId) return;
    this.host.apiService
      .saveVoyageRadarVessel({
        taskid,
        sensorId,
        mmsi: record.mmsi,
        source: record.source ?? 'search',
        query: record.query ?? undefined,
        identity: {
          shipName: record.name ?? undefined,
          imoNumber: record.imoNumber ?? undefined,
          callSign: record.callSign ?? undefined,
          shipType: record.shipType ?? undefined,
          flag: record.flag ?? undefined,
          country: record.country ?? undefined,
        },
        asOf: {
          latitude: record.lat ?? undefined,
          longitude: record.lng ?? undefined,
          time_utc: record.timeUtc ?? undefined,
          navigationalStatus: record.navStatus ?? undefined,
          waterBody: record.waterBody ?? undefined,
          sog: record.sog ?? undefined,
          cog: record.cog ?? undefined,
          heading: record.heading ?? undefined,
        },
      })
      .toPromise()
      .then((res: any) => {
        record.kept = true;
        // Mirror into the local sensor doc so a re-hydrate within this session
        // agrees with the server without a refetch.
        if (sensor && res?.vessel) {
          const list = Array.isArray(sensor.vesselHistory) ? sensor.vesselHistory : [];
          const i = list.findIndex((v: any) => String(v?.mmsi) === record.mmsi);
          if (i >= 0) list[i] = res.vessel;
          else list.push(res.vessel);
          sensor.vesselHistory = list;
        }
        this.pushLocatedState();
      })
      .catch(() => {
        // Keeping is best-effort: the marker is already on the map, and the
        // vessel bar simply reports it as not kept.
        record.kept = false;
        this.pushLocatedState();
      });
  }

  /**
   * Take a located vessel's marker off the map, keeping its saved record.
   *
   * The reversible half of the pair below. A Saved Vessels row is LIST-ONLY
   * until its Locate is clicked, so this simply puts the row back in that state:
   * the vessel stays saved, stays in the panel, and one click draws it again.
   * Nothing is sent to the server, because nothing about the record changed.
   *
   * Kept distinct from `removeLocatedVessel` because the two acts were one act
   * with one ✕ behind it: "clear my map" and "forget this vessel" are not the
   * same decision, and only one of them can be undone.
   */
  takeLocatedOffMap(mmsi: string): void {
    const key = String(mmsi ?? '').trim();
    const entry = this._locatedVessels.get(key);
    if (!entry) return;
    if (entry.marker && this._locatedLayer) this._locatedLayer.removeLayer(entry.marker);
    // The card is not bound to the marker, so it does not leave with it.
    this.closeVesselPopupFor(key);
    this._locatedVessels.delete(key);
    if (this.active?.mmsi === key && !this.host.voyageTrack.hasTrack) {
      this.deactivateVessel();
    }
    this.pushLocatedState();
  }

  /**
   * Take EVERY located marker off the map in one act — the ON MAP bar's
   * `layers_clear`, and the counterpart of the snapshot and track rows' own.
   *
   * Nothing is deleted: each vessel keeps its Saved Vessels row and can be drawn
   * again from it. Iterated over a copy of the keys because `takeLocatedOffMap`
   * mutates the map it would otherwise be walking.
   */
  takeAllLocatedOffMap(): void {
    for (const mmsi of Array.from(this._locatedVessels.keys())) {
      this.takeLocatedOffMap(mmsi);
    }
  }

  /**
   * Delete a vessel from Saved Vessels — permanently, and off the map with it.
   *
   * The irreversible half: the stored record goes, server-side too, so a reload
   * does not resurrect it. Works for an undrawn row as well (history is
   * list-only until located), where there is no marker to take down, only the
   * record.
   */
  removeLocatedVessel(mmsi: string): void {
    const entry = this._locatedVessels.get(mmsi);
    if (entry) {
      if (entry.marker && this._locatedLayer) this._locatedLayer.removeLayer(entry.marker);
      this.closeVesselPopupFor(mmsi);
      this._locatedVessels.delete(mmsi);
      if (this.active?.mmsi === mmsi && !this.host.voyageTrack.hasTrack) {
        this.deactivateVessel();
      }
    }
    // Forget server-side so a reload does not resurrect the record.
    const sensor = this.sensor;
    const taskid = this.host.collection?.taskId;
    const sensorId = sensor?.sensor_uuid ?? sensor?.id;
    const inHistory = (sensor?.vesselHistory ?? []).some(
      (v: any) => String(v?.mmsi) === mmsi,
    );
    if ((entry?.record.kept || inHistory) && taskid && sensorId) {
      this.host.apiService
        .removeVoyageRadarVessel({ taskid, sensorId, mmsi })
        .toPromise()
        .catch(() => undefined);
      if (Array.isArray(sensor?.vesselHistory)) {
        sensor.vesselHistory = sensor.vesselHistory.filter(
          (v: any) => String(v?.mmsi) !== mmsi
        );
      }
    }
    this.pushLocatedState();
  }

  /**
   * Located markers follow the snapshot-wide labels toggle — the Tt control
   * every vessel already obeys. No second labels concept: one switch decides
   * whether the map is named, whatever layer a hull arrived on.
   */
  private syncLocatedLabels(): void {
    const on = this.host.voyageRadarPlayback.snapshot().showLabels === true;
    for (const entry of this._locatedVessels.values()) {
      entry.marker?.unbindTooltip?.();
      // A ring is framing a snapshot hull, and that hull is named by the
      // snapshot's own labelling pass — naming it here too would double it.
      if (on && !entry.ring) this.bindLocatedLabel(entry);
    }
  }

  /** The drawer row's crosshair: recentre the map on a located vessel's fix. */
  centerLocatedVessel(mmsi: string): void {
    const r = this._locatedVessels.get(mmsi)?.record;
    if (r?.lat != null && r?.lng != null) {
      this.host.map?.flyTo([r.lat, r.lng], Math.max(this.host.map?.getZoom?.() ?? 8, 8));
    }
  }

  /** The same read-only popup every hull gets, fed from the located record. */
  openLocatedInfo(mmsi: string): void {
    const r = this._locatedVessels.get(mmsi)?.record;
    if (!r || r.lat == null || r.lng == null) return;
    this.openVesselPopupAt(L.latLng(r.lat, r.lng), {
      Name: r.name,
      mmsi: r.mmsi,
      Lat: r.lat,
      Lng: r.lng,
      shipType: r.shipType,
      navStatus: r.navStatus,
      imo: r.imoNumber,
      callSign: r.callSign,
      flag: r.flag,
      country: r.country,
      time_utc: r.timeUtc,
      Speed: r.sog ?? null,
      Course: r.cog ?? null,
      heading: r.heading ?? null,
      length: r.length ?? null,
      beam: r.beam ?? null,
      destination: r.destination ?? null,
      fromPort: r.fromPort ?? null,
      maxDraught: r.maxDraught ?? null,
      Color: VoyageMapController.LOCATED_COLOR,
    });
  }

  /** One place that opens the standard vessel popup at a point. */
  private openVesselPopupAt(latlng: any, properties: any, opts?: VoyagePopupActions): void {
    if (!this.host.map || !latlng) return;
    this.ensurePopupActionsWired();
    const mmsi = String(properties?.['MMSI'] ?? properties?.mmsi ?? '').trim();
    // The card prints the hull's position, and the point this popup is anchored
    // at IS that position — so a caller that hands over sparse properties (a
    // search hit is name + MMSI and nothing else) still gets a truthful one,
    // rather than the row silently vanishing on some popups and not others.
    const withPosition =
      properties && (properties['Lat'] ?? properties.lat ?? properties.latitude) != null
        ? properties
        : { ...(properties ?? {}), Lat: latlng.lat, Lng: latlng.lng };
    // Not tracked here: `ensurePopupActionsWired`'s `popupopen` listener records
    // it, the same way it records the three cards this method does not open.
    L.popup({
      className: 'ais-vessel-popup',
      closeButton: false,
      offset: L.point(0, -6),
      autoPanPadding: L.point(40, 90),
    } as any)
      .setLatLng(latlng)
      .setContent(buildVoyagePopup(withPosition, opts ?? this.popupOptsFor(mmsi)))
      .openOn(this.host.map);
  }

  /**
   * Close the vessel card if it is describing THIS hull.
   *
   * Called by every path that takes a located vessel off the map. Scoped to the
   * one MMSI rather than calling `map.closePopup()`, because taking one vessel
   * down must not shut a card the user opened on another.
   */
  /**
   * Flag the open card as having a lookup in flight — the spinning button and
   * the strip along its bottom edge (styled in `styles.scss`).
   *
   * Driven from `refreshVesselLookup` rather than from the click delegate, so
   * the mark appears only when a request really starts (the guards there turn
   * back several clicks) and clears on every path out, including the ones that
   * do NOT replace the card: an operator with no data, or an error.
   *
   * Scoped to the hull whose card is open. The same lookup runs from the
   * Saved Vessels section's Refresh, where the open card — if there is one — may
   * belong to a different vessel and must not spin for someone else's request.
   */
  private setVesselPopupUpdating(mmsi: string, on: boolean): void {
    if (this._vesselPopupMmsi !== String(mmsi ?? '').trim()) return;
    const card = this._vesselPopup?.getElement?.()?.querySelector?.('.ais-popup');
    // On the way out the card has usually been replaced already, and the fresh
    // one never carried the class — so this is simply a no-op there.
    card?.classList?.toggle('is-updating', on);
  }

  private closeVesselPopupFor(mmsi: string): void {
    const key = String(mmsi ?? '').trim();
    if (!key || this._vesselPopupMmsi !== key) return;
    // `isOpen` guards the popup having been dismissed by a map click already —
    // Leaflet leaves the reference behind, and closing a closed popup is a no-op
    // only if it is still the map's current one.
    if (this._vesselPopup?.isOpen?.()) this.host.map?.closePopup?.(this._vesselPopup);
    this._vesselPopup = null;
    this._vesselPopupMmsi = null;
  }

  /** Amber — the located marker family's colour, distinct from every fleet hue. */
  private static readonly LOCATED_COLOR = '#B45309';

  /** True when a fix is too old to be captioned as current. */
  private isStaleFix(timeUtc: string | null): boolean {
    if (!timeUtc) return true;
    const t = new Date(timeUtc).getTime();
    return isNaN(t) || Date.now() - t > VoyageMapController.STALE_FIX_MS;
  }

  /** Normalise a search result OR a stored vesselHistory row to one shape. */
  private toLocatedRecord(source: any): VoyageLocatedRecord | null {
    if (!source) return null;
    const identity = source.identity ?? source;
    const asOf = source.asOf ?? source;
    const details = source.details ?? source;
    const mmsi = String(source.mmsi ?? source.id ?? '').trim();
    if (!mmsi) return null;
    const num = (v: any): number | null => {
      // `Number(null)` and `Number('')` are both 0, and this runs over lat/lng:
      // a record whose fix did not survive the feed (`{ latitude: null, lat:
      // null }` reaches here as null, not undefined) would otherwise normalize
      // to 0, 0 — a real place in the Gulf of Guinea, and one that every
      // "no position for this vessel" guard downstream reads as a valid fix.
      if (v == null || (typeof v === 'string' && !v.trim())) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    return {
      mmsi,
      name: identity.shipName ?? identity.name ?? null,
      shipType: identity.shipType ?? null,
      imoNumber: identity.imoNumber ?? null,
      callSign: identity.callSign ?? null,
      flag: identity.flag ?? null,
      country: identity.country ?? null,
      lat: num(asOf.latitude ?? asOf.lat),
      lng: num(asOf.longitude ?? asOf.lng),
      timeUtc: asOf.time_utc ?? null,
      navStatus: asOf.navigationalStatus ?? asOf.navStatus ?? null,
      waterBody: asOf.waterBody ?? null,
      source: source.source === 'lookup' ? 'lookup' : 'search',
      query: source.query ?? null,
      kept: false,
      // Lookup-enriched fields — absent until a "Get latest" has run.
      sog: num(asOf.sog),
      cog: num(asOf.cog),
      heading: num(asOf.heading),
      destination: details.destination ?? details.matchedDestination ?? null,
      fromPort: details.fromPort ?? null,
      length: num(details.length),
      beam: num(details.beam),
      maxDraught: num(details.maxDraught),
    };
  }

  /** Create or update the amber marker for a located vessel. */
  private upsertLocatedMarker(record: VoyageLocatedRecord): void {
    if (record.lat == null || record.lng == null) return;
    const map = this.host.map;
    if (!map) return;
    if (!this._locatedLayer || !map.hasLayer(this._locatedLayer)) {
      this._locatedLayer = L.layerGroup().addTo(map);
    }
    const existing = this._locatedVessels.get(record.mmsi);
    if (existing) {
      // Same hull located again: newer fix wins, but kept-ness survives.
      record.kept = existing.record.kept || record.kept;
      existing.record = record;
      existing.marker.setLatLng([record.lat, record.lng]);
      // The fix may have just moved off the snapshot's — a lookup returning a
      // newer position is exactly how a twin stops being one — so the form is
      // re-decided here rather than only in the display pass.
      this.paintLocated(existing, !!this.snapshotTwinFor(record));
      return;
    }
    const ring = !!this.snapshotTwinFor(record);
    const marker = L.marker([record.lat, record.lng], {
      icon: this.locatedIcon(record, ring),
      keyboard: false,
    } as any);
    marker.on('click', () => this.selectLocated(record.mmsi, { openPopup: true }));
    this._locatedLayer.addLayer(marker);
    const entry = { record, marker, ring };
    this._locatedVessels.set(record.mmsi, entry);
    this.paintLocated(entry, ring);
  }

  /**
   * The painted snapshot marker standing for this hull at (near enough) the
   * same fix, or null.
   *
   * Only a marker the display pipeline actually PAINTED counts: a hull filtered
   * out by ship type, absorbed into a count bubble, or belonging to a layer
   * playback has taken off the map is not on screen to collide with, and a
   * saved vessel reduced to a ring around nothing would be invisible.
   */
  private snapshotTwinFor(record: VoyageLocatedRecord): any | null {
    const lat = record?.lat;
    const lng = record?.lng;
    if (lat == null || lng == null) return null;
    const map = this.host.map;
    const layer = this.displayLayer;
    if (!map || !layer || !map.hasLayer(layer)) return null;
    const marker = this.drawnMarkerFor(layer, record.mmsi);
    const latlng = typeof marker?.getLatLng === 'function' ? marker.getLatLng() : null;
    if (!latlng) return null;
    const apart = map.distance(latlng, L.latLng(lat, lng));
    return apart <= VoyageMapController.TWIN_FIX_METERS ? marker : null;
  }

  /**
   * Draws one located marker in the form its surroundings call for.
   *
   * A saved hull that the snapshot is ALREADY drawing keeps only its amber
   * ring, painted under the snapshot's own arrow: one vessel, one glyph, with a
   * ring around it saying it is saved. Stacking a second icon on the first is
   * what this replaces — at one position, two markers for one hull read as two
   * vessels, and the arrow underneath (which carries the heading and the ship
   * type) was the one being covered up.
   *
   * Anywhere else — open water, a hull the snapshot is not drawing, or a fix
   * that a lookup has since moved away from the snapshot's — the full diamond
   * is correct and comes back, because there is then nothing underneath it and
   * a ring alone would mark a vessel with no vessel in it.
   */
  private paintLocated(
    entry: { record: VoyageLocatedRecord; marker: any; ring: boolean },
    ring: boolean,
  ): void {
    entry.ring = ring;
    entry.marker.setIcon(this.locatedIcon(entry.record, ring));
    // Under the arrow, not over it. Both markers sit in Leaflet's shared marker
    // pane, where stacking otherwise falls out of latitude — which would put the
    // ring on top of the hull it is supposed to frame about half the time.
    entry.marker.setZIndexOffset?.(ring ? -1000 : 0);
    if (ring) {
      // The snapshot layer names the hulls it draws (`applyVesselLabels`), so a
      // permanent tooltip here would print the same name twice at one point.
      entry.marker.unbindTooltip?.();
    } else if (this.labelsOn) {
      this.bindLocatedLabel(entry);
    }
  }

  /**
   * Re-decides every located marker's form against what the snapshot is drawing
   * right now. Runs at the end of the display pass, so a type filter, a cluster
   * forming, a playback frame or a pan that changes which hulls are painted
   * also settles the markers that sit on top of them.
   */
  private syncLocatedTwins(): void {
    if (!this._locatedVessels.size) return;
    for (const entry of this._locatedVessels.values()) {
      const ring = !!this.snapshotTwinFor(entry.record);
      if (ring !== entry.ring) this.paintLocated(entry, ring);
    }
  }

  /**
   * Which way a located hull is pointing, or null when it is not saying.
   *
   * Course over ground first — that is what the snapshot's own arrows are
   * rotated by (`displayDirectionInMap` passes `Course`), so one hull drawn by
   * both layers cannot end up with two glyphs disagreeing. Heading is the
   * fallback for a vessel making no way: moored, COG is noise or absent, but
   * the bow still points somewhere.
   *
   * `>= 360` excludes both AIS sentinels without naming them twice — 360 for
   * "course not available", 511 for "heading not available".
   */
  private locatedCourse(record: VoyageLocatedRecord): number | null {
    const real = (v: any): number | null => {
      // The empty-string guard is load-bearing: `Number('')` is 0, which would
      // point a hull that reported nothing due north.
      if (v == null || (typeof v === 'string' && !v.trim())) return null;
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 && n < 360 ? n : null;
    };
    return real(record?.cog) ?? real(record?.heading);
  }

  /**
   * Amber arrow along the hull's course; amber diamond when it reports none.
   * Gray for a "last known" fix. `ring` drops the glyph and keeps the halo —
   * see `paintLocated`.
   *
   * Two shapes, and the difference between them is information: a lookup pays
   * for the course along with the fix, and drawing every hull as a diamond
   * threw it away — on a POI task, where the snapshot layer is empty and these
   * are the ONLY vessels on the chart, it threw away the whole of it. The
   * diamond stays for hulls that report no course, because an arrow pointing
   * north on a vessel that never said so is a made-up fact, not a default.
   *
   * What separates these from the snapshot's arrows is unchanged, and was never
   * the shape: colour there means ship type, here it is one constant amber, and
   * only these carry the halo.
   */
  private locatedIcon(record: VoyageLocatedRecord, ring: boolean): any {
    const stale = this.isStaleFix(record.timeUtc);
    const cls = `vr-located${stale ? ' vr-located--stale' : ''}${ring ? ' vr-located--ring' : ''}`;
    const course = this.locatedCourse(record);
    // Rotation is inline because it is per-hull: a stylesheet cannot carry 360
    // of these, and `transform` here also owns the centring both glyphs need.
    const glyph =
      course != null
        ? `<span class="vr-located__arrow" style="transform:translate(-50%,-50%) rotate(${course}deg)"></span>`
        : `<span class="vr-located__diamond"></span>`;
    return L.divIcon({
      className: cls,
      html: ring
        ? `<span class="vr-located__halo"></span>`
        : `<span class="vr-located__halo"></span>${glyph}`,
      iconSize: [26, 26],
      iconAnchor: [13, 13],
    });
  }

  private bindLocatedLabel(entry: { record: VoyageLocatedRecord; marker: any }): void {
    const label = entry.record.name || `MMSI ${entry.record.mmsi}`;
    entry.marker.unbindTooltip?.();
    entry.marker.bindTooltip(label, {
      permanent: true,
      direction: 'right',
      offset: [14, 0],
      className: 'ais-vessel-label',
    });
  }

  /** Recompute the search-history rows and hand them to their controller. */
  private pushLocatedState(): void {
    const rows = Array.from(this._locatedVessels.values()).map(({ record: r }) => ({
      mmsi: r.mmsi,
      name: r.name,
      timeUtc: r.timeUtc,
      stale: this.isStaleFix(r.timeUtc),
      kept: r.kept,
      query: r.query ?? null,
    }));
    this.host.voyageVessel.setLocatedVessels(rows);
  }

  /** Take every located artefact off the map (task switch — see reset()). */
  private clearLocatedVessels(): void {
    this._locatedLayer = null;
    this._locatedVessels.clear();
    this.lookupLoadingMmsi = null;
    // Nulled rather than closed, like the layer fields above: the task-switch
    // sweep has already taken the popup off the map, and a reference to the
    // previous task's card must not survive into the next one.
    this._vesselPopup = null;
    this._vesselPopupMmsi = null;
    this.host.voyageVessel.close();
  }

  // ── Teardown ────────────────────────────────────────────────────────────────
  /**
   * Resets everything this controller owns — both halves. Called from the host's
   * `_resetVoyageRadarToolbar`, which `resetMapNew` invokes on a task switch.
   *
   * Deliberately nulls the layer fields rather than removing the layers: the
   * shared reset has already swept the map, so a removeLayer() here would be
   * operating on a map that no longer holds them.
   */
  reset(): void {
    this.host.toolbarDrawer.close();
    // The search cache holds worldwide results bought against the OUTGOING
    // sensor's wallet, so it leaves with the task rather than answering the
    // next one's queries for free.
    this.host.voyageSearch.reset();
    // Off the map before the map is swept: this handler is the one thing here
    // that outlives the layers, and a task switch would otherwise leave it
    // walking a layer that no longer exists on every pan.
    this.stopViewportRepaint();
    this.clearVesselClusters();
    this._labelsPainted = false;
    if (this.host.voyageRadarPlayback.mode === 'playback') {
      this.host.voyageRadarPlayback.close();
    }
    this.host.voyageTrack.close();
    // Drop the SELECTION, not just the track — otherwise the next collection
    // opens with a stale "Trace · <old vessel>" pill that does nothing.
    this.host.voyageTrack.setActiveVessel(null);
    this.active = null;

    this.playbackLayer = null;
    // Nulled like every other layer field here — the sweep has already taken the
    // group off the map, and the next display pass builds a fresh one.
    this._clusterLayer = null;
    this._liveLayerHidden = false;
    this.loadedSteps = [];
    // Cleared with the steps it describes — a stale key would serve the previous
    // task's frames to the next one.
    this._stepsCacheKey = '';
    this.releaseLiveLayer();

    this._haloMarker = null;
    this._trackRenderer = null;
    this._trailBase = null;
    this._trailLink = null;
    this._trailDone = null;
    this._trackDots = null;
    this._trackMarks = null;
    // DETACHED, not merely forgotten. Nulling the fields alone left the
    // listeners bound to a map that outlives this reset, so every task switch
    // made with a track drawn added another set — and `voyageTrack.close()`
    // above does not run the layer teardown that would have removed them.
    this._detachTrackHover();
    this._ghostMarker = null;
    this._ghostEl = null;
    this._trailDoneSegments = [];
    this._stepGaps = [];
    this._trackLatLngs = [];
    this._trailIndex = -1;
    // Dropped rather than restored: the next task fits its own AOI, so replaying
    // the previous one's viewport would fight it.
    this._preTrackView = null;
    this._trackFitView = null;
    this._focusMmsi = null;
    this._tracedMmsi = null;
    // Located vessels belong to the task that just closed; the next task
    // hydrates its own from its own vesselHistory. Field-nulling only, like
    // every other layer here — the shared sweep has already cleared the map.
    this.clearLocatedVessels();

    // The target belongs to the task that just closed, so the fields go with it
    // — the next task builds its own from its own footprint. Nulled rather than
    // removed, like every layer above: the sweep this runs inside has already
    // taken it off the map. The POI result state goes too, since the reticle
    // that was carrying it is gone.
    this.targetLayer = null;
    this.targetCircle = null;
    this.targetMarker = null;
    this.poiVessels = [];
    this.poiRadiusKm = null;
    this.poiQuery = '';
    this.poiPendingMmsi = null;
  }
}
